import { useConfigurationStore } from '@/stores/configuration-store.js';
import { applyReceiptPreferences, applySharedReceiptDesign } from '@/utils/receiptDesign.js';

let printersPromise = null;
const printerKeys = { thermal: 'thermal', A4: 'A4', A5: 'A5' };
const selectedPrinters = {
    thermal: localStorage.getItem('inventree.printAgent.printer.thermal') || localStorage.getItem('inventree.printAgent.printer') || '',
    A4: localStorage.getItem('inventree.printAgent.printer.A4') || '',
    A5: localStorage.getItem('inventree.printAgent.printer.A5') || '',
};

// Build-time fallback only. The connection is resolved per request from the
// installation's own settings (see agentConnection) so one bundle serves every
// client and a key can be rotated without rebuilding and redeploying.
const FALLBACK_AGENT_URL = String(import.meta.env.VITE_PRINT_AGENT_URL || 'http://127.0.0.1:8123');
const FALLBACK_AGENT_KEY = import.meta.env.VITE_PRINT_AGENT_KEY || '_2Gut1eFmC2pt06M1SrPlfKGxxF1hmN9w';

/**
 * The configuration rows as the store holds them right now, or nothing.
 *
 * Nothing rather than an error, and on purpose: this module is imported by
 * code that runs before Pinia is active — and by the print tests, which have
 * no Pinia at all — so a store that is not there yet must not throw inside a
 * print. Each caller falls back to its own built-in value.
 */
function storedConfigurations() {
    try {
        return useConfigurationStore().configurations ?? {};
    } catch {
        return {};
    }
}

/**
 * Where this till's Print Agent is, and the key it expects.
 *
 * Read from the configuration store rather than captured at module load: the
 * settings arrive after this module is imported, they change when an operator
 * saves, and each installation serves its own. Resolved on every request so a
 * rotated key takes effect on the next print rather than the next page load.
 */
function agentConnection() {
    const configurations = storedConfigurations();

    const url = String(configurations.print_agent_url || FALLBACK_AGENT_URL).trim();
    const key = String(configurations.print_agent_key || FALLBACK_AGENT_KEY);

    return {
        url: (url || FALLBACK_AGENT_URL).replace(/\/$/, ''),
        key,
    };
}

// One authoritative 80mm Sale receipt profile. Both the Print Agent and the
// browser fallback append this last, so older generic receipt rules cannot
// change the Sale table widths, alignment, or typography.
const sale80mmFinalStyles = `
body.sale-pos-receipt,
body.sale-pos-receipt .line,
body.sale-pos-receipt .info-row,
body.sale-pos-receipt .sum-row,
body.sale-pos-receipt table tbody td,
body.sale-pos-receipt .footer { color: #000 !important; font-weight: 700 !important; }
body.sale-pos-receipt .company,
body.sale-pos-receipt .sale-invoice-title,
body.sale-pos-receipt .section-title,
body.sale-pos-receipt table thead th { font-weight: 600 !important; }
body.sale-pos-receipt .total-line,
body.sale-pos-receipt .total-line span { font-weight: 600 !important; }
/* Keep the configured footer message in normal receipt flow. Some legacy
   receipt styles use footer-like classes with positioning/flex rules; on an
   80mm print that can make the message appear above the receipt content. */
body[class] .footer,
body[class] .footer-bar,
body[class] .footer-message,
body[class] .print-footer,
body[class] .receipt-footer {
    position: static !important;
    inset: auto !important;
    float: none !important;
    clear: both !important;
    order: initial !important;
    display: block !important;
    width: 100% !important;
    height: auto !important;
    max-height: none !important;
    transform: none !important;
}
/* Render the receipt as one vertical flow and force the footer to the final
   position before the thermal cut, even when a template has mixed wrappers. */
body.print-80mm,
body.print-80mm > .document,
body.print-80mm > .page,
body.print-80mm .document > .page {
    display: flex !important;
    flex-direction: column !important;
}
body.print-80mm .footer,
body.print-80mm .footer-bar,
body.print-80mm .footer-message,
body.print-80mm .print-footer,
body.print-80mm .receipt-footer {
    order: 9999 !important;
    align-self: stretch !important;
}
/* Gate Pass 80mm: the company block owns the single dotted rule after
   NTN/STRN, while the footer owns the single lower separator. */
body.sale-pos-receipt.gatepass-document .brand {
    border-bottom: 1px dotted #000 !important;
}
body.sale-pos-receipt.gatepass-document .total {
    border-bottom: 0 !important;
}
body.sale-pos-receipt.gatepass-document .footer {
    border-top: 1px solid #000 !important;
}
body.supplier-document .items-section-title {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
}
body.payment-document table th:nth-child(1),
body.payment-document table td:nth-child(1) { width: 8% !important; }
body.payment-document table th:nth-child(2),
body.payment-document table td:nth-child(2) {
    width: 70% !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: clip !important;
}
body.payment-document table th:nth-child(3),
body.payment-document table td:nth-child(3) { width: 22% !important; }
body.receiving-document table th:nth-child(1),
body.receiving-document table td:nth-child(1) { width: 8% !important; }
body.receiving-document table th:nth-child(2),
body.receiving-document table td:nth-child(2) {
    width: 74% !important;
    text-align: left !important;
}
body.receiving-document table th:nth-child(3),
body.receiving-document table td:nth-child(3) { width: 18% !important; }
body.receiving-document table td.col-item {
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    white-space: normal !important;
    overflow: hidden !important;
    overflow-wrap: break-word !important;
    word-break: normal !important;
}
body.sale-pos-receipt .product-name-row td {
    font-weight: 700 !important;
    text-align: left !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
}
body.sale-pos-receipt .sale-items-table td.item-name {
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
}
/* Refund/exchange thermal tables have a full-width product row followed by
   numeric detail cells. Keep every value below its matching header. */
body.sale-pos-receipt.refund-document table.returned-items-table th:nth-child(1),
body.sale-pos-receipt.refund-document table.returned-items-table td:nth-child(1),
body.sale-pos-receipt.refund-document table.new-items-table th:nth-child(2),
body.sale-pos-receipt.refund-document table.new-items-table td:nth-child(2) { width: 18% !important; text-align: right !important; }
body.sale-pos-receipt.refund-document table.returned-items-table th:nth-child(2),
body.sale-pos-receipt.refund-document table.returned-items-table td:nth-child(2),
body.sale-pos-receipt.refund-document table.new-items-table th:nth-child(1),
body.sale-pos-receipt.refund-document table.new-items-table td:nth-child(1) { width: 10% !important; text-align: center !important; }
body.sale-pos-receipt.refund-document table.returned-items-table th:nth-child(3),
body.sale-pos-receipt.refund-document table.returned-items-table td:nth-child(3),
body.sale-pos-receipt.refund-document table.new-items-table th:nth-child(3),
body.sale-pos-receipt.refund-document table.new-items-table td:nth-child(3) { width: 24% !important; text-align: right !important; }
body.sale-pos-receipt.refund-document table.returned-items-table th:nth-child(4),
body.sale-pos-receipt.refund-document table.returned-items-table td:nth-child(4),
body.sale-pos-receipt.refund-document table.new-items-table th:nth-child(4),
body.sale-pos-receipt.refund-document table.new-items-table td:nth-child(4) { width: 20% !important; text-align: center !important; }
body.sale-pos-receipt.refund-document table.returned-items-table th:nth-child(5),
body.sale-pos-receipt.refund-document table.returned-items-table td:nth-child(5),
body.sale-pos-receipt.refund-document table.new-items-table th:nth-child(5),
body.sale-pos-receipt.refund-document table.new-items-table td:nth-child(5) { width: 28% !important; text-align: right !important; }
body.sale-pos-receipt.refund-document table.product-name-row td.item-name {
    width: 100% !important;
    max-width: 100% !important;
    white-space: normal !important;
    overflow: visible !important;
    overflow-wrap: break-word !important;
    word-break: normal !important;
    text-align: left !important;
}
/* Thermal Purchase Order markup uses a plain table rather than
   .sale-items-table. Keep its five receipt columns independent and readable. */
body.sale-pos-receipt.order-request-document .hr {
    display: none !important;
    border: 0 !important;
    height: 0 !important;
    margin: 0 !important;
}
body.sale-pos-receipt.order-request-document table {
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
}
body.sale-pos-receipt.order-request-document table th,
body.sale-pos-receipt.order-request-document table td {
    border-bottom: 0 !important;
    padding: 0 3px !important;
    line-height: 1.1 !important;
    overflow: visible !important;
    height: auto !important;
    max-height: none !important;
}
body.sale-pos-receipt.order-request-document table th:nth-child(1),
body.sale-pos-receipt.order-request-document table td:nth-child(1) { width: 18% !important; text-align: right !important; }
body.sale-pos-receipt.order-request-document table th:nth-child(2),
body.sale-pos-receipt.order-request-document table td:nth-child(2) { width: 12% !important; text-align: center !important; }
body.sale-pos-receipt.order-request-document table th:nth-child(3),
body.sale-pos-receipt.order-request-document table td:nth-child(3) { width: 25% !important; text-align: center !important; }
body.sale-pos-receipt.order-request-document table th:nth-child(4),
body.sale-pos-receipt.order-request-document table td:nth-child(4) { width: 20% !important; text-align: right !important; }
body.sale-pos-receipt.order-request-document table th:nth-child(5),
body.sale-pos-receipt.order-request-document table td:nth-child(5) { width: 25% !important; text-align: right !important; }
body.sale-pos-receipt.order-request-document table td.item-name,
body.sale-pos-receipt.order-request-document table .product-name-row td {
    width: 100% !important;
    max-width: none !important;
    padding: 0 !important;
    height: auto !important;
    max-height: none !important;
    text-align: left !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    font-weight: 700 !important;
}
body.sale-pos-receipt .sale-items-table td.name-col,
body.sale-pos-receipt .sale-items-table .product-name-row td.name-col {
    display: table-cell !important;
    width: 100% !important;
    max-width: none !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    text-align: left !important;
}
/* This block is appended last, after all shared receipt styles. Remove every
   dotted separator from a sale receipt and restore only the rule immediately
   following the company header (the NTN/STRN separator). */
body.sale-pos-receipt .hr,
body.sale-pos-receipt .summary-separator,
body.sale-pos-receipt .header-rule,
body.sale-pos-receipt .header + .hr,
body.sale-pos-receipt .header-row + .hr {
    display: none !important;
    border: 0 !important;
    border-top: 0 !important;
    border-bottom: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}
body.sale-pos-receipt.order-request-document .document-reference {
    text-align: right !important;
    font-size: 10px !important;
    line-height: 1.15 !important;
    font-weight: 500 !important;
    margin: 1px 0 2px !important;
    white-space: nowrap !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
body.sale-pos-receipt.order-request-document .items-end-separator {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1px 0 2px !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm.order-request-document .footer { border-top: 0 !important; }
body.sale-pos-receipt .print-header {
    border-bottom: 0 !important;
}
body.sale-pos-receipt .print-header + .hr {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
body.sale-pos-receipt .sale-items-table thead th:nth-child(1),
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(1) { text-align: right !important; }
body.sale-pos-receipt .sale-items-table thead th:nth-child(2),
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(2) { text-align: center !important; }
body.sale-pos-receipt .sale-items-table thead th:nth-child(3),
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(3) { text-align: right !important; }
body.sale-pos-receipt .sale-items-table thead th:nth-child(4),
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(4) { text-align: right !important; }
body.sale-pos-receipt .sale-items-table thead th:nth-child(5),
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(5) { text-align: right !important; }
body.sale-pos-receipt .sale-items-table th,
body.sale-pos-receipt .sale-items-table td { font-size: 9.5px !important; line-height: 1.15 !important; }
body.sale-pos-receipt .sale-items-table th:nth-child(1),
body.sale-pos-receipt .sale-items-table td:nth-child(1) { width: 18% !important; text-align: right !important; }
body.sale-pos-receipt .sale-items-table th:nth-child(2),
body.sale-pos-receipt .sale-items-table td:nth-child(2) { width: 12% !important; text-align: center !important; }
body.sale-pos-receipt .sale-items-table th:nth-child(3),
body.sale-pos-receipt .sale-items-table td:nth-child(3) { width: 25% !important; text-align: right !important; }
body.sale-pos-receipt .sale-items-table th:nth-child(4),
body.sale-pos-receipt .sale-items-table td:nth-child(4) { width: 20% !important; text-align: right !important; }
body.sale-pos-receipt .sale-items-table th:nth-child(5),
body.sale-pos-receipt .sale-items-table td:nth-child(5) { width: 25% !important; text-align: right !important; }
body.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td { font-weight: 700 !important; }
/* Shared 80mm typography for every document type. Table geometry remains
   document-specific, but no document may re-enable the old heavy defaults. */
body[class][class][class],
body[class][class][class] .line,
body[class][class][class] .info-row,
body[class][class][class] .sum-row,
body[class][class][class] table tbody td,
body[class][class][class] .footer { color: #000 !important; font-weight: 400 !important; }
body[class][class][class] .company,
body[class][class][class] .company-name,
body[class][class][class] .sale-invoice-title,
body[class][class][class] .invoice-word,
body[class][class][class] .section-title,
body[class][class][class] .info-label,
body[class][class][class] table thead th { font-weight: 600 !important; }
body[class][class][class] .total-line,
body[class][class][class] .grand-total,
body[class][class][class] .grand-total-row,
body[class][class][class] .total-line span,
body[class][class][class] .grand-total span,
body[class][class][class] .grand-total-row span { font-weight: 600 !important; }
body[class][class][class] .product-name-row td,
body[class][class][class] td.item-name { font-weight: 500 !important; }
/* Final shared priority: override legacy high-specificity typography. */
body[class][class][class][class][class],
body[class][class][class][class][class] .line,
body[class][class][class][class][class] .info-row,
body[class][class][class][class][class] .sum-row,
body[class][class][class][class][class] table tbody td,
body[class][class][class][class][class] .footer { font-weight: 400 !important; }
body[class][class][class][class][class] .company,
body[class][class][class][class][class] .company-name,
body[class][class][class][class][class] .sale-invoice-title,
body[class][class][class][class][class] .invoice-word,
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] table thead th { font-weight: 500 !important; }
body[class][class][class][class][class] .total-line,
body[class][class][class][class][class] .grand-total,
body[class][class][class][class][class] .grand-total-row,
body[class][class][class][class][class] .total-line span,
body[class][class][class][class][class] .grand-total span,
body[class][class][class][class][class] .grand-total-row span { font-weight: 600 !important; }
body[class][class][class][class][class] .product-name-row td,
body[class][class][class][class][class] td.item-name { font-weight: 400 !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th,
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td { font-size: 9px !important; line-height: 1.1 !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th:nth-child(1),
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td:nth-child(1) { width: 18% !important; text-align: right !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th:nth-child(2),
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td:nth-child(2) { width: 12% !important; text-align: center !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th:nth-child(3),
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td:nth-child(3) { width: 25% !important; text-align: right !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th:nth-child(4),
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td:nth-child(4) { width: 20% !important; text-align: right !important; }
body[class][class][class][class][class].sale-pos-receipt .sale-items-table th:nth-child(5),
body[class][class][class][class][class].sale-pos-receipt .sale-items-table td:nth-child(5) { width: 25% !important; text-align: right !important; }
/* Shared 80mm table contract for every receipt. Long names may wrap, but no
   cell may hide or clip its content. Existing document-specific column rules
   still control the widths and numeric alignment for each table shape. */
body[class][class][class][class][class] table {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    table-layout: fixed !important;
}
body.print-80mm.refund-document:not(.exchange-document) .returned-items-title { display: none !important; }
body.print-80mm.refund-document .returned-items-table { margin-bottom: 2mm !important; }
body.print-80mm.refund-document table td[colspan] { text-align: center !important; }
body.print-80mm[class][class][class][class][class].refund-document td.no-items-found {
    width: 100% !important;
    text-align: center !important;
    display: table-cell !important;
    white-space: normal !important;
}
body.print-80mm[class][class][class][class][class].refund-document table.new-items-table tr.no-items-row,
body.print-80mm[class][class][class][class][class].refund-document table.returned-items-table tr.no-items-row {
    display: table-row !important;
    width: 100% !important;
}
body.print-80mm[class][class][class][class][class].refund-document table.new-items-table tr.no-items-row td.no-items-found,
body.print-80mm[class][class][class][class][class].refund-document table.returned-items-table tr.no-items-row td.no-items-found {
    display: table-cell !important;
    width: 100% !important;
    min-width: 100% !important;
    padding: 3px 0 !important;
    text-align: center !important;
    white-space: nowrap !important;
}
body.print-80mm[class][class][class][class][class].refund-document table.sale-items-table tr.product-name-row {
    display: table-row !important;
}
body.print-80mm[class][class][class][class][class].refund-document table.sale-items-table tr.product-name-row td.item-name {
    display: table-cell !important;
    width: 100% !important;
    text-align: left !important;
    white-space: normal !important;
    overflow: visible !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    line-height: 1.15 !important;
}
body[class][class][class][class][class] table th,
body[class][class][class][class][class] table td {
    min-width: 0 !important;
    max-width: 100% !important;
    overflow: visible !important;
    text-overflow: clip !important;
    word-break: normal !important;
}
body[class][class][class][class][class] table td.item-name,
body[class][class][class][class][class] table td.product-name,
body[class][class][class][class][class] table td.col-item,
body[class][class][class][class][class] table .product-name-row td,
body[class][class][class][class][class] table td.product-name {
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    text-align: left !important;
}
body[class][class][class][class][class] table tbody tr.product-name-row td {
    width: 100% !important;
    font-weight: 400 !important;
}
/* Final 80mm compact pass for every HTML receipt type. */
body[class][class][class][class][class] .hr,
body[class][class][class][class][class] .summary-separator,
body[class][class][class][class][class] .separator,
body[class][class][class][class][class] .sep,
body[class][class][class][class][class] .sep-dark {
    display: none !important;
    border: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}
/* Keep exactly one dotted separator directly below the company registration
   block. All other separators remain removed. */
body[class][class][class][class][class] .header-row + .hr,
body[class][class][class][class][class] .header + .hr,
body[class][class][class][class][class] .print-header + .hr,
body[class][class][class][class][class] .header-rule,
body[class][class][class][class][class] .po-header-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
body[class][class][class][class][class] table tbody td {
    border-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.1 !important;
}
body[class][class][class][class][class] table td.col-qty,
body[class][class][class][class][class] table th.col-qty { text-align: center !important; }
body[class][class][class][class][class] table td.col-tax,
body[class][class][class][class][class] table th.col-tax { text-align: center !important; }
body[class][class][class][class][class] table td.col-discount,
body[class][class][class][class][class] table th.col-discount { text-align: right !important; }
/* Last spacing pass: the individual document styles set large table and
   section margins. Override those values for every 80mm HTML receipt. */
body[class][class][class][class][class] table {
    margin-top: 0 !important;
    margin-bottom: 1px !important;
}
body[class][class][class][class][class] table thead th {
    padding-top: 1px !important;
    padding-bottom: 1px !important;
}
body[class][class][class][class][class] table tbody tr,
body[class][class][class][class][class] table tbody td {
    height: auto !important;
    min-height: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .line,
body[class][class][class][class][class] .info-row,
body[class][class][class][class][class] .pm-row,
body[class][class][class][class][class] .sum-row {
    min-height: 0 !important;
    height: auto !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .doc-title,
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] .pm-title {
    margin-top: 1px !important;
    margin-bottom: 0 !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .total-line,
body[class][class][class][class][class] .grand-total-row {
    margin-top: 1px !important;
    margin-bottom: 1px !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .footer,
body[class][class][class][class][class] .footer-bar {
    margin-top: 2px !important;
    padding-top: 1px !important;
    line-height: 1.1 !important;
}
/* Keep each numeric column in the same position in the header and every
   detail row. The product-name row spans all five columns separately. */
body[class][class][class][class][class] table.sale-items-table,
body[class][class][class][class][class] table.items-table {
    table-layout: fixed !important;
}
body[class][class][class][class][class] table.sale-items-table th:nth-child(1),
body[class][class][class][class][class] table.sale-items-table td:nth-child(1),
body[class][class][class][class][class] table.items-table th:nth-child(1),
body[class][class][class][class][class] table.items-table td:nth-child(1) {
    width: 20% !important;
    text-align: right !important;
}
body[class][class][class][class][class] table.sale-items-table th:nth-child(2),
body[class][class][class][class][class] table.sale-items-table td:nth-child(2),
body[class][class][class][class][class] table.items-table th:nth-child(2),
body[class][class][class][class][class] table.items-table td:nth-child(2) {
    width: 12% !important;
    text-align: center !important;
}
body[class][class][class][class][class] table.sale-items-table th:nth-child(3),
body[class][class][class][class][class] table.sale-items-table td:nth-child(3),
body[class][class][class][class][class] table.items-table th:nth-child(3),
body[class][class][class][class][class] table.items-table td:nth-child(3) {
    width: 22% !important;
    text-align: right !important;
}
body[class][class][class][class][class] table.sale-items-table th:nth-child(4),
body[class][class][class][class][class] table.sale-items-table td:nth-child(4),
body[class][class][class][class][class] table.items-table th:nth-child(4),
body[class][class][class][class][class] table.items-table td:nth-child(4) {
    width: 18% !important;
    text-align: right !important;
}
body[class][class][class][class][class] table.sale-items-table th:nth-child(5),
body[class][class][class][class][class] table.sale-items-table td:nth-child(5),
body[class][class][class][class][class] table.items-table th:nth-child(5),
body[class][class][class][class][class] table.items-table td:nth-child(5) {
    width: 28% !important;
    text-align: right !important;
}
body[class][class][class][class][class] table.sale-items-table td.item-name,
body[class][class][class][class][class] table.items-table td.item-name,
body[class][class][class][class][class] table tr.product-name-row td {
    width: 100% !important;
    text-align: left !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    white-space: normal !important;
}
/* Three-column supporting tables: label/quantity-or-amount/date. */
body[class][class][class][class][class] table:has(th.col-branch) th:nth-child(1),
body[class][class][class][class][class] table:has(th.col-branch) td:nth-child(1) { width: 50% !important; text-align: left !important; }
body[class][class][class][class][class] table:has(th.col-branch) th:nth-child(2),
body[class][class][class][class][class] table:has(th.col-branch) td:nth-child(2) { width: 25% !important; text-align: center !important; }
body[class][class][class][class][class] table:has(th.col-branch) th:nth-child(3),
body[class][class][class][class][class] table:has(th.col-branch) td:nth-child(3) { width: 25% !important; text-align: right !important; }
/* Product names use the full row and wrap instead of being clipped at the
   right edge. Numeric columns remain on their own detail row. */
body[class][class][class][class][class] table tr.product-name-row td,
body[class][class][class][class][class] table td.item-name {
    display: table-cell !important;
    width: 100% !important;
    max-width: none !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] table th,
body[class][class][class][class][class] table td {
    padding-left: 3px !important;
    padding-right: 3px !important;
}
body[class][class][class][class][class] table td.col-qty,
body[class][class][class][class][class] table th.col-qty { text-align: center !important; }
body[class][class][class][class][class] table td.col-tax,
body[class][class][class][class][class] table th.col-tax { text-align: center !important; }
body[class][class][class][class][class] table td.col-discount,
body[class][class][class][class][class] table th.col-discount { text-align: right !important; }
/* Compact section spacing for every 80mm HTML receipt. */
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] .pm-title {
    margin-top: 1px !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    font-size: 11px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .print-details,
body[class][class][class][class][class] .print-details div,
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] .pm-title {
    font-size: 11px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .line,
body[class][class][class][class][class] .info-row,
body[class][class][class][class][class] .sum-row,
body[class][class][class][class][class] .pm-row {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.1 !important;
}
body[class][class][class][class][class] .info-strip,
body[class][class][class][class][class] .info-block,
body[class][class][class][class][class] table {
    margin-top: 0 !important;
    margin-bottom: 1px !important;
}
body[class][class][class][class][class] .total-line,
body[class][class][class][class][class] .grand-total,
body[class][class][class][class][class] .grand-total-row {
    margin-top: 1px !important;
    margin-bottom: 1px !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
}
body[class][class][class][class][class] .footer,
body[class][class][class][class][class] .footer-bar {
    margin-top: 1mm !important;
    padding-top: 1mm !important;
}
/* Dynamic 80mm item-table contract: only rendered numeric columns receive
   width, so hidden Tax/Discount columns never leave a blank gap. */
body[class][class][class][class][class] table:has(th.col-rate) {
    table-layout: fixed !important;
    width: 100% !important;
}
body[class][class][class][class][class] table:has(th.col-rate):not(:has(.col-tax)):not(:has(.col-discount)) .col-rate,
body[class][class][class][class][class] table:has(th.col-rate):not(:has(.col-tax)):not(:has(.col-discount)) .col-qty,
body[class][class][class][class][class] table:has(th.col-rate):not(:has(.col-tax)):not(:has(.col-discount)) .col-total {
    width: 33.333333% !important;
    min-width: 0 !important;
}
body[class][class][class][class][class] table:has(th.col-rate):has(.col-discount):not(:has(.col-tax)) .col-rate,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-discount):not(:has(.col-tax)) .col-qty,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-discount):not(:has(.col-tax)) .col-discount,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-discount):not(:has(.col-tax)) .col-total,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):not(:has(.col-discount)) .col-rate,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):not(:has(.col-discount)) .col-qty,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):not(:has(.col-discount)) .col-tax,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):not(:has(.col-discount)) .col-total {
    width: 25% !important;
    min-width: 0 !important;
}
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):has(.col-discount) .col-rate,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):has(.col-discount) .col-qty,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):has(.col-discount) .col-tax,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):has(.col-discount) .col-discount,
body[class][class][class][class][class] table:has(th.col-rate):has(.col-tax):has(.col-discount) .col-total {
    width: 20% !important;
    min-width: 0 !important;
}
/* Sales receipt sections must touch with only the normal text line height;
   the old template margins were creating large gaps after Payment, Customer,
   Total Items, and before Gross Amount. */
body[class][class][class][class][class].sale-pos-receipt .subtitle,
body[class][class][class][class][class].sale-pos-receipt .print-details,
body[class][class][class][class][class].sale-pos-receipt .print-details div,
body[class][class][class][class][class].sale-pos-receipt .sale-meta-line,
body[class][class][class][class][class].sale-pos-receipt .customer-line,
body[class][class][class][class][class].sale-pos-receipt .section-title {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class].sale-pos-receipt .sale-items-table + .hr,
body[class][class][class][class][class].sale-pos-receipt .net-total-row + .hr {
    margin: 1px 0 !important;
}
body[class][class][class][class][class].sale-pos-receipt .total-line {
    margin: 1px 0 !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class].sale-pos-receipt .footer,
body[class][class][class][class][class].sale-pos-receipt .sale-payment-qr {
    margin-top: 2px !important;
    margin-bottom: 1px !important;
    padding-top: 0 !important;
}
/* Match the compact 80mm reference layout: no blank vertical bands between
   information rows, tables, totals, or section headings. */
body[class][class][class][class][class] .header,
body[class][class][class][class][class] .print-header,
body[class][class][class][class][class] .doc-title,
body[class][class][class][class][class] .subtitle,
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] .pm-title,
body[class][class][class][class][class] .line,
body[class][class][class][class][class] .info-row,
body[class][class][class][class][class] .pm-row,
body[class][class][class][class][class] .sum-row {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .logo {
    margin-top: 0 !important;
    margin-bottom: 1px !important;
}
body[class][class][class][class][class] table {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
}
body[class][class][class][class][class] table thead th {
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] table tbody td {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    line-height: 1.05 !important;
    vertical-align: top !important;
}
body[class][class][class][class][class] table thead th {
    vertical-align: middle !important;
    white-space: nowrap !important;
}
body[class][class][class][class][class] table td.col-rate,
body[class][class][class][class][class] table td.col-qty,
body[class][class][class][class][class] table td.col-tax,
body[class][class][class][class][class] table td.col-discount,
body[class][class][class][class][class] table td.col-total,
body[class][class][class][class][class] table td.col-date {
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
}
body[class][class][class][class][class] .total-line,
body[class][class][class][class][class] .grand-total-row {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .footer,
body[class][class][class][class][class] .footer-bar {
    margin-top: 1px !important;
    padding-top: 1px !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .sale-payment-qr {
    margin-top: 1px !important;
    margin-bottom: 0 !important;
}
/* Shared document details for all POS-style 80mm receipts. */
body[class][class][class][class][class].purchase-order-document .line > :last-child,
body[class][class][class][class][class].supplier-document .line > :last-child {
    max-width: 100% !important;
    white-space: nowrap !important;
    overflow: visible !important;
}
body[class][class][class][class][class].payment-document table td.col-item {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: clip !important;
}
body[class][class][class][class][class].receiving-document table th:nth-child(1),
body[class][class][class][class][class].receiving-document table td:nth-child(1) { width: 8% !important; }
body[class][class][class][class][class].receiving-document table th:nth-child(2),
body[class][class][class][class][class].receiving-document table td:nth-child(2) {
    width: 70% !important;
    text-align: left !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
}
body[class][class][class][class][class].receiving-document table td.col-item {
    display: table-cell !important;
    vertical-align: top !important;
    overflow: visible !important;
}
body[class][class][class][class][class].receiving-document table th:nth-child(3),
body[class][class][class][class][class].receiving-document table td:nth-child(3) { width: 22% !important; }
body.print-80mm[class][class][class][class][class].receiving-document table.supplier-items-table th:first-child,
body.print-80mm[class][class][class][class][class].receiving-document table.supplier-items-table td:first-child,
body.print-80mm[class][class][class][class][class].sale-pos-receipt table.supplier-items-table th:first-child,
body.print-80mm[class][class][class][class][class].sale-pos-receipt table.supplier-items-table td:first-child {
    width: 78% !important;
    text-align: left !important;
}
body.print-80mm[class][class][class][class][class].receiving-document table.supplier-items-table th:last-child,
body.print-80mm[class][class][class][class][class].receiving-document table.supplier-items-table td:last-child,
body.print-80mm[class][class][class][class][class].sale-pos-receipt table.supplier-items-table th:last-child,
body.print-80mm[class][class][class][class][class].sale-pos-receipt table.supplier-items-table td:last-child {
    width: 22% !important;
    text-align: right !important;
    white-space: nowrap !important;
}
body.print-80mm[class][class][class][class][class].payment-document table td.col-item,
body.print-80mm[class][class][class][class][class].sale-pos-receipt table.supplier-items-table td.col-item {
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
    text-align: left !important;
}
body.print-80mm[class][class][class][class][class].payment-document .payment-amount-separator,
body.print-80mm[class][class][class][class][class].sale-pos-receipt .payment-amount-separator {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body[class][class][class][class][class].transfer-document .transfer-items-heading,
body[class][class][class][class][class].transfer-document .footer-bottom,
body[class][class][class][class][class].gatepass-document .footer-bottom {
    display: none !important;
}
body[class][class][class][class][class].transfer-document table thead th {
    border-bottom: 1px solid #000 !important;
}
body[class][class][class][class][class].transfer-document table th.item-name-head,
body[class][class][class][class][class].transfer-document table td.item-name {
    width: 78% !important;
    text-align: left !important;
}
body[class][class][class][class][class].transfer-document table th.col-qty,
body[class][class][class][class][class].transfer-document table td.col-qty {
    width: 22% !important;
    text-align: right !important;
    padding-right: 0 !important;
}
body[class][class][class][class][class].transfer-document .footer,
body[class][class][class][class][class].transfer-document .footer-bar,
body[class][class][class][class][class].transfer-document .footer-message {
    margin: 1px 0 0 !important;
    padding: 0 !important;
    line-height: 1.05 !important;
    white-space: normal !important;
}
body[class][class][class][class][class].transfer-document .footer-bar:empty,
body[class][class][class][class][class].transfer-document .footer-bar > :empty {
    display: none !important;
}
/* Never clip receipt footer content. Long configured footer text must remain
   visible and may wrap onto additional lines when the paper is narrow. */
body[class][class][class][class][class] .footer,
body[class][class][class][class][class] .footer-bar,
body[class][class][class][class][class] .footer-message,
body[class][class][class][class][class] .print-footer,
body[class][class][class][class][class] .receipt-footer {
    display: block !important;
    width: 100% !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    white-space: pre-line !important;
    overflow-wrap: anywhere !important;
    word-break: break-word !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
}
body[class][class][class][class][class] .footer *,
body[class][class][class][class][class] .footer-bar *,
body[class][class][class][class][class] .footer-message * {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
}
/* Final reference-density profile for every 80mm POS receipt. */
body[class][class][class][class][class] {
    line-height: 1.05 !important;
}
body[class][class][class][class][class] .line,
body[class][class][class][class][class] .info-row,
body[class][class][class][class][class] .pm-row,
body[class][class][class][class][class] .sum-row {
    padding: 0.5px 0 !important;
    margin: 0 !important;
    line-height: 1.1 !important;
}
body[class][class][class][class][class] .section-title,
body[class][class][class][class][class] .info-label,
body[class][class][class][class][class] .pm-title,
body[class][class][class][class][class] .doc-title,
body[class][class][class][class][class] .subtitle {
    padding: 0 !important;
    margin: 0 !important;
    line-height: 1.05 !important;
}
body[class][class][class][class][class] table th,
body[class][class][class][class][class] table td {
    padding-top: 0.5px !important;
    padding-bottom: 0.5px !important;
    line-height: 1.1 !important;
}
body[class][class][class][class][class] .sale-payment-qr,
body[class][class][class][class][class] .qr-section {
    margin: 1px auto 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
}
body[class][class][class][class][class] .sale-payment-qr img,
body[class][class][class][class][class] .qr-section img,
body[class][class][class][class][class] img.qr-code,
body[class][class][class][class][class] img[class*="qr"] {
    width: 24mm !important;
    height: 24mm !important;
    max-width: 24mm !important;
    max-height: 24mm !important;
    margin: 0 auto 1px !important;
}
body[class][class][class][class][class] .sale-payment-qr-label,
body[class][class][class][class][class] .qr-label {
    margin: 0 !important;
    line-height: 1 !important;
}
/* Give every 80mm receipt a little more breathing room. Keep the footer rule
   separate from its text so the configured footer does not touch the line. */
body.print-80mm[class][class][class][class][class] table tbody td {
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    line-height: 1.18 !important;
}
body.print-80mm[class][class][class][class][class] .line,
body.print-80mm[class][class][class][class][class] .info-row,
body.print-80mm[class][class][class][class][class] .pm-row,
body.print-80mm[class][class][class][class][class] .sum-row {
    padding-top: 1px !important;
    padding-bottom: 1px !important;
    line-height: 1.15 !important;
}
body.print-80mm[class][class][class][class][class] .doc-title,
body.print-80mm[class][class][class][class][class] .sale-invoice-title,
body.print-80mm[class][class][class][class][class] .invoice-word,
body.print-80mm[class][class][class][class][class] .section-title,
body.print-80mm[class][class][class][class][class] .info-label,
body.print-80mm[class][class][class][class][class] .pm-title,
body.print-80mm[class][class][class][class][class] .section {
    margin-top: 3px !important;
    margin-bottom: 2px !important;
    line-height: 1.2 !important;
}
body.print-80mm[class][class][class][class][class] .footer,
body.print-80mm[class][class][class][class][class] .footer-bar {
    margin-top: 3mm !important;
    padding-top: 2mm !important;
}
body.print-80mm[class][class][class][class][class].receiving-document .receiving-payment-separator,
body.print-80mm[class][class][class][class][class].sale-pos-receipt .receiving-payment-separator {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm[class][class][class][class][class].order-request-document .order-number-row {
    justify-content: space-between !important;
    white-space: nowrap !important;
}
body.print-80mm[class][class][class][class][class].order-request-document .order-number-row span:last-child {
    display: block !important;
    margin-left: auto !important;
    text-align: right !important;
    white-space: nowrap !important;
}
body.print-80mm[class][class][class][class][class].order-request-document .items-end-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 2mm !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm[class][class][class][class][class].order-request-document .footer {
    border-top: 0 !important;
}
/* Final three-column contract. This must be last because the print-agent
   compatibility rules above also target sale-items-table by nth-child and
   otherwise restore the old geometry after the document CSS runs. */
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col {
    table-layout: fixed !important;
    width: 100% !important;
}
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th.col-rate,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td.col-rate,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th:nth-child(1),
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td:nth-child(1) {
    width: 28% !important;
    text-align: right !important;
}
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th.col-qty,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td.col-qty,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th:nth-child(2),
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td:nth-child(2) {
    width: 18% !important;
    text-align: center !important;
}
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th.col-total,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td.col-total,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th:nth-child(3),
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td:nth-child(3) {
    width: 54% !important;
    text-align: right !important;
}
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col th,
body.print-80mm[class][class][class][class][class] table.sale-items-table.items-3col td {
    padding-left: 2px !important;
    padding-right: 2px !important;
}
/* The same geometry for tables where Tax/Discount are visible. */
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th.col-rate,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td.col-rate { width: 18% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th.col-qty,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td.col-qty { width: 12% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th.col-tax,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td.col-tax { width: 25% !important; text-align: center !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th.col-discount,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td.col-discount { width: 20% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th.col-total,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td.col-total { width: 25% !important; text-align: right !important; }
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) th,
body.print-80mm[class][class][class][class][class] table.sale-items-table:not(.items-3col) td { padding-left: 2px !important; padding-right: 2px !important; }
/* The PO thermal template uses this dedicated rule before its footer. */
body.print-80mm.order-request-document[class][class][class][class][class] .footer-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 2mm 0 1.5mm !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
/* Keep the 80mm Agent header separator visible after the company tax details. */
body.print-80mm[class][class][class][class][class] .header + .hr,
body.print-80mm[class][class][class][class][class] .print-header + .hr,
body.print-80mm[class][class][class][class][class] .gatepass-header-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px dotted #000 !important;
}
/* Final 80mm item-column widths. The table itself contains only visible
   Tax/Discount cells, so each rendered numeric column shares the full width. */
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):not(:has(th.col-tax)):not(:has(th.col-discount)) .col-rate,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):not(:has(th.col-tax)):not(:has(th.col-discount)) .col-qty,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):not(:has(th.col-tax)):not(:has(th.col-discount)) .col-total {
    width: 33.333333% !important;
    min-width: 0 !important;
}
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-discount):not(:has(th.col-tax)) .col-rate,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-discount):not(:has(th.col-tax)) .col-qty,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-discount):not(:has(th.col-tax)) .col-discount,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-discount):not(:has(th.col-tax)) .col-total,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):not(:has(th.col-discount)) .col-rate,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):not(:has(th.col-discount)) .col-qty,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):not(:has(th.col-discount)) .col-tax,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):not(:has(th.col-discount)) .col-total {
    width: 25% !important;
    min-width: 0 !important;
}
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-rate,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-qty,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-tax,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-discount,
body.print-80mm[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-total {
    width: 20% !important;
    min-width: 0 !important;
}
/* The purchase order prints each line's tax with its rate - 23923.80 (18%) - which
   five equal columns cannot hold on a till roll; the tax and the total take the
   room the quantity does not need. */
body.print-80mm.purchase-order-document[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-rate { width: 19% !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-qty { width: 10% !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-tax { width: 27% !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-discount { width: 17% !important; }
body.print-80mm.purchase-order-document[class][class][class][class][class] table:has(th.col-rate):has(th.col-tax):has(th.col-discount) .col-total { width: 27% !important; }
/* The product-name row spans the table and reads left, whatever the
   positional rules above say about the first column: those describe the
   Rate cell under it, not the name. Six [class] tokens outrank every
   nth-child rule in this sheet and the shared design. */
body.print-80mm[class][class][class][class][class][class] table.sale-items-table tr.product-name-row td,
body.print-80mm[class][class][class][class][class][class] table.item-list tr.product-name-row td,
body.print-80mm[class][class][class][class][class][class] table.items-table tr.product-name-row td { text-align: left !important; }
`;

function agentError(error, fallback) {
    if (error?.name === 'TypeError' || error?.message === 'Failed to fetch') {
        return new Error('Print Agent is offline. Start the agent at 127.0.0.1:8123.');
    }
    return new Error(error?.message || fallback);
}

function printerNames(payload) {
    const rows = Array.isArray(payload) ? payload : payload?.printers || payload?.data || payload?.results || payload?.value || [];
    const values = Array.isArray(rows) ? rows : Object.keys(rows || {});

    return values.map((item) => (typeof item === 'string' ? item : item?.name || item?.printer_name || item?.printer || '')).filter(Boolean);
}

/**
 * Which Print Agent build this browser's computer wants — 'windows',
 * 'linux' or 'macos' — for the download buttons on the Receipts tab.
 *
 * The platform hint comes first, the user agent second, and macOS is checked
 * before Windows because "darwin" contains "win". Nothing recognisable
 * answers Windows: that is where most tills are, and the operator can pick
 * another tab.
 */
export function detectAgentPlatform(nav = typeof navigator === 'undefined' ? {} : navigator) {
    const hint = `${nav.userAgentData?.platform || nav.platform || ''} ${nav.userAgent || ''}`.toLowerCase();
    if (hint.includes('mac') || hint.includes('darwin')) return 'macos';
    if (hint.includes('win')) return 'windows';
    if (hint.includes('linux') || hint.includes('x11')) return 'linux';
    return 'windows';
}

export function getSelectedPrinter(printType = 'thermal') {
    return selectedPrinters[printerKeys[printType] || 'thermal'];
}

export function setSelectedPrinter(printer, printType = 'thermal') {
    const key = printerKeys[printType] || 'thermal';
    selectedPrinters[key] = String(printer || '');
    const storageKey = `inventree.printAgent.printer.${key}`;
    if (selectedPrinters[key]) localStorage.setItem(storageKey, selectedPrinters[key]);
    else localStorage.removeItem(storageKey);
}

export function getPrinterTransport(printer) {
    return /pos|thermal/i.test(String(printer || '')) ? 'escpos' : 'windows';
}

// A printer with no paper — "Microsoft Print to PDF" is the one operators
// pick — has to be told a filename. The Print Agent, a background process with
// no window, settles that itself: it raises the Save dialog on the till's
// desktop and, where nobody could see one, names the file in its own folder.
// Either way the name it proposes is this one, and only the browser knows what
// the document is, so send it. A dialog the operator closes comes back as a 200
// with `cancelled: true` — handled, not failed — so agentRequest does not open
// the browser's print dialog on top of the one just dismissed.
const documentNameLabels = [
    ['gatepass-document', 'Gate Pass'],
    ['refund-document', 'Refund'],
    ['exchange-document', 'Exchange'],
    ['supplier-return-document', 'Supplier Return'],
    ['purchase-order-document', 'Purchase Order'],
    ['supplier-document', 'Supplier Voucher'],
    ['sale-document', 'Invoice'],
    ['sale-pos-receipt', 'Receipt'],
];

export function documentNameFromHtml(html) {
    const markup = String(html || '');
    const bodyClass = (markup.match(/<body[^>]*class=["']([^"']*)["']/i) || [])[1] || '';
    const label = (documentNameLabels.find(([token]) => bodyClass.includes(token)) || [])[1] || 'Document';
    // The reference the operator would search their folder for. Tags are
    // stripped first, because an invoice number split across <span>s is one
    // number to a reader and two to a regex over the markup.
    const text = markup
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ');
    const reference = (text.match(/#\s*([A-Za-z0-9][A-Za-z0-9._/-]{0,29})/) || [])[1] || '';
    return reference ? `${label} ${reference}` : label;
}

export function buildPrintPayload(
    printer,
    content,
    copies = 1,
    type = 'html',
    cut = true,
    requestedTransport = 'windows',
    pageOptions = null,
    documentName = '',
) {
    const printerTransport = getPrinterTransport(printer);
    const isHtml = String(type || '').toLowerCase() === 'html';
    const payload = {
        printer,
        type,
        // HTML receipts must stay on the Windows/GDI renderer so their 80mm
        // CSS (rules, wrapping, column widths, and typography) is honored.
        // Raw ESC/POS payloads still use the printer's native transport.
        transport: isHtml ? 'windows' : printerTransport || requestedTransport,
        copies,
        cut,
        content,
    };

    // Windows printing does not consistently derive orientation from HTML
    // @page rules. Pass the paper geometry explicitly so the Print Agent can
    // select the same orientation as the browser preview.
    // Only the two fields PrintJobRequest actually declares. `page` and
    // `landscape` were also being sent and Pydantic dropped them silently, so
    // the payload and the agent's model had drifted apart for no gain — these
    // two already carry the same information.
    if (isHtml && pageOptions && typeof pageOptions === 'object') {
        payload.paper_size = pageOptions.paperSize;
        payload.orientation = pageOptions.orientation;
    }

    // Ignored by every printer that has paper; the agent only reads it when the
    // target turns out to be a print-to-file driver. 120 is the model's cap.
    if (documentName) payload.document_name = String(documentName).slice(0, 120);

    return payload;
}

async function agentRequest(path, options = {}) {
    const { url, key } = agentConnection();
    let response;
    try {
        response = await fetch(`${url}${path}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': key,
                ...(options.headers || {}),
            },
        });
    } catch (error) {
        throw agentError(error, 'Print Agent is offline.');
    }

    let payload = null;
    try {
        payload = await response.json();
    } catch {
        /* agent may return an empty success */
    }
    if (!response.ok || payload?.success === false) {
        // FastAPI's HTTPException serialises to {"detail": ...}, so without it
        // "Printer 'X' is not installed" reached the cashier as the bare status
        // code. `detail` may also be a validation array rather than a string.
        const detail = Array.isArray(payload?.detail)
            ? payload.detail
                  .map((item) => item?.msg || item?.message)
                  .filter(Boolean)
                  .join('; ')
            : payload?.detail;

        // Now that the key comes from this installation's settings, the two
        // sides can drift apart — and the agent's own wording ("Invalid or
        // missing X-API-Key header") does not say where either side lives.
        if (response.status === 401) {
            throw new Error(
                'The Print Agent rejected this key. It must match on both sides: Configurations → Receipts & Printing → Print Agent, ' +
                    "and api_key in the agent's config.json.",
            );
        }

        throw new Error(payload?.error || payload?.message || detail || `Print request failed (${response.status}).`);
    }
    return payload;
}

/**
 * Send a raw payload straight to a named printer.
 *
 * Deliberately has no browser fallback, unlike every other export here: this
 * carries raw ESC/POS bytes, and there is nothing a print dialog could do with
 * them. HTML documents go through printHtmlReceiptViaAgent, which does fall
 * back. Nothing in the application calls this today — it is the escape hatch
 * for a payload built somewhere other than the browser.
 */
export async function printReceipt(printer, content, copies = 1, type = 'escpos', cut = true, transport = type) {
    const names = await getPrinters();
    if (!printer) throw new Error('No printer is selected. Choose a printer before printing.');
    if (!names.includes(printer)) throw new Error(`Printer "${printer}" is not installed or is no longer available.`);

    return agentRequest('/print', {
        method: 'POST',
        body: JSON.stringify(buildPrintPayload(printer, content, copies, type, cut, transport)),
    });
}

export function encodePrintContent(value) {
    const bytes = new TextEncoder().encode(String(value ?? ''));
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary);
}

function applyA4Typography(html, receiptSize) {
    if (
        String(receiptSize || '')
            .trim()
            .toUpperCase() !== 'A4'
    ) {
        return html;
    }

    const styles = `<style data-inventree-a4-typography>
@page { margin: 0 !important; size: A4 portrait !important; }
* { box-sizing: border-box !important; }
html, body { width: 210mm !important; max-width: 210mm !important; margin: 0 !important; }
body {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 14px !important;
    line-height: 1.4 !important;
    font-weight: 500 !important;
    color: #000 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
}
body, body * { color: #000 !important; }
.document, .half, .receipt-container { width: 100% !important; max-width: none !important; margin: 0 !important; }
.page { width: 100% !important; max-width: none !important; margin: 0 !important; padding: 5mm 6mm !important; }
.top-bar { display: none !important; height: 0 !important; }
.header, .brand, .print-header { width: 100% !important; margin: 0 0 2mm !important; padding: 0 !important; text-align: left !important; }
.header-row, .brand-row { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; }
.header-left, .brand-left { display: flex !important; flex: 1 1 auto !important; min-width: 0 !important; align-items: center !important; gap: 4mm !important; text-align: left !important; }
.header-right, .brand-right { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: right !important; padding-top: 2px !important; }
.logo, .logo-img { display: block !important; width: auto !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
.company, .company-name { font-size: 21px !important; line-height: 1.05 !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: .2px !important; }
.invoice-word, .sale-invoice-title, .doc-title, .title, .document-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; margin-top: 3px !important; }
.invoice-no, .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; text-align: left !important; margin-top: 2px !important; }
.company-meta, .company-sub, .branch-detail, .header-address { font-size: 11px !important; line-height: 1.25 !important; font-weight: 600 !important; margin-top: 2px !important; overflow-wrap: anywhere !important; }
.header-tax, .ntn, .strn { font-size: 9px !important; font-weight: 600 !important; margin-top: 2px !important; }
.header-rule { display: block !important; border: 0 !important; border-top: 1px dashed #000 !important; margin-bottom: 3mm !important; height: 0 !important; }
.sep, .sep-dark, .hr { display: none !important; }
.info-strip { display: flex !important; gap: 8mm !important; margin-bottom: 3mm !important; }
.info-block { flex: 1 1 0 !important; min-width: 0 !important; }
.info-label, .pm-title, .section, .section-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 800 !important; text-transform: uppercase !important; letter-spacing: 1px !important; color: #000 !important; margin-bottom: 6px !important; padding-bottom: 0 !important; border-bottom: 0 !important; }
.customer-name, .info-name { font-size: 14px !important; font-weight: 700 !important; color: #000 !important; margin-bottom: 3px !important; }
.info-row, .line, .pm-row { display: flex !important; gap: 5px !important; margin-top: 3px !important; font-size: 12px !important; line-height: 1.25 !important; font-weight: 500 !important; color: #000 !important; }
.info-row .lbl, .info-row span:first-child, .line span:first-child, .pm-row .pm-lbl { font-size: 12px !important; font-weight: 400 !important; color: #000 !important; min-width: 90px !important; }
.info-row span:last-child, .line span:last-child, .pm-row span:last-child { font-size: 12px !important; font-weight: 400 !important; color: #000 !important; }
table { width: 100% !important; min-width: 100% !important; max-width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; margin-bottom: 3mm !important; }
thead { display: table-header-group !important; }
thead tr { background: #e5e5e5 !important; color: #000 !important; border: 0 !important; }
thead th { padding: 4px 5px !important; font-size: 12px !important; line-height: 1.2 !important; background: #e5e5e5 !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; border-left: 0 !important; border-right: 0 !important; text-transform: uppercase !important; letter-spacing: .4px !important; text-align: left !important; font-weight: 700 !important; }
tbody tr { border-bottom: 1px solid #000 !important; break-inside: avoid !important; page-break-inside: avoid !important; }
tbody tr:nth-child(even) { background: #f9fafb !important; }
tbody td { padding: 4px 5px !important; font-size: 12px !important; line-height: 1.25 !important; font-weight: 500 !important; color: #000 !important; vertical-align: middle !important; word-break: break-word !important; }
tbody td.item-name, td:first-child { font-weight: 700 !important; color: #000 !important; }
.bottom-section { display: flex !important; justify-content: flex-end !important; align-items: flex-start !important; margin-top: 1mm !important; break-inside: avoid !important; page-break-inside: avoid !important; }
.summary-block { flex: 0 0 105mm !important; width: 105mm !important; margin-left: auto !important; border: 0 !important; }
.total-items { display: flex !important; justify-content: space-between !important; font-size: 10px !important; font-weight: 700 !important; padding: 1px 0 !important; }
.sum-row { display: flex !important; justify-content: space-between !important; font-size: 13px !important; font-weight: 500 !important; color: #000 !important; padding: 1.5px 5px !important; border-bottom: 1px solid #000 !important; }
.sum-row.bold { font-weight: 700 !important; }
.sum-row.no-border { border-bottom: 0 !important; }
.total, .total-line, .grand-total, .grand-total-row { display: flex !important; justify-content: space-between !important; background: #e5e5e5 !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; padding: 5px !important; font-size: 15px !important; line-height: 1.25 !important; font-weight: 700 !important; }
.balance, .balance-row { display: flex !important; justify-content: space-between !important; background: #fff3cd !important; border: 1px solid #ffc107 !important; padding: 5px 7px !important; font-size: 10px !important; font-weight: 700 !important; color: #856404 !important; margin-top: 2px !important; }
.footer, .footer-bar, .footer-message, .print-footer, .receipt-footer { font-size: 10px !important; line-height: 1.25 !important; margin-top: 1.5mm !important; border-top: 1px solid #000 !important; padding-top: 1mm !important; color: #000 !important; white-space: pre-line !important; break-inside: avoid !important; page-break-inside: avoid !important; }
.sale-payment-qr { text-align: center !important; margin: 1.5mm 0 1mm !important; break-inside: avoid !important; page-break-inside: avoid !important; }
.sale-payment-qr img { display: block !important; width: 40mm !important; height: 40mm !important; max-width: 100% !important; margin: 0 auto 1mm !important; object-fit: contain !important; }
.sale-payment-qr-label { font-size: 9px !important; line-height: 1 !important; font-weight: 600 !important; color: #000 !important; }
/* Document specific column widths */
.purchase-order-document .po-items-table th:nth-child(1), .purchase-order-document .po-items-table td:nth-child(1) { width: 5% !important; }
.purchase-order-document .po-items-table th:nth-child(2), .purchase-order-document .po-items-table td:nth-child(2) { width: 31% !important; }
.purchase-order-document .po-items-table th:nth-child(4), .purchase-order-document .po-items-table td:nth-child(4) { width: 8% !important; }
.purchase-order-document .po-items-table th:nth-child(3), .purchase-order-document .po-items-table td:nth-child(3) { width: 13% !important; }
.purchase-order-document .po-items-table th:nth-child(5), .purchase-order-document .po-items-table td:nth-child(5) { width: 18% !important; }
.purchase-order-document .po-items-table th:nth-child(6), .purchase-order-document .po-items-table td:nth-child(6) { width: 13% !important; }
.purchase-order-document .po-items-table th:nth-child(7), .purchase-order-document .po-items-table td:nth-child(7) { width: 13% !important; }
body.supplier-document .payment-footer-separator,
body.supplier-document .receiving-footer-separator { display: block !important; width: 100% !important; height: 0 !important; border: 0 !important; border-top: 1px solid #000 !important; margin: 1.5mm 0 0 !important; }
body.supplier-document .footer, body.supplier-document .footer-bar { border-top: 0 !important; }
/* Gate Pass follows the Sale A4 header: no decorative line at the sheet
   edge, one divider after the document number, and one before the footer. */
body[class].gatepass-document .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
body[class].gatepass-document .header-rule { display: block !important; height: 0 !important; border: 0 !important; border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; padding: 0 !important; }
body[class].gatepass-document .footer,
body[class].gatepass-document .footer-bar { border-top: 1px solid #000 !important; margin-top: 1.5mm !important; padding-top: 1mm !important; }
body[class].gatepass-document .total { border-bottom: 0 !important; }
/* Order Request uses the same Sale A4 header rule placement: after the
   Order/WOR number, with no line at the top edge of the sheet. */
body[class].order-request-document .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
body[class].order-request-document .header-rule { display: block !important; height: 0 !important; border: 0 !important; border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; padding: 0 !important; }
body[class].order-request-document .footer,
body[class].order-request-document .footer-bar { border-top: 1px solid #000 !important; margin-top: 1.5mm !important; padding-top: 1mm !important; }
/* Final A4 Sale header contract. Keep this last so every document type uses
   the same header scale, alignment, font family, and weight as Sale. */
body[class] .header-row,
body[class] .brand-row,
body[class] .print-header { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; }
body[class] .header-left,
body[class] .brand-left { display: flex !important; flex: 1 1 auto !important; min-width: 0 !important; align-items: flex-start !important; gap: 4mm !important; text-align: left !important; }
body[class] .header-right,
body[class] .brand-right { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: right !important; }
body[class] .header-left .logo,
body[class] .header-left .logo-img,
body[class] .brand-left .logo,
body[class] .brand-left .logo-img { width: auto !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
 body[class] .company,
 body[class] .company-name { font-family: Arial, Helvetica, sans-serif !important; font-size: 19px !important; line-height: 1.05 !important; font-weight: 700 !important; letter-spacing: 0 !important; text-align: left !important; }
body[class] .invoice-word,
body[class] .sale-invoice-title,
body[class] .doc-title,
body[class] .title,
 body[class] .document-title { font-family: Arial, Helvetica, sans-serif !important; font-size: 14px !important; line-height: 1.15 !important; font-weight: 700 !important; letter-spacing: 0 !important; text-align: left !important; }
body[class] .invoice-no,
body[class] .number { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.2 !important; font-weight: 400 !important; letter-spacing: 0 !important; text-align: left !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .branch-detail,
body[class] .header-address,
body[class] .header-tax,
body[class] .ntn,
body[class] .strn { font-family: Arial, Helvetica, sans-serif !important; font-size: 11px !important; line-height: 1.25 !important; font-weight: 400 !important; letter-spacing: 0 !important; }
 body[class] .header-rule,
 body[class] .po-header-separator { border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; }
/* Keep the PO A4 header identical to Sale and make all document headings
   slightly smaller, but clearly bold. */
 body[class].purchase-order-document .header-row,
 body[class].purchase-order-document .brand-row,
 body[class].purchase-order-document .print-header { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; }
 body[class].purchase-order-document .header-left,
 body[class].purchase-order-document .brand-left { display: flex !important; flex: 1 1 auto !important; min-width: 0 !important; align-items: flex-start !important; gap: 4mm !important; text-align: left !important; }
 body[class].purchase-order-document .header-right,
 body[class].purchase-order-document .brand-right { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: right !important; }
  body[class].purchase-order-document .header-left { display: flex !important; flex: 1 1 auto !important; align-items: flex-start !important; gap: 4mm !important; min-width: 0 !important; padding-left: 0 !important; }
  body[class].purchase-order-document .header-left .logo-img { width: auto !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; align-self: flex-start !important; }
  body[class].purchase-order-document .header-left .brand-details { display: block !important; min-width: 0 !important; text-align: left !important; }
  body[class].purchase-order-document .header-left > .invoice-no { display: none !important; }
 body[class].purchase-order-document .header-left .company-name,
 body[class].purchase-order-document .header-left .invoice-word,
 body[class].purchase-order-document .header-left .invoice-no { margin: 0 !important; padding: 0 !important; text-align: left !important; }
 body[class].purchase-order-document .header-right > .invoice-word,
 body[class].purchase-order-document .header-right > .invoice-no,
 body[class].purchase-order-document .header-right > .logo-img,
 body[class].purchase-order-document .header-right > .company-name { display: none !important; }
 /* PO A4 must use the complete Sale printable frame, not the template's
    legacy .half/page width. */
 body[class].purchase-order-document > .half,
 body[class].purchase-order-document .half,
 body[class].purchase-order-document .page,
 body[class].purchase-order-document .document,
 body[class].purchase-order-document .receipt-container { width: 100% !important; min-width: 0 !important; max-width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important; }
 body[class].purchase-order-document .page { padding: 5mm 6mm !important; }
 body[class].purchase-order-document .po-items-table { width: 100% !important; min-width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
 body[class] .info-label,
 body[class] .pm-title,
 body[class] .section,
 body[class] .section-title { font-size: 12px !important; line-height: 1.15 !important; font-weight: 700 !important; letter-spacing: .5px !important; }
 body[class] .customer-name,
 body[class] .info-name { font-size: 13px !important; line-height: 1.15 !important; font-weight: 700 !important; }
 /* A4 footer: render one divider only. */
 body[class] .payment-footer-separator,
 body[class] .receiving-footer-separator { display: none !important; border: 0 !important; height: 0 !important; margin: 0 !important; padding: 0 !important; }
 body[class] .footer { border-top: 1px solid #000 !important; }
 body[class] .footer-bar { border-top: 1px solid #000 !important; }
 body[class]:has(.footer) .footer-bar { border-top: 0 !important; }
 body[class] .footer-message,
 body[class] .print-footer,
 body[class] .receipt-footer { border-top: 0 !important; }
 /* A4 tables/totals: horizontal rules only, no vertical separators or fills. */
 body[class] table,
 body[class] table th,
 body[class] table td { border-left: 0 !important; border-right: 0 !important; }
 body[class] table thead,
 body[class] table thead tr,
 body[class] table thead th,
 body[class] .total,
 body[class] .total-line,
 body[class] .grand-total,
 body[class] .grand-total-row,
 body[class] .balance,
 body[class] .balance-row { background: transparent !important; background-color: transparent !important; }
 /* Shared A4 alignment contract for Sale, PO, receiving, payment, transfer,
    gate pass, load form, return/exchange, and order request documents. */
 body[class] .page,
 body[class] .half,
 body[class] .document,
 body[class] .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin-left: 0 !important; margin-right: 0 !important; }
 body[class] .page { padding: 5mm 6mm !important; }
 body[class] .header-row,
 body[class] .header,
 body[class] .brand-row,
 body[class] .print-header { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; min-width: 0 !important; gap: 6mm !important; margin: 0 0 2mm !important; padding: 0 !important; }
 body[class] .header-left,
 body[class] .brand-left { display: flex !important; align-items: flex-start !important; justify-content: flex-start !important; flex: 1 1 62% !important; width: 62% !important; max-width: 62% !important; min-width: 0 !important; gap: 4mm !important; text-align: left !important; }
 body[class] .header-right,
 body[class] .brand-right { display: block !important; flex: 0 1 38% !important; width: 38% !important; max-width: 38% !important; min-width: 0 !important; padding-top: 0 !important; text-align: right !important; overflow-wrap: anywhere !important; }
 body[class] .brand-details { display: block !important; min-width: 0 !important; flex: 1 1 auto !important; text-align: left !important; }
 body[class] .header-left .logo,
 body[class] .header-left .logo-img,
 body[class] .brand-left .logo,
 body[class] .brand-left .logo-img { flex: 0 0 auto !important; width: auto !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
 body[class] .header-left .company-name,
 body[class] .header-left .invoice-word,
 body[class] .header-left .invoice-no,
 body[class] .brand-left .company-name,
 body[class] .brand-left .invoice-word,
 body[class] .brand-left .invoice-no { display: block !important; width: auto !important; margin: 0 !important; padding: 0 !important; text-align: left !important; white-space: normal !important; overflow-wrap: anywhere !important; }
 body[class] .header-left .invoice-word,
 body[class] .brand-left .invoice-word { margin-top: 2px !important; }
 body[class] .header-left .invoice-no,
 body[class] .brand-left .invoice-no { margin-top: 1px !important; }
 body[class] .info-strip,
 body[class] .grid { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; align-items: start !important; column-gap: 8mm !important; row-gap: 2mm !important; width: 100% !important; margin: 0 0 3mm !important; }
 body[class] .info-block { min-width: 0 !important; width: auto !important; }
 body[class] .info-label,
 body[class] .section,
 body[class] .section-title { display: block !important; width: 100% !important; margin: 0 0 2mm !important; padding: 0 !important; text-align: left !important; }
 body[class] .info-row,
 body[class] .line,
 body[class] .pm-row { display: flex !important; align-items: baseline !important; justify-content: flex-start !important; width: 100% !important; min-width: 0 !important; gap: 5px !important; margin: 0 !important; padding: 1px 0 !important; }
 body[class] .info-row .lbl,
 body[class] .line > b,
 body[class] .line > span:first-child,
 body[class] .pm-row .pm-lbl { flex: 0 0 90px !important; width: 90px !important; min-width: 90px !important; text-align: left !important; }
 body[class] .info-row > span:last-child,
 body[class] .line > span:last-child,
 body[class] .pm-row > span:last-child { flex: 1 1 auto !important; min-width: 0 !important; text-align: right !important; overflow-wrap: anywhere !important; }
 body[class] .info-row .lbl,
 body[class] .info-row > span:first-child,
 body[class] .line > span:first-child,
 body[class] .pm-row .pm-lbl { flex: 0 0 115px !important; width: 115px !important; min-width: 115px !important; white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
 body[class] table { width: 100% !important; min-width: 100% !important; max-width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; margin: 0 0 3mm !important; }
 body[class] table th,
 body[class] table td { box-sizing: border-box !important; vertical-align: middle !important; overflow-wrap: anywhere !important; }
 body[class] table th { padding: 4px 5px !important; text-align: left !important; white-space: normal !important; }
 body[class] table td { padding: 4px 5px !important; text-align: left !important; }
 body[class] table th.right,
 body[class] table td.right,
 body[class] table th.text-right,
 body[class] table td.text-right { text-align: right !important; }
 body[class] table th.center,
 body[class] table td.center,
 body[class] table th.text-center,
 body[class] table td.text-center { text-align: center !important; }
 body[class] table th:first-child,
 body[class] table td:first-child { text-align: left !important; }
 body[class] .summary-block { width: 105mm !important; max-width: 100% !important; margin-left: auto !important; margin-right: 0 !important; }
 body[class] .bottom-section { width: 100% !important; margin: 1mm 0 0 !important; }
 html:has(body.purchase-order-document),
 body.purchase-order-document { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin: 0 !important; padding: 0 !important; zoom: 1 !important; transform: none !important; }
 body.purchase-order-document .half,
 body.purchase-order-document .page { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; margin-left: 0 !important; margin-right: 0 !important; }
 body.purchase-order-document .page { padding: 5mm 6mm !important; font-size: 14px !important; line-height: 1.4 !important; }
 body.purchase-order-document table { width: 198mm !important; min-width: 198mm !important; max-width: 198mm !important; }
 body[class].order-request-document .footer,
 body[class].order-request-document .footer-bar,
 body[class].order-request-document .footer-message { border: 0 !important; box-shadow: none !important; margin-top: 1mm !important; padding-top: 0 !important; }
 body[class].sale-pos-receipt .sale-items-table thead th { font-size: 14px !important; line-height: 1.2 !important; padding: 5px 6px !important; }
 body[class].sale-pos-receipt .sale-items-table tbody td { font-size: 14px !important; line-height: 1.3 !important; padding: 5px 6px !important; }
 body[class].sale-pos-receipt .sale-items-table tbody td.item-name { font-size: 14px !important; font-weight: 700 !important; }
 body[class].sale-pos-receipt .sale-items-table + .hr + .line { font-size: 14px !important; line-height: 1.3 !important; font-weight: 700 !important; margin-top: 2mm !important; padding: 2px 0 !important; }
 body[class].sale-pos-receipt .sale-items-table + .hr + .line span { font-size: 14px !important; font-weight: 700 !important; }
 /* Return/Refund A4 uses the same Sale A4 frame and typography. The refund
    source contains a legacy .header wrapper, so normalize that wrapper before
    applying the shared Sale header and table geometry. */
 body[class].refund-document .header { display: block !important; width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 0 2mm !important; padding: 0 !important; }
 body[class].refund-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; }
 body[class].refund-document .header-left { display: flex !important; flex: 1 1 62% !important; width: 62% !important; max-width: 62% !important; min-width: 0 !important; align-items: flex-start !important; justify-content: flex-start !important; gap: 4mm !important; text-align: left !important; }
 body[class].refund-document .header-right { display: block !important; flex: 0 1 38% !important; width: 38% !important; max-width: 38% !important; min-width: 0 !important; text-align: right !important; }
 body[class].refund-document .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
 body[class].refund-document .header-left .logo,
 body[class].refund-document .header-left .logo-img { display: block !important; flex: 0 0 auto !important; width: auto !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; }
 body[class].refund-document .info-strip { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; width: 100% !important; margin-bottom: 3mm !important; }
 body[class].refund-document .info-block { display: block !important; width: auto !important; min-width: 0 !important; margin-bottom: 0 !important; }
 body[class].refund-document .line { display: flex !important; flex-direction: row !important; align-items: baseline !important; justify-content: flex-start !important; width: 100% !important; gap: 5px !important; font-size: 12px !important; line-height: 1.25 !important; padding: 1px 0 !important; }
 body[class].refund-document .line > :first-child { flex: 0 0 90px !important; width: 90px !important; min-width: 90px !important; text-align: left !important; white-space: normal !important; }
 body[class].refund-document .line > :last-child { flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
 body[class].refund-document .items-table { display: table !important; width: 100% !important; min-width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin: 0 0 3mm !important; }
 body[class].refund-document .items-table th,
 body[class].refund-document .items-table td { font-size: 12px !important; line-height: 1.25 !important; padding: 4px 5px !important; }
 body[class].refund-document .items-table th:nth-child(1), body[class].refund-document .items-table td:nth-child(1) { width: 5% !important; }
 body[class].refund-document .items-table th:nth-child(2), body[class].refund-document .items-table td:nth-child(2) { width: 31% !important; text-align: left !important; }
 body[class].refund-document .items-table th:nth-child(3), body[class].refund-document .items-table td:nth-child(3) { width: 14% !important; }
 body[class].refund-document .items-table th:nth-child(4), body[class].refund-document .items-table td:nth-child(4) { width: 10% !important; }
 body[class].refund-document .items-table th:nth-child(5), body[class].refund-document .items-table td:nth-child(5) { width: 14% !important; }
 body[class].refund-document .items-table th:nth-child(6), body[class].refund-document .items-table td:nth-child(6) { width: 10% !important; }
 body[class].refund-document .items-table th:nth-child(7), body[class].refund-document .items-table td:nth-child(7) { width: 16% !important; }
 body[class].refund-document .total-line { display: flex !important; justify-content: space-between !important; width: 100% !important; font-size: 15px !important; line-height: 1.25 !important; padding: 5px !important; font-weight: 700 !important; }
 body[class].refund-document .footer { width: 100% !important; font-size: 10px !important; line-height: 1.25 !important; margin-top: 1.5mm !important; padding-top: 1mm !important; }
</style>`;

    return String(html).includes('</head>') ? String(html).replace('</head>', `${styles}</head>`) : `${styles}${html}`;
}

// The compact scale for the 80mm thermal sheet: the base font stack, sizes,
// weight resets, separators and item spacing the shared receipt design does
// not cover. It was dropped out of the print chain in fa2cc03 while the
// function itself stayed behind, so 80mm receipts have printed without it
// since; afc6c23 then deleted the orphan. Restored into its original
// position rather than re-derived.
function apply80mmItemSpacing(html) {
    if (String(html).includes('data-inventree-80mm-compact-scale')) return String(html);
    const styles = `<style data-inventree-80mm-compact-scale>
body[class] {
    /* Segoe UI has a real semibold face on Windows. Arial maps 600/700 to
       the same heavy bitmap in the agent's GDI renderer. */
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-size: 14px !important;
    line-height: 1.25 !important;
    font-weight: 400 !important;
    font-synthesis: none !important;
    color: #222 !important;
    -webkit-font-smoothing: none !important;
    text-rendering: geometricPrecision !important;
}
/* Reset inherited invoice styles; restore emphasis only in the explicit rules below. */
body[class] * { font-weight: 400 !important; color: #222 !important; }
body[class] .hr,
body[class] .sep,
body[class] .sep-dark,
body[class] .rule,
body[class] .header-rule { display: none !important; border: 0 !important; margin: 0 !important; }
body[class] .header,
body[class] .brand,
body[class] .print-header,
body[class] .header-row { margin-bottom: 1mm !important; padding-bottom: 1mm !important; border-bottom: 0 !important; }
/* Keep the document-type separator on every 80mm receipt.  Previously this
   was only drawn when NTN/STRN data was present, which removed the dotted
   rule immediately before titles such as "Sale Invoice" and "Payment
   Receipt" for ordinary receipts. */
body[class] .header-row,
body[class] .header:not(:has(.header-row)),
body[class] .brand:not(:has(.header-row)),
body[class] .print-header:not(:has(.header-row)) { border-bottom: 1px dotted #000 !important; }
body.print-80mm.gatepass-document .gatepass-header-separator {
    display: block !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    border-top: 1px dotted #000 !important;
}
body[class] .receiving-footer-separator,
body[class] .payment-footer-separator,
body[class] .footer-separator,
body[class] .footer-divider { display: none !important; border: 0 !important; height: 0 !important; margin: 0 !important; padding: 0 !important; }
body[class] .footer,
body[class] .footer-bar { border-top: 1px solid #000 !important; margin-top: 1mm !important; padding-top: 1mm !important; }
body[class] .footer-bottom { border-top: 0 !important; }
body[class] .logo,
body[class] .logo-img,
body[class] .refund-logo { max-height: 16mm !important; max-width: 55% !important; }
body[class] .company,
body[class] .company-name { font-size: 18px !important; line-height: 1.1 !important; font-weight: 700 !important; text-transform: none !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .branch-detail,
body[class] .header-address,
body[class] .header-tax,
body[class] .print-details,
body[class] .print-details div { font-size: 11px !important; line-height: 1.2 !important; color: #222 !important; font-weight: 400 !important; }
body[class] .doc-title,
body[class] .sale-invoice-title,
body[class] .title,
body[class] .document-title,
body[class] .invoice-word,
body[class] .receipt-type {
    display: block !important;
    width: 100% !important;
    text-align: center !important;
    font-size: 15px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
    text-transform: none !important;
}
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title,
body[class] .subtitle { font-size: 13px !important; line-height: 1.15 !important; font-weight: 400 !important; text-transform: none !important; }
body[class] .line,
body[class] .info-row,
body[class] .pm-row,
body[class] .sum-row { font-size: 13px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body[class] .line,
body[class] .info-row,
body[class] .pm-row { margin-top: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { margin-top: 1px !important; margin-bottom: 1px !important; }
body[class] .line > :first-child,
body[class] .info-row > :first-child,
body[class] .pm-row > :first-child,
body[class] .line > :last-child,
body[class] .info-row > :last-child,
body[class] .pm-row > :last-child,
body[class] .line span,
body[class] .info-row span,
body[class] .pm-row span { font-weight: 400 !important; color: #222 !important; }
body[class] b,
body[class] strong { font-weight: 400 !important; color: #222 !important; }
body[class] .line.bold,
body[class] .line.bold *,
body[class] .sum-row.bold,
body[class] .sum-row.bold * { font-weight: 400 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title,
body[class] .subtitle,
body[class] .company,
body[class] .company-name,
body[class] .doc-title,
body[class] .sale-invoice-title { font-weight: 400 !important; text-transform: none !important; }
body[class] .total-line span,
body[class] .grand-total span,
body[class] .grand-total-row span { font-weight: 700 !important; }
body[class] table thead th { font-size: 13px !important; line-height: 1.1 !important; padding: 2px 3px !important; font-weight: 400 !important; text-transform: none !important; }
body[class] table tbody td { font-size: 13px !important; line-height: 1.1 !important; padding: 1px 3px !important; font-weight: 400 !important; color: #222 !important; }
body[class] table tbody tr { line-height: 1.1 !important; }
body[class] table tbody tr.product-name-row td,
body[class] table tbody tr.item-name-row td { padding-bottom: 0 !important; }
body[class] table tbody td.item-name { font-weight: 400 !important; color: #222 !important; }
body[class] table tbody td.item-total { font-weight: 400 !important; color: #222 !important; }
body[class] .sum-row { padding-top: 0 !important; padding-bottom: 0 !important; }
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row { font-size: 15px !important; line-height: 1.15 !important; padding: 3px 4px !important; font-weight: 700 !important; }
body[class] .sale-payment-qr img,
body[class] .qr-section img,
body[class] img.qr-code,
body[class] img[class*="qr"] { width: 24mm !important; height: 24mm !important; max-width: 24mm !important; max-height: 24mm !important; }

/* Final 80mm Sale profile: regular labels and amounts, with emphasis only
   on the company name, Sale Invoice title, and Grand Total. */
html:has(body[class].sale-pos-receipt),
html:has(body[class].transfer-document) { width: 80mm !important; min-width: 80mm !important; max-width: 80mm !important; margin: 0 !important; padding: 0 !important; }
body[class].sale-pos-receipt,
body[class].transfer-document { width: 80mm !important; min-width: 80mm !important; max-width: 80mm !important; margin: 0 !important; padding: 2mm 2.5mm !important; zoom: 1 !important; transform: none !important; }
body[class].sale-pos-receipt > *,
body[class].transfer-document > * { width: 100% !important; min-width: 0 !important; max-width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; }
body[class].sale-pos-receipt {
    font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    font-size: 13px !important;
    line-height: 1.25 !important;
    font-weight: 400 !important;
}
body[class].sale-pos-receipt * { font-weight: 400 !important; }
body[class].sale-pos-receipt .company,
body[class].sale-pos-receipt .company-name { font-size: 19px !important; line-height: 1.1 !important; font-weight: 700 !important; text-transform: none !important; }
body[class].sale-pos-receipt .company-meta,
body[class].sale-pos-receipt .company-sub,
body[class].sale-pos-receipt .branch-detail,
body[class].sale-pos-receipt .header-address,
body[class].sale-pos-receipt .header-tax { font-size: 11px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body[class].sale-pos-receipt .doc-title,
body[class].sale-pos-receipt .sale-invoice-title,
body[class].sale-pos-receipt .title,
body[class].sale-pos-receipt .document-title,
body[class].sale-pos-receipt .invoice-word { font-size: 15px !important; line-height: 1.15 !important; font-weight: 700 !important; text-transform: none !important; }
body[class].sale-pos-receipt .section,
body[class].sale-pos-receipt .section-title,
body[class].sale-pos-receipt .info-label,
body[class].sale-pos-receipt .pm-title { font-size: 13px !important; line-height: 1.15 !important; font-weight: 400 !important; text-transform: none !important; }
body[class].sale-pos-receipt .line,
body[class].sale-pos-receipt .info-row,
body[class].sale-pos-receipt .pm-row,
body[class].sale-pos-receipt .sum-row { font-size: 13px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body[class].sale-pos-receipt .line *,
body[class].sale-pos-receipt .info-row *,
body[class].sale-pos-receipt .pm-row *,
body[class].sale-pos-receipt .sum-row * { font-weight: 400 !important; }
body[class].sale-pos-receipt .line.bold,
body[class].sale-pos-receipt .line.bold *,
body[class].sale-pos-receipt .sum-row.bold,
body[class].sale-pos-receipt .sum-row.bold * { font-weight: 400 !important; }
body[class].sale-pos-receipt table thead th { font-size: 13px !important; line-height: 1.15 !important; font-weight: 400 !important; text-transform: none !important; }
body[class].sale-pos-receipt table tbody td { font-size: 13px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body[class].sale-pos-receipt table tbody td.item-name,
body[class].sale-pos-receipt table tbody td.item-total { font-weight: 400 !important; }
body[class].sale-pos-receipt .total-line,
body[class].sale-pos-receipt .grand-total,
body[class].sale-pos-receipt .grand-total-row { font-size: 13px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class].sale-pos-receipt .total-line *,
body[class].sale-pos-receipt .grand-total *,
body[class].sale-pos-receipt .grand-total-row * { font-weight: 700 !important; }
body[class].sale-pos-receipt .footer,
body[class].sale-pos-receipt .footer-bar,
body[class].sale-pos-receipt .footer-message,
body[class].sale-pos-receipt .print-footer,
body[class].sale-pos-receipt .receipt-footer { font-size: 11px !important; line-height: 1.25 !important; font-weight: 400 !important; }
    body[class].sale-pos-receipt .sale-payment-qr-label { font-size: 10px !important; font-weight: 400 !important; }
    body[class].sale-pos-receipt,
    body[class].sale-pos-receipt * {
        font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
    }
    body[class].sale-pos-receipt .company,
    body[class].sale-pos-receipt .company-name,
    body[class].sale-pos-receipt .sale-invoice-title,
    body[class].sale-pos-receipt .doc-title,
    body[class].sale-pos-receipt .invoice-word,
    body[class].sale-pos-receipt .section,
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .info-label,
    body[class].sale-pos-receipt .pm-title,
    body[class].sale-pos-receipt table thead th,
    body[class].sale-pos-receipt .total-line,
    body[class].sale-pos-receipt .grand-total,
    body[class].sale-pos-receipt .grand-total-row {
        text-transform: uppercase !important;
    }
    body[class].sale-pos-receipt .company-meta,
    body[class].sale-pos-receipt .company-sub,
    body[class].sale-pos-receipt .branch-detail,
    body[class].sale-pos-receipt .header-address,
    body[class].sale-pos-receipt .header-tax,
    body[class].sale-pos-receipt .print-details,
    body[class].sale-pos-receipt .line,
    body[class].sale-pos-receipt .info-row,
    body[class].sale-pos-receipt .pm-row,
    body[class].sale-pos-receipt .sum-row,
    body[class].sale-pos-receipt table tbody td,
    body[class].sale-pos-receipt .footer,
    body[class].sale-pos-receipt .footer-bar,
    body[class].sale-pos-receipt .footer-message,
    body[class].sale-pos-receipt .sale-payment-qr-label { text-transform: none !important; }
    body[class].sale-pos-receipt .sale-items-table { table-layout: fixed !important; width: 100% !important; border-collapse: collapse !important; }
    body[class].sale-pos-receipt .sale-items-table th,
    body[class].sale-pos-receipt .sale-items-table td {
        font-family: "Segoe UI", Arial, Helvetica, sans-serif !important;
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: 1.15 !important;
        padding: 2px 2px !important;
        min-width: 0 !important;
        max-width: 100% !important;
        overflow: visible !important;
        text-overflow: clip !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
        vertical-align: middle !important;
    }
    /* Everything in the item table is regular weight so the receipt reads as a
       list, not a wall of bold. Only the Grand Total line below stays bold. */
    body[class].sale-pos-receipt .sale-items-table th *,
    body[class].sale-pos-receipt .sale-items-table td *,
    body[class].sale-pos-receipt .sale-items-table .item-name,
    body[class].sale-pos-receipt .sale-items-table td.item-name,
    body[class].sale-pos-receipt .sale-items-table .item-total,
    body[class].sale-pos-receipt .sale-items-table td.item-total {
        font-weight: 400 !important;
    }
    body[class].sale-pos-receipt .sale-items-table th { font-weight: 400 !important; text-transform: none !important; }
    body[class].sale-pos-receipt .sale-items-table td.right,
    body[class].sale-pos-receipt .sale-items-table th.right { text-align: right !important; white-space: nowrap !important; }
    body[class].sale-pos-receipt .sale-items-table td.center,
    body[class].sale-pos-receipt .sale-items-table th.center { text-align: center !important; white-space: nowrap !important; }
    body[class].sale-pos-receipt .sale-items-table td:last-child,
    body[class].sale-pos-receipt .sale-items-table th:last-child { text-align: right !important; white-space: nowrap !important; }
    body[class].sale-pos-receipt .sale-items-table th:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(3),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(3),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(5),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(5) { width: 20% !important; }
    body[class].sale-pos-receipt .sale-items-table .product-name-row td {
        width: 100% !important;
        max-width: 100% !important;
        overflow: visible !important;
        text-overflow: clip !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;
        text-align: left !important;
        font-weight: 400 !important;
    }
    /* Final 80mm receipt corrections: keep the header rule visible, allow
       long product names to wrap, and restore summary separators. */
    body[class] .header-rule,
    body[class] .po-header-separator {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
        margin: 1mm 0 2mm !important;
        padding: 0 !important;
    }
    body[class].purchase-order-document .po-header-separator {
        display: block !important;
        border-top: 1px dotted #000 !important;
    }
    /* PO product rows have a separate product-name row, so keep the six
       numeric columns visually balanced instead of giving Tax extra width. */
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(1),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(1),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(3),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(3),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(4),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(4),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(5),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(5),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(6),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(6),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(7),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(7) { width: 10.5% !important; }
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > th:nth-child(2),
    body[class].purchase-order-document .po-items-table tr:not(.product-name-row) > td:nth-child(2) { width: 37% !important; }
    body[class].sale-pos-receipt .header + .hr {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
        margin: 1mm 0 2mm !important;
        padding: 0 !important;
    }
    /* Sale thermal receipts use five equal-width columns. */
    body[class].sale-pos-receipt .sale-items-table th:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(3),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(3),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(5),
    body[class].sale-pos-receipt .sale-items-table td:nth-child(5) { width: 20% !important; }
    body[class].transfer-document .transfer-items-table + .hr {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
        margin: 1mm 0 2mm !important;
        padding: 0 !important;
    }
    body[class] .product-name-row td,
    body[class] td.item-name,
    body[class] .po-items-table td.item-name,
    body[class].purchase-order-document .po-items-table tr.product-name-row td.item-name {
        white-space: normal !important;
        overflow: visible !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
        text-overflow: clip !important;
        padding-bottom: 1px !important;
    }
    body[class] .summary-separator {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
        margin: 1mm 0 !important;
        padding: 0 !important;
    }
    body[class] .total-line,
    body[class] .grand-total,
    body[class] .grand-total-row {
        border-top: 1px solid #000 !important;
    }
    /* These are deliberately the last 80mm rules.  The Print Agent stylesheet
       is injected after the document stylesheet, so the older generic reset
       above must not be allowed to hide header rules or clip product names. */
    body[class] .header + .hr,
    body[class].sale-pos-receipt .header + .hr,
    body[class].purchase-order-document .header + .hr,
    body[class] .header-row + .hr,
    body[class] .header-rule,
    body[class] .po-header-separator {
        display: block !important;
        visibility: visible !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 !important;
        padding: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
    }
    body[class].sale-pos-receipt .sale-items-table .product-name-row td,
    body[class].purchase-order-document .sale-items-table .product-name-row td,
    body[class].purchase-order-document .po-items-table .product-name-row td,
    body[class] table td.item-name,
    body[class] table .product-name-row td {
        min-width: 0 !important;
        max-width: 100% !important;
        white-space: normal !important;
        overflow: visible !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
        text-overflow: clip !important;
    }
    /* Final overflow contract for every 80mm document.  This covers values
       that are not product rows as well: rates, quantities, tax, discounts,
       totals, invoice numbers, supplier/payment values, and summary amounts. */
    body[class] table td,
    body[class] table td * ,
    body[class] .line > span,
    body[class] .info-row > span,
    body[class] .sum-row > span,
    body[class] .pm-row > span {
        min-width: 0 !important;
        max-width: 100% !important;
        overflow: visible !important;
        text-overflow: clip !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
    }
    body[class] table td.item-name,
    body[class] table .product-name-row td {
        white-space: normal !important;
        text-align: left !important;
    }
    /* A value may break when it genuinely exceeds the narrow paper, but it
       must never disappear behind an overflow-hidden cell. */
    body[class] table td:not(.item-name),
    body[class] table td:not(.item-name) * {
        white-space: normal !important;
    }
    /* One consistent dotted rule after company address/NTN/STRN and a small,
       predictable gap before the receipt type. */
    body[class].sale-pos-receipt .header + .hr,
    body[class].purchase-order-document .header + .hr,
    body[class].supplier-document .header + .hr,
    body[class].transfer-document .header + .hr,
    body[class].gatepass-document .header + .hr,
    body[class] .header-row + .hr,
    body[class] .header-rule,
    body[class] .po-header-separator {
        display: block !important;
        visibility: visible !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 1mm !important;
        padding: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
    }
    body[class] .doc-title,
    body[class] .sale-invoice-title,
    body[class] .invoice-word {
        margin-top: 1mm !important;
        margin-bottom: 1mm !important;
    }
    /* Gate Pass item header/footer rules are each a single pixel line. */
    body[class].gatepass-document table thead th,
    body[class].gatepass-document table thead th.gatepass-th {
        border-bottom: 1px solid #000 !important;
    }
    body[class].gatepass-document table tbody tr td {
        border-bottom: 0 !important;
    }
    /* Exactly one footer divider for Sale, PO, Transfer, Gate Pass,
       Receiving/Payment, Refund/Return, and Order Request. */
    body[class] .payment-footer-separator,
    body[class] .receiving-footer-separator,
    body[class] .footer-separator,
    body[class] .footer-divider,
    body[class] .footer-message,
    body[class] .footer-bottom,
    body[class] .print-footer,
    body[class] .receipt-footer {
        border-top: 0 !important;
    }
    body[class] .payment-footer-separator,
    body[class] .receiving-footer-separator,
    body[class] .footer-separator,
    body[class] .footer-divider {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
    }
    body[class] .footer,
    body[class] .footer-bar {
        border-top: 1px solid #000 !important;
        margin-top: 1mm !important;
        padding-top: 1mm !important;
    }
    /* Sale 80mm receipts: keep the title clean, use solid emphasis only for
       Net Total, and avoid dotted rules between item values. */
    body[class].sale-pos-receipt .header + .hr,
    body[class].sale-pos-receipt .header-rule,
    body[class].sale-pos-receipt .header,
    body[class].sale-pos-receipt .header-row,
    body[class].sale-pos-receipt .print-header {
        border-bottom: 0 !important;
        border-top: 0 !important;
    }
    body[class].sale-pos-receipt .header + .hr,
    body[class].sale-pos-receipt .header-rule { display: none !important; }
    /* The tax-registration separator belongs immediately after the header,
       before the Sale Invoice title. */
    body[class].sale-pos-receipt .header + .hr {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 1mm !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
    }
    body[class].sale-pos-receipt .sale-items-table tbody td,
    body[class].sale-pos-receipt .item-list tbody td,
    body[class].sale-pos-receipt .sale-items-table tbody tr,
    body[class].sale-pos-receipt .item-list tbody tr { border-bottom: 0 !important; }
    body[class].sale-pos-receipt .sale-items-table th.col-tax,
    body[class].sale-pos-receipt .sale-items-table td.col-tax,
    body[class].sale-pos-receipt .sale-items-table th.col-discount,
    body[class].sale-pos-receipt .sale-items-table td.col-discount,
    body[class].sale-pos-receipt .item-list th:nth-child(3),
    body[class].sale-pos-receipt .item-list td:nth-child(3),
    body[class].sale-pos-receipt .item-list th:nth-child(4),
    body[class].sale-pos-receipt .item-list td:nth-child(4),
    body[class].sale-pos-receipt .item-list .product-name-row td,
    body[class].sale-pos-receipt .item-list .item-name { font-size: 9.5px !important; }
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .info-label,
    body[class].sale-pos-receipt .pm-title { font-size: 13px !important; }
    body[class].sale-pos-receipt .line,
    body[class].sale-pos-receipt .info-row,
    body[class].sale-pos-receipt .sum-row,
    body[class].sale-pos-receipt table tbody td { color: #000 !important; font-weight: 700 !important; }
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .info-label,
    body[class].sale-pos-receipt .doc-title,
    body[class].sale-pos-receipt .sale-invoice-title,
    body[class].sale-pos-receipt .line.bold { font-weight: 800 !important; color: #000 !important; }
    body[class].sale-pos-receipt .net-total-row {
        border-bottom: 1px solid #000 !important;
        padding-bottom: 1px !important;
    }
    /* Last Sale 80mm pass for the Agent renderer: keep it identical to the
       browser preview after all older high-specificity receipt rules run. */
    body[class].sale-pos-receipt .header + .hr,
    body[class].sale-pos-receipt .print-header + .hr,
    body[class].sale-pos-receipt .header-rule {
        display: block !important;
        visibility: visible !important;
        height: 0 !important;
        margin: 1mm 0 !important;
        padding: 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
    }
    body[class].sale-pos-receipt,
    body[class].sale-pos-receipt .line,
    body[class].sale-pos-receipt .info-row,
    body[class].sale-pos-receipt .pm-row,
    body[class].sale-pos-receipt .sum-row,
    body[class].sale-pos-receipt table tbody td,
    body[class].sale-pos-receipt .footer,
    body[class].sale-pos-receipt .footer-bar,
    body[class].sale-pos-receipt .footer-message {
        color: #000 !important;
        font-weight: 700 !important;
    }
    body[class].sale-pos-receipt .company,
    body[class].sale-pos-receipt .company-name,
    body[class].sale-pos-receipt .sale-invoice-title,
    body[class].sale-pos-receipt .doc-title,
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .info-label,
    body[class].sale-pos-receipt .pm-title,
    body[class].sale-pos-receipt table thead th,
    body[class].sale-pos-receipt .total-line,
    body[class].sale-pos-receipt .grand-total,
    body[class].sale-pos-receipt .grand-total-row {
        color: #000 !important;
        font-weight: 800 !important;
    }
    body[class].sale-pos-receipt .sale-items-table thead th,
    body[class].sale-pos-receipt .item-list thead th,
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td,
    body[class].sale-pos-receipt .item-list tbody tr:not(.product-name-row) td {
        text-align: right !important;
    }
    body[class].sale-pos-receipt .sale-items-table th,
    body[class].sale-pos-receipt .sale-items-table td,
    body[class].sale-pos-receipt .item-list th,
    body[class].sale-pos-receipt .item-list td,
    body[class].sale-pos-receipt .sale-items-table th *,
    body[class].sale-pos-receipt .sale-items-table td *,
    body[class].sale-pos-receipt .item-list th *,
    body[class].sale-pos-receipt .item-list td * {
        font-size: 9.5px !important;
        line-height: 1.15 !important;
    }
    body[class].sale-pos-receipt .sale-items-table thead th,
    body[class].sale-pos-receipt .item-list thead th,
    body[class].sale-pos-receipt .sale-items-table th.col-tax,
    body[class].sale-pos-receipt .sale-items-table th.col-discount,
    body[class].sale-pos-receipt .sale-items-table th.col-disc,
    body[class].sale-pos-receipt .sale-items-table th:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table th:nth-child(5),
    body[class].sale-pos-receipt .item-list th:nth-child(3),
    body[class].sale-pos-receipt .item-list th:nth-child(4),
    body[class].sale-pos-receipt .item-list th:nth-child(1),
    body[class].sale-pos-receipt .item-list th:nth-child(2),
    body[class].sale-pos-receipt .item-list th:nth-child(5),
    body[class].sale-pos-receipt .sale-items-table tbody td,
    body[class].sale-pos-receipt .item-list tbody td {
        font-size: 9.5px !important;
        line-height: 1.15 !important;
    }
    body[class].sale-pos-receipt .sale-items-table thead th,
    body[class].sale-pos-receipt .item-list thead th {
        font-weight: 800 !important;
    }
    body[class].sale-pos-receipt .sale-items-table tbody td,
    body[class].sale-pos-receipt .item-list tbody td {
        font-weight: 700 !important;
    }
    body[class].sale-pos-receipt .sale-items-table tbody td,
    body[class].sale-pos-receipt .sale-items-table tbody tr,
    body[class].sale-pos-receipt .item-list tbody td,
    body[class].sale-pos-receipt .item-list tbody tr {
        border-bottom: 0 !important;
    }
    body[class].sale-pos-receipt .sale-items-table + .hr,
    body[class].sale-pos-receipt .item-list + .hr {
        display: block !important;
        visibility: visible !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 !important;
        padding: 0 !important;
        border: 0 !important;
        border-top: 1px solid #000 !important;
    }
    body[class].sale-pos-receipt .net-total-row {
        border-bottom: 1px solid #000 !important;
        padding-bottom: 1px !important;
        margin-bottom: 1px !important;
    }
    body[class].sale-pos-receipt .net-total-row + .hr {
        display: none !important;
    }
    body[class].sale-pos-receipt .sale-payment-qr img,
    body[class].sale-pos-receipt .qr-section img,
    body[class].sale-pos-receipt img.qr-code,
    body[class].sale-pos-receipt img[class*="qr"] {
        image-rendering: pixelated !important;
        background: transparent !important;
        mix-blend-mode: multiply !important;
        filter: grayscale(1) contrast(5) brightness(0.75) !important;
    }
    body[class].sale-pos-receipt .sale-payment-qr-label {
        font-weight: 700 !important;
    }

    /* Final sale receipt cleanup: keep only the header rule after the
       company/NTN/STRN block, use regular weight for receipt data, and keep
       product names bold and flush with the left edge of the item table. */
    body[class].sale-pos-receipt .hr {
        display: none !important;
        border: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
    }
    body[class].sale-pos-receipt .print-header + .hr {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 !important;
        border: 0 !important;
        border-top: 1px dotted #000 !important;
    }
    body[class].sale-pos-receipt .sale-items-table + .hr {
        display: block !important;
        width: 100% !important;
        height: 0 !important;
        margin: 1mm 0 !important;
        border: 0 !important;
        border-top: 1px solid #000 !important;
    }
    body[class].sale-pos-receipt .line,
    body[class].sale-pos-receipt .line *,
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .print-details,
    body[class].sale-pos-receipt .print-details div,
    body[class].sale-pos-receipt .header-tax,
    body[class].sale-pos-receipt .footer,
    body[class].sale-pos-receipt .footer * {
        font-weight: 700 !important;
    }
    body[class].sale-pos-receipt,
    body[class].sale-pos-receipt .line,
    body[class].sale-pos-receipt .section-title,
    body[class].sale-pos-receipt .footer {
        font-size: 14px !important;
        line-height: 1.25 !important;
    }
    body[class].sale-pos-receipt .sale-items-table th,
    body[class].sale-pos-receipt .sale-items-table td {
        font-size: 11px !important;
        line-height: 1.2 !important;
    }
    body[class].sale-pos-receipt .company,
    body[class].sale-pos-receipt .company-name,
    body[class].sale-pos-receipt .sale-invoice-title,
    body[class].sale-pos-receipt table thead th,
    body[class].sale-pos-receipt .total-line,
    body[class].sale-pos-receipt .total-line * {
        font-weight: 700 !important;
    }
    body[class].sale-pos-receipt .sale-items-table tbody td {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        line-height: 1.1 !important;
    }
    body[class].sale-pos-receipt .sale-items-table tbody tr.product-name-row td,
    body[class].sale-pos-receipt .sale-items-table tbody tr.product-name-row td.item-name {
        padding-left: 0 !important;
        padding-right: 0 !important;
        padding-top: 1px !important;
        padding-bottom: 0 !important;
        text-align: left !important;
        font-weight: 700 !important;
    }
    body[class].sale-pos-receipt .sale-items-table thead th:nth-child(1),
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(1) { text-align: right !important; }
    body[class].sale-pos-receipt .sale-items-table thead th:nth-child(2),
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(2) { text-align: center !important; }
    body[class].sale-pos-receipt .sale-items-table thead th:nth-child(3),
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(3) { text-align: right !important; }
    body[class].sale-pos-receipt .sale-items-table thead th:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(4),
    body[class].sale-pos-receipt .sale-items-table thead th:nth-child(5),
    body[class].sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td:nth-child(5) { text-align: right !important; }
    body[class].sale-pos-receipt .sale-items-table tbody tr.product-name-row td {
        white-space: normal !important;
        overflow: visible !important;
        text-overflow: clip !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
    }
    body[class].sale-pos-receipt .sale-items-table td.name-col,
    body[class].sale-pos-receipt .sale-items-table .product-name-row td.name-col {
        display: table-cell !important;
        width: 100% !important;
        max-width: none !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        text-align: left !important;
    }
    body[class].sale-pos-receipt .sale-items-table .name-col,
    body[class].sale-pos-receipt .sale-items-table .product-name-row td {
        overflow: visible !important;
        text-overflow: clip !important;
        white-space: normal !important;
        overflow-wrap: anywhere !important;
        word-break: normal !important;
    }
    html:has(body[class]),
    body[class],
    body[class].sale-pos-receipt {
        overflow: visible !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
    }
    html:has(body[class])::-webkit-scrollbar,
    html:has(body[class])::-webkit-scrollbar-button,
    body[class]::-webkit-scrollbar,
    body[class]::-webkit-scrollbar-button,
    body[class].sale-pos-receipt::-webkit-scrollbar,
    body[class].sale-pos-receipt::-webkit-scrollbar-button {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
        background: transparent !important;
    }
    body[class].supplier-document .receiving-items-title,
    body[class] .receiving-items-title {
        display: none !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
        border: 0 !important;
    }
</style>`;
    return String(html).includes('</head>') ? String(html).replace('</head>', `${styles}</head>`) : `${styles}${html}`;
}

function applyBrowser80mmLayout(html, receiptSize) {
    const normalizedSize = String(receiptSize || '')
        .trim()
        .toUpperCase();
    if (normalizedSize === 'A4') {
        const styles = `<style data-inventree-browser-a4-layout>
@page { size: A4 portrait !important; margin: 4mm !important; }
html { width: calc(210mm - 8mm) !important; margin: 0 !important; padding: 0 !important; }
body.print-a4 { width: calc(210mm - 8mm) !important; min-width: calc(210mm - 8mm) !important; max-width: calc(210mm - 8mm) !important; min-height: 0 !important; height: auto !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
body.print-a4 { padding: 1mm 4mm !important; box-sizing: border-box !important; }
body.print-a4 .half, body.print-a4 .page, body.print-a4 .document, body.print-a4 .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; min-height: 0 !important; height: auto !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body.print-a4 > *,
body.print-a4 .header,
body.print-a4 .header-row,
body.print-a4 .header-rule,
body.print-a4 .info-strip,
body.print-a4 table,
body.print-a4 .bottom-section,
body.print-a4 .total-line,
body.print-a4 .footer,
body.print-a4 .footer-bar { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
body.print-a4 .info-strip { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; align-items: start !important; }
body.print-a4 .grid { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; align-items: start !important; width: 100% !important; }
body.print-a4 .info-block { width: 100% !important; min-width: 0 !important; }
body.print-a4 .info-block.full-width { grid-column: 1 / -1 !important; }
body.print-a4 .info-block .line,
body.print-a4 .info-block .info-row,
body.print-a4 .grid > * .line,
body.print-a4 .grid > * .info-row { display: grid !important; grid-template-columns: minmax(26mm, 38%) minmax(0, 1fr) !important; column-gap: 3mm !important; align-items: start !important; width: 100% !important; }
body.print-a4 .info-block .line > :first-child,
body.print-a4 .info-block .info-row > :first-child,
body.print-a4 .grid > * .line > :first-child,
body.print-a4 .grid > * .info-row > :first-child { min-width: 0 !important; text-align: left !important; }
body.print-a4 .info-block .line > :last-child,
body.print-a4 .info-block .info-row > :last-child,
body.print-a4 .grid > * .line > :last-child,
body.print-a4 .grid > * .info-row > :last-child { min-width: 0 !important; max-width: 100% !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4 .header-left,
body.print-a4 .header-right { min-width: 0 !important; overflow-wrap: anywhere !important; }
body.print-a4 table { table-layout: fixed !important; }
body.print-a4 table th,
body.print-a4 table td { min-width: 0 !important; overflow-wrap: anywhere !important; }
body.print-a4 .footer, body.print-a4 .footer-bar, body.print-a4 .footer-message { break-inside: avoid !important; page-break-inside: avoid !important; }
body.print-a4.sale-pos-receipt .sale-payment-qr img,
body.print-a4.sale-document .sale-payment-qr img { width: 40mm !important; height: 40mm !important; max-width: 100% !important; object-fit: contain !important; image-rendering: auto !important; }
/* Load Form and Return/Exchange must consume the printable frame only once. */
body.print-a4.load-form-document,
body.print-a4.refund-document { height: auto !important; max-height: none !important; overflow: visible !important; }
body.print-a4.load-form-document .page { width: 100% !important; max-width: 100% !important; min-height: 0 !important; height: auto !important; max-height: none !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
body.print-a4.load-form-document .delivery,
body.print-a4.load-form-document .delivery-head,
body.print-a4.load-form-document .delivery-meta,
body.print-a4.load-form-document .signature,
body.print-a4.refund-document .header,
body.print-a4.refund-document .info-strip { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
body.print-a4.load-form-document .delivery-head,
body.print-a4.load-form-document .delivery-meta,
body.print-a4.load-form-document .footer-bottom { display: grid !important; grid-template-columns: minmax(0, 1fr) max-content !important; gap: 4mm !important; align-items: start !important; }
body.print-a4.load-form-document .delivery-head > :first-child,
body.print-a4.load-form-document .delivery-meta > :first-child,
body.print-a4.load-form-document .footer-bottom > :first-child { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.load-form-document .delivery-head > :last-child,
body.print-a4.load-form-document .delivery-meta > :last-child,
body.print-a4.load-form-document .footer-bottom > :last-child { max-width: 65mm !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.load-form-document .signature { display: grid !important; grid-template-columns: max-content minmax(0, 1fr) !important; gap: 4mm !important; }
body.print-a4.load-form-document .signature span { width: 100% !important; min-width: 0 !important; }
body.print-a4.load-form-document .load-summary-table th:nth-child(1),
body.print-a4.load-form-document .load-summary-table td:nth-child(1) { width: 60% !important; }
body.print-a4.load-form-document .load-summary-table th:nth-child(2),
body.print-a4.load-form-document .load-summary-table td:nth-child(2),
body.print-a4.load-form-document .load-summary-table th:nth-child(3),
body.print-a4.load-form-document .load-summary-table td:nth-child(3) { width: 20% !important; }
body.print-a4.load-form-document .delivery-items-table th:nth-child(1),
body.print-a4.load-form-document .delivery-items-table td:nth-child(1) { width: 46% !important; }
body.print-a4.load-form-document .delivery-items-table th:nth-child(n+2),
body.print-a4.load-form-document .delivery-items-table td:nth-child(n+2) { width: 18% !important; }
body.print-a4.refund-document .header-left { display: grid !important; grid-template-columns: auto minmax(0, 1fr) !important; gap: 4mm !important; min-width: 0 !important; }
body.print-a4.refund-document .brand-details,
body.print-a4.refund-document .invoice-no { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.refund-document .company-meta { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.refund-document .line { display: grid !important; grid-template-columns: minmax(30mm, 35%) minmax(0, 1fr) !important; gap: 3mm !important; align-items: start !important; width: 100% !important; }
body.print-a4.refund-document .line > :last-child { min-width: 0 !important; max-width: 100% !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.refund-document .items-table th,
body.print-a4.refund-document .items-table td { padding-left: 4px !important; padding-right: 4px !important; }
body.print-a4.refund-document .items-table th:first-child,
body.print-a4.refund-document .items-table td:first-child { width: 5% !important; }
body.print-a4.refund-document .items-table th:nth-child(2),
body.print-a4.refund-document .items-table td:nth-child(2) { width: 35% !important; text-align: left !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a4.refund-document .items-table .col-rate { width: 13% !important; }
body.print-a4.refund-document .items-table .col-qty { width: 8% !important; }
body.print-a4.refund-document .items-table .col-tax,
body.print-a4.refund-document .items-table .col-discount { width: 10% !important; }
body.print-a4.refund-document .items-table .col-total { width: 19% !important; }
body.print-a4.refund-document .items-table .col-rate,
body.print-a4.refund-document .items-table .col-qty,
body.print-a4.refund-document .items-table .col-tax,
body.print-a4.refund-document .items-table .col-discount,
body.print-a4.refund-document .items-table .col-total { white-space: nowrap !important; }
/* Browser A4 scale: match the Sale A4 design standard. */
body.print-a4 { font-family: Arial, Helvetica, sans-serif !important; font-size: 14px !important; line-height: 1.4 !important; color: #000 !important; }
body.print-a4 .company-name, body.print-a4 .company { font-size: 21px !important; line-height: 1.05 !important; font-weight: 600 !important; text-transform: none !important; letter-spacing: .2px !important; color: #000 !important; }
body.print-a4 .doc-title, body.print-a4 .invoice-word, body.print-a4 .sale-invoice-title, body.print-a4 .title, body.print-a4 .document-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; color: #000 !important; }
body.print-a4 .invoice-no, body.print-a4 .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; color: #000 !important; }
body.print-a4 .company-meta, body.print-a4 .company-sub, body.print-a4 .branch-detail, body.print-a4 .header-address { font-size: 11px !important; line-height: 1.25 !important; font-weight: 600 !important; color: #000 !important; }
body.print-a4 .header-tax, body.print-a4 .ntn, body.print-a4 .strn { font-size: 9px !important; font-weight: 600 !important; color: #000 !important; }
body.print-a4 .info-label, body.print-a4 .section-title, body.print-a4 .pm-title, body.print-a4 .section { font-size: 14px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; text-transform: uppercase !important; color: #000 !important; }
body.print-a4 .customer-name, body.print-a4 .info-name { font-size: 14px !important; font-weight: 700 !important; color: #000 !important; }
body.print-a4 .info-row, body.print-a4 .line, body.print-a4 .pm-row, body.print-a4 .sum-row { font-size: 12px !important; line-height: 1.25 !important; font-weight: 500 !important; color: #000 !important; }
body.print-a4 .info-row .lbl, body.print-a4 .info-row span:first-child, body.print-a4 .line span:first-child, body.print-a4 .pm-row .pm-lbl { font-size: 12px !important; font-weight: 400 !important; color: #000 !important; }
body.print-a4 .info-row span:last-child, body.print-a4 .line span:last-child, body.print-a4 .pm-row span:last-child { font-size: 12px !important; font-weight: 400 !important; color: #000 !important; }
body.print-a4 table thead th, body.print-a4 table th { font-size: 12px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; background: #e5e5e5 !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; border-left: 0 !important; border-right: 0 !important; }
body.print-a4 table tbody td, body.print-a4 table td { font-size: 12px !important; line-height: 1.25 !important; padding: 4px 5px !important; font-weight: 500 !important; color: #000 !important; }
body.print-a4 .sum-row { font-size: 13px !important; font-weight: 500 !important; padding: 1.5px 5px !important; border-bottom: 1px solid #000 !important; color: #000 !important; }
body.print-a4 .sum-row.bold { font-weight: 700 !important; }
body.print-a4 .total-line, body.print-a4 .grand-total, body.print-a4 .grand-total-row { font-size: 15px !important; line-height: 1.25 !important; padding: 5px !important; font-weight: 700 !important; background: #e5e5e5 !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a4 .balance, body.print-a4 .balance-row { font-size: 10px !important; font-weight: 700 !important; background: #fff3cd !important; border: 1px solid #ffc107 !important; padding: 5px 7px !important; color: #856404 !important; margin-top: 2px !important; }
body.print-a4 .footer, body.print-a4 .footer-bar, body.print-a4 .footer-message { font-size: 10px !important; line-height: 1.25 !important; margin-top: 1.5mm !important; padding-top: 1mm !important; border-top: 1px solid #000 !important; color: #000 !important; }
</style>`;
        return addBrowserPrintClass(String(html), 'print-a4', styles);
    }
    if (normalizedSize === 'A5' || normalizedSize === 'A5P') {
        const isPortrait = normalizedSize === 'A5P';
        const pageWidth = isPortrait ? '148mm' : '210mm';
        const pageHeight = isPortrait ? '210mm' : '148mm';
        const printClass = isPortrait ? 'print-a5p' : 'print-a5';
        const styles = `<style data-inventree-browser-a5-layout>
@page { size: A5 ${isPortrait ? 'portrait' : 'landscape'} !important; margin: 5mm 0 0 !important; }
html { width: ${pageWidth} !important; max-width: ${pageWidth} !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body.${printClass} { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; min-height: ${pageHeight} !important; height: auto !important; max-height: none !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; box-sizing: border-box !important; }
body.${printClass} .half, body.${printClass} .page, body.${printClass} .document, body.${printClass} .receipt-container { width: 100% !important; max-width: 100% !important; min-height: 0 !important; height: auto !important; max-height: none !important; margin-left: 0 !important; margin-right: 0 !important; overflow: visible !important; }
body.${printClass} > * { width: 100% !important; max-width: 100% !important; }
body.${printClass} .header-row,
body.${printClass} .header-rule,
body.${printClass} .info-strip,
body.${printClass} table,
body.${printClass} .bottom-section,
body.${printClass} .total-line,
body.${printClass} .footer,
body.${printClass} .footer-bar { width: 100% !important; max-width: 100% !important; }
body.print-a5p { overflow-x: hidden !important; }
body.print-a5p .header-row,
body.print-a5p .header,
body.print-a5p .brand,
body.print-a5p .info-strip,
body.print-a5p .grid { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }
body.print-a5p .header-row,
body.print-a5p .info-strip,
body.print-a5p .grid { gap: 3mm !important; }
body.print-a5p .header-left,
body.print-a5p .header-right,
body.print-a5p .info-block { min-width: 0 !important; max-width: 100% !important; overflow-wrap: anywhere !important; }
body.print-a5p table { width: 100% !important; min-width: 0 !important; max-width: 100% !important; table-layout: fixed !important; }
body.print-a5p table th,
body.print-a5p table td { min-width: 0 !important; max-width: 100% !important; overflow-wrap: anywhere !important; word-break: break-word !important; white-space: normal !important; }
body.print-a5p img { max-width: 100% !important; height: auto !important; }
body.print-a5p .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
body.${printClass},
body.${printClass} .half,
body.${printClass} .page,
body.${printClass} .document,
body.${printClass} .receipt-container { overflow: visible !important; }
body.${printClass},
body.${printClass} > *,
body.${printClass} .half,
body.${printClass} .page,
body.${printClass} .document,
body.${printClass} .receipt-container { width: 100% !important; max-width: 100% !important; }
body.${printClass}.sale-pos-receipt .header-left { display: grid !important; grid-template-columns: auto minmax(0, 1fr) !important; grid-template-rows: auto auto auto !important; align-items: start !important; column-gap: 3mm !important; }
body.${printClass}.sale-pos-receipt .header-left .logo-img { grid-column: 1 !important; grid-row: 1 / span 3 !important; }
body.${printClass}.sale-pos-receipt .header-left .company-name { grid-column: 2 !important; grid-row: 1 !important; }
body.${printClass}.sale-pos-receipt .header-left .invoice-word { grid-column: 2 !important; grid-row: 2 !important; display: block !important; }
body.${printClass}.sale-pos-receipt .header-left .invoice-no { grid-column: 2 !important; grid-row: 3 !important; display: block !important; }
body.${printClass}.sale-pos-receipt .header-right > .invoice-word,
body.${printClass}.sale-pos-receipt .header-right > .invoice-no,
body.${printClass}.sale-pos-receipt .header-right > .logo-img,
body.${printClass}.sale-pos-receipt .header-right > .company-name { display: none !important; }
body.${printClass}.sale-pos-receipt .header-right .company-sub { font-size: 8px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body.${printClass}.load-form-document,
body.${printClass}.load-form-document .page { height: auto !important; max-height: none !important; overflow: visible !important; }
body.${printClass}.order-request-document .footer-bar { border-top: 0 !important; margin-top: 1mm !important; padding-top: 0 !important; }
body.${printClass}.order-request-document .footer-message { border-top: 0 !important; }
body.${printClass}[class] .company-name,
body.${printClass}[class] .company { font-size: 17px !important; line-height: 1.05 !important; }
body.${printClass} .footer, body.${printClass} .footer-bar, body.${printClass} .footer-message { break-inside: avoid !important; page-break-inside: avoid !important; }
body.${printClass}.sale-pos-receipt .sale-payment-qr img,
body.${printClass}.sale-document .sale-payment-qr img { width: 38mm !important; height: 38mm !important; max-width: 100% !important; object-fit: contain !important; image-rendering: auto !important; }
/* Shared transfer-style A5 profile for every module and orientation. */
body.${printClass} { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.3 !important; color: #111827 !important; padding: 0 ${isPortrait ? '10mm' : '0'} !important; box-sizing: border-box !important; }
body.${printClass} .page { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 3mm ${isPortrait ? '10mm' : '6mm'} !important; box-sizing: border-box !important; }
body.${printClass} .top-bar,
body.${printClass} .hr,
body.${printClass} .sep,
body.${printClass} .sep-dark,
body.${printClass} .rule { display: none !important; border: 0 !important; }
body.${printClass} .header-rule { display: block !important; border-top: 1px dashed #64748b !important; }
body.${printClass} .header,
body.${printClass} .brand,
body.${printClass} .header-row { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; margin-bottom: 2.5mm !important; text-align: left !important; }
body.${printClass} .header-left { display: flex !important; align-items: center !important; flex: 1 1 auto !important; gap: 3mm !important; text-align: left !important; }
body.${printClass} .header-left .brand-details { flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body.${printClass} .header-left .invoice-word,
body.${printClass} .header-left .invoice-no { display: block !important; width: 100% !important; margin-left: 0 !important; text-align: left !important; }
body.${printClass} .header-right { display: block !important; flex: 0 0 ${isPortrait ? '43%' : '38%'} !important; width: ${isPortrait ? '43%' : '38%'} !important; text-align: right !important; }
body.${printClass} .logo,
body.${printClass} .logo-img { max-width: 34mm !important; max-height: 10mm !important; object-fit: contain !important; }
body.${printClass} .company,
body.${printClass} .company-name { font-size: 11px !important; line-height: 1.1 !important; font-weight: 800 !important; }
body.${printClass} .company-meta,
body.${printClass} .company-sub,
body.${printClass} .branch-detail,
body.${printClass} .header-address,
body.${printClass} .header-tax { font-size: 8px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body.${printClass} .invoice-word,
body.${printClass} .sale-invoice-title,
body.${printClass} .title,
body.${printClass} .doc-title { font-size: 15px !important; line-height: 1.1 !important; }
body.${printClass} .invoice-no,
body.${printClass} .number { font-size: 10px !important; line-height: 1.15 !important; }
body.${printClass} .info-label,
body.${printClass} .section-title,
body.${printClass} .pm-title { font-size: 8px !important; line-height: 1.15 !important; }
body.${printClass} .info-row,
body.${printClass} .line,
body.${printClass} .pm-row,
body.${printClass} .sum-row { font-size: 9px !important; line-height: 1.2 !important; }
body.${printClass} table th { font-size: 8.5px !important; }
body.${printClass} table td { font-size: 9px !important; }
body.${printClass} .header-rule { display: block !important; border-top: 1px dashed #64748b !important; margin: 2mm 0 !important; }
body.${printClass} .info-strip { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; gap: 8mm !important; width: 100% !important; margin-bottom: 2mm !important; }
body.${printClass} .info-block { flex: 1 1 0 !important; min-width: 0 !important; width: auto !important; }
body.${printClass} .section,
body.${printClass} .section-title,
body.${printClass} .info-label,
body.${printClass} .pm-title { color: #111827 !important; text-transform: uppercase !important; font-weight: 800 !important; letter-spacing: .5px !important; border-bottom: 1px solid #777 !important; padding-bottom: 2px !important; margin: 2mm 0 1mm !important; }
body.${printClass} table { display: table !important; width: 100% !important; min-width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; margin: 0 0 2mm !important; }
body.${printClass} table th { background: #f3f4f6 !important; color: #111827 !important; border-top: 1px solid #333 !important; border-bottom: 1px solid #333 !important; padding: 3px 5px !important; text-transform: uppercase !important; font-weight: 800 !important; }
body.print-a5p table th { border-left: 0 !important; border-right: 0 !important; }
body.${printClass} table td { color: #343a40 !important; border-bottom: 1px solid #999 !important; padding: 2.2px 5px !important; vertical-align: middle !important; word-break: break-word !important; }
body.${printClass} .bottom-section { display: flex !important; justify-content: flex-end !important; align-items: flex-start !important; margin-top: 1mm !important; }
body.${printClass} .summary-block { flex: 0 0 78mm !important; width: 78mm !important; margin-left: auto !important; }
body.${printClass} .sum-row,
body.${printClass} .total-items { display: flex !important; justify-content: space-between !important; width: 100% !important; }
body.${printClass} .grand-total,
body.${printClass} .grand-total-row,
body.${printClass} .total-line { display: flex !important; justify-content: space-between !important; width: 100% !important; background: #f3f4f6 !important; border-top: 1px solid #333 !important; border-bottom: 1px solid #333 !important; }
body.${printClass} .footer,
body.${printClass} .footer-bar { border-top: 1px solid #343a40 !important; margin-top: 1.5mm !important; padding-top: 1mm !important; color: #343a40 !important; font-size: 9px !important; line-height: 1.25 !important; }
body.${printClass} .header-row { margin-bottom: 1mm !important; }
body.${printClass} .header-rule { margin: 1mm 0 !important; }
body.${printClass} { color: #111827 !important; }
body.${printClass} * { color: #111827 !important; }
body.${printClass} .info-row,
body.${printClass} .line,
body.${printClass} .sum-row,
body.${printClass} table td { color: #111827 !important; font-weight: 500 !important; }
body.${printClass} table th,
body.${printClass} .info-label,
body.${printClass} .section-title,
body.${printClass} .grand-total,
body.${printClass} .grand-total-row { color: #111827 !important; font-weight: 700 !important; }
/* Final A5 safe printable area: keep every column inside the sheet edges. */
body.${printClass} .page { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 3mm ${isPortrait ? '10mm' : '6mm'} !important; box-sizing: border-box !important; overflow: visible !important; }
body.${printClass} table,
body.${printClass} .header-row,
body.${printClass} .info-strip,
body.${printClass} .bottom-section,
body.${printClass} .footer,
body.${printClass} .footer-bar { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }
/* Final shared A5 frame: equal gutters and equal detail-column spacing. */
body.${printClass} { padding: 3mm 0 0 !important; box-sizing: border-box !important; }
body.${printClass} > *,
body.${printClass} .half,
body.${printClass} .page,
body.${printClass} .document,
body.${printClass} .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding-left: 0 !important; padding-right: 0 !important; box-sizing: border-box !important; }
body.${printClass} > * { padding-left: ${isPortrait ? '10mm' : '6mm'} !important; padding-right: ${isPortrait ? '10mm' : '6mm'} !important; }
body.${printClass} > .half,
body.${printClass} > .document { padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass} > .half > .page,
body.${printClass} > .document > .page,
body.${printClass} > .page { padding: 3mm ${isPortrait ? '10mm' : '6mm'} !important; }
body.${printClass}.order-request-document > .half { padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass}.order-request-document .half > .page { padding: 3mm ${isPortrait ? '10mm' : '6mm'} !important; }
body.${printClass} .info-strip { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; align-items: start !important; }
body.${printClass} .grid { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; align-items: start !important; width: 100% !important; }
body.${printClass} .info-block { width: 100% !important; min-width: 0 !important; }
body.${printClass} .info-block.full-width { grid-column: 1 / -1 !important; }
body.${printClass} .info-block .info-row,
body.${printClass} .grid > * .info-row { display: grid !important; grid-template-columns: minmax(22mm, 38%) minmax(0, 1fr) !important; column-gap: 3mm !important; align-items: start !important; width: 100% !important; }
body.${printClass} .info-block .info-row > :first-child,
body.${printClass} .grid > * .info-row > :first-child { min-width: 0 !important; text-align: left !important; }
body.${printClass} .info-block .info-row > :last-child,
body.${printClass} .grid > * .info-row > :last-child { min-width: 0 !important; max-width: 100% !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass} .header-left,
body.${printClass} .header-right { min-width: 0 !important; overflow-wrap: anywhere !important; }
body.${printClass} table { table-layout: fixed !important; }
body.${printClass} table th,
body.${printClass} table td { min-width: 0 !important; overflow-wrap: anywhere !important; }
body.${printClass} thead tr,
body.${printClass} tbody tr { break-inside: avoid !important; page-break-inside: avoid !important; }
body.print-a5p table th { border-left: 0 !important; border-right: 0 !important; }
/* Final document-specific paper geometry prevents nested A4 widths from
   overflowing A5 and lets longer load/refund content continue to page two. */
body.${printClass}.load-form-document,
body.${printClass}.refund-document { min-height: ${pageHeight} !important; height: auto !important; max-height: none !important; overflow: visible !important; }
body.${printClass}.load-form-document .page { width: 100% !important; max-width: 100% !important; min-height: 0 !important; height: auto !important; max-height: none !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
body.${printClass}.load-form-document .delivery,
body.${printClass}.load-form-document .delivery-head,
body.${printClass}.load-form-document .delivery-meta,
body.${printClass}.load-form-document .signature,
body.${printClass}.refund-document .header,
body.${printClass}.refund-document .info-strip { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
body.${printClass}.load-form-document .delivery-head,
body.${printClass}.load-form-document .delivery-meta,
body.${printClass}.load-form-document .footer-bottom { display: grid !important; grid-template-columns: minmax(0, 1fr) max-content !important; gap: 3mm !important; align-items: start !important; }
body.${printClass}.load-form-document .delivery-head > :first-child,
body.${printClass}.load-form-document .delivery-meta > :first-child,
body.${printClass}.load-form-document .footer-bottom > :first-child { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass}.load-form-document .delivery-head > :last-child,
body.${printClass}.load-form-document .delivery-meta > :last-child,
body.${printClass}.load-form-document .footer-bottom > :last-child { max-width: 48mm !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass}.load-form-document .signature { display: grid !important; grid-template-columns: max-content minmax(0, 1fr) !important; gap: 3mm !important; }
body.${printClass}.load-form-document .signature span { width: 100% !important; min-width: 0 !important; }
body.${printClass}.load-form-document .load-summary-table th:nth-child(1),
body.${printClass}.load-form-document .load-summary-table td:nth-child(1) { width: 60% !important; }
body.${printClass}.load-form-document .load-summary-table th:nth-child(2),
body.${printClass}.load-form-document .load-summary-table td:nth-child(2),
body.${printClass}.load-form-document .load-summary-table th:nth-child(3),
body.${printClass}.load-form-document .load-summary-table td:nth-child(3) { width: 20% !important; }
body.${printClass}.load-form-document .delivery-items-table th:nth-child(1),
body.${printClass}.load-form-document .delivery-items-table td:nth-child(1) { width: 46% !important; }
body.${printClass}.load-form-document .delivery-items-table th:nth-child(n+2),
body.${printClass}.load-form-document .delivery-items-table td:nth-child(n+2) { width: 18% !important; }
body.${printClass}.refund-document .header-left { display: grid !important; grid-template-columns: auto minmax(0, 1fr) !important; gap: 3mm !important; min-width: 0 !important; }
body.${printClass}.refund-document .brand-details,
body.${printClass}.refund-document .invoice-no { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass}.refund-document .company-meta { min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass}.refund-document .items-table th,
body.${printClass}.refund-document .items-table td { padding-left: 3px !important; padding-right: 3px !important; }
body.${printClass}.refund-document .items-table th:first-child,
body.${printClass}.refund-document .items-table td:first-child { width: 5% !important; }
body.${printClass}.refund-document .items-table th:nth-child(2),
body.${printClass}.refund-document .items-table td:nth-child(2) { width: 35% !important; text-align: left !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body.${printClass}.refund-document .items-table .col-rate { width: 13% !important; }
body.${printClass}.refund-document .items-table .col-qty { width: 8% !important; }
body.${printClass}.refund-document .items-table .col-tax,
body.${printClass}.refund-document .items-table .col-discount { width: 10% !important; }
body.${printClass}.refund-document .items-table .col-total { width: 19% !important; }
body.${printClass}.refund-document .items-table .col-rate,
body.${printClass}.refund-document .items-table .col-qty,
body.${printClass}.refund-document .items-table .col-tax,
body.${printClass}.refund-document .items-table .col-discount,
body.${printClass}.refund-document .items-table .col-total { white-space: nowrap !important; }
/* Final refund paper profile: keep A5 landscape and A5 portrait from
   inheriting a narrow legacy receipt column. */
body.${printClass}.refund-document { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; padding: 0 !important; }
body.${printClass}.refund-document .half { width: ${pageWidth} !important; max-width: ${pageWidth} !important; min-width: ${pageWidth} !important; margin: 0 auto !important; padding: 0 !important; }
body.${printClass}.refund-document .page { width: calc(${pageWidth} - 13mm) !important; max-width: calc(${pageWidth} - 13mm) !important; min-width: 0 !important; margin: 0 auto !important; padding: 2.5mm 0 !important; box-sizing: border-box !important; }
body.${printClass}.refund-document .header,
body.${printClass}.refund-document .header-row,
body.${printClass}.refund-document .header-rule,
body.${printClass}.refund-document .info-strip,
body.${printClass}.refund-document .items-table,
body.${printClass}.refund-document .section-title,
body.${printClass}.refund-document .line,
body.${printClass}.refund-document .total-line,
body.${printClass}.refund-document .footer { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin-left: auto !important; margin-right: auto !important; box-sizing: border-box !important; }
body.${printClass}.refund-document .header,
body.${printClass}.refund-document .header-row,
body.${printClass}.refund-document .header-rule,
body.${printClass}.refund-document .info-strip,
body.${printClass}.refund-document .items-table,
body.${printClass}.refund-document .section-title,
body.${printClass}.refund-document .line,
body.${printClass}.refund-document .total-line { width: 100% !important; max-width: 100% !important; }
body.${printClass}.refund-document .info-strip { display: block !important; width: 100% !important; }
body.${printClass}.refund-document .info-block { display: block !important; width: 100% !important; min-width: 0 !important; margin-bottom: 2mm !important; }
body.${printClass}.refund-document .header-row { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 38%) !important; column-gap: 6mm !important; width: 100% !important; align-items: start !important; }
body.${printClass}.refund-document .header-left { display: flex !important; width: 100% !important; min-width: 0 !important; }
body.${printClass}.refund-document .brand-details { display: block !important; flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; }
body.${printClass}.refund-document .header-left .logo,
body.${printClass}.refund-document .header-left .logo-img { flex: 0 0 auto !important; width: auto !important; }
body.${printClass}.refund-document .header-right { width: 100% !important; min-width: 0 !important; }
body.${printClass}.refund-document .items-table { table-layout: auto !important; }
body.${printClass}.refund-document .items-table th,
body.${printClass}.refund-document .items-table td,
body.${printClass}.refund-document .items-table th { min-width: 0 !important; overflow-wrap: normal !important; word-break: normal !important; }
body.${printClass}.refund-document * { word-break: normal !important; }
/* A5 refund details are stacked deliberately. Two flex columns make long
   refund/exchange values collapse into character-wide columns on browsers
   that apply the A5 paper scale before layout. */
/* One final rule for every refund line, including totals and settlement rows
   that sit outside the two detail blocks. */
body.${printClass}.refund-document .line { display: grid !important; grid-template-columns: 28% minmax(0, 72%) !important; column-gap: 3mm !important; align-items: start !important; width: 100% !important; }
body.${printClass}.refund-document .line > :first-child { min-width: 0 !important; white-space: nowrap !important; }
body.${printClass}.refund-document .line > :last-child { min-width: 0 !important; max-width: 100% !important; white-space: normal !important; overflow-wrap: anywhere !important; text-align: right !important; }
body.${printClass}.purchase-order-document .po-header-separator { display: block !important; border-top: 1px dotted #64748b !important; height: 0 !important; margin: 1mm 0 2mm !important; }
body.${printClass}.purchase-order-document .sep,
body.${printClass}.purchase-order-document .summary-separator { display: none !important; }
body.${printClass}.purchase-order-document .info-label { font-size: 11px !important; line-height: 1.15 !important; font-weight: 600 !important; letter-spacing: .4px !important; }
body.${printClass}.purchase-order-document .info-strip + .sep { display: block !important; border: 0 !important; border-top: 1px solid #000 !important; height: 0 !important; margin: 1mm 0 2mm !important; }
body.${printClass}.purchase-order-document .footer-bar { border-top: 1px solid #000 !important; margin-top: 1mm !important; padding-top: 1mm !important; }
body.${printClass}.purchase-order-document .total-line,
body.${printClass}.purchase-order-document .grand-total-row { border-top: 0 !important; }
body.${printClass}.purchase-order-document .sum-row + .total-line,
body.${printClass}.purchase-order-document .sum-row + .grand-total-row { border-top: 0 !important; }
body.${printClass}.purchase-order-document .po-items-table { table-layout: fixed !important; width: 100% !important; }
body.${printClass}.purchase-order-document .po-items-table th,
body.${printClass}.purchase-order-document .po-items-table td { font-size: 10px !important; line-height: 1.15 !important; padding: 2px 4px !important; font-weight: 500 !important; overflow-wrap: normal !important; word-break: normal !important; }
body.${printClass}.purchase-order-document .po-items-table th { font-weight: 700 !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(1), body.${printClass}.purchase-order-document .po-items-table td:nth-child(1) { width: 5% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(2), body.${printClass}.purchase-order-document .po-items-table td:nth-child(2) { width: 37% !important; text-align: left !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(4), body.${printClass}.purchase-order-document .po-items-table td:nth-child(4) { width: 14% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(3), body.${printClass}.purchase-order-document .po-items-table td:nth-child(3) { width: 13% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(5), body.${printClass}.purchase-order-document .po-items-table td:nth-child(5) { width: 14% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(6), body.${printClass}.purchase-order-document .po-items-table td:nth-child(6) { width: 8% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(7), body.${printClass}.purchase-order-document .po-items-table td:nth-child(7) { width: 9% !important; }
body.${printClass}.purchase-order-document .po-items-table th:nth-child(1),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(1),
body.${printClass}.purchase-order-document .po-items-table th:nth-child(3),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(3),
body.${printClass}.purchase-order-document .po-items-table th:nth-child(4),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(4),
body.${printClass}.purchase-order-document .po-items-table th:nth-child(5),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(5),
body.${printClass}.purchase-order-document .po-items-table th:nth-child(6),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(6),
body.${printClass}.purchase-order-document .po-items-table th:nth-child(7),
body.${printClass}.purchase-order-document .po-items-table td:nth-child(7) { white-space: nowrap !important; word-break: normal !important; overflow-wrap: normal !important; }
body.${printClass}.purchase-order-document .line,
body.${printClass}.purchase-order-document .info-row,
body.${printClass}.purchase-order-document .sum-row,
body.${printClass}.purchase-order-document .po-items-table th,
body.${printClass}.purchase-order-document .po-items-table td { font-size: 9px !important; line-height: 1.15 !important; }
body.${printClass}.purchase-order-document .po-extra-tax-row,
body.${printClass}.purchase-order-document .tax-details + .line,
body.${printClass}.purchase-order-document .tax-details + .sum-row { border-top: 0 !important; }
body.${printClass}.load-form-document { font-size: 8.5px !important; line-height: 1.15 !important; }
body.${printClass}.load-form-document .section,
body.${printClass}.load-form-document .line,
body.${printClass}.load-form-document .delivery-head,
body.${printClass}.load-form-document .delivery-meta,
body.${printClass}.load-form-document .signature,
body.${printClass}.load-form-document .footer { font-size: 8.5px !important; line-height: 1.15 !important; }
body.${printClass}.load-form-document table th { font-size: 8px !important; padding: 2px 3px !important; }
body.${printClass}.load-form-document table td { font-size: 8.5px !important; padding: 1.8px 3px !important; }
/* Final browser refund profile: use the complete A5 sheet instead of the
   legacy narrow receipt frame inherited by .half/.page. */
body.${printClass}.refund-document { width: ${pageWidth} !important; max-width: ${pageWidth} !important; min-width: ${pageWidth} !important; padding: 0 !important; box-sizing: border-box !important; }
body.${printClass}.refund-document .half { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 0 !important; }
body.${printClass}.refund-document .page { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 3mm 8mm !important; box-sizing: border-box !important; }
body.${printClass}.refund-document .header,
body.${printClass}.refund-document .header-row,
body.${printClass}.refund-document .header-rule,
body.${printClass}.refund-document .info-strip,
body.${printClass}.refund-document .info-block,
body.${printClass}.refund-document .section-title,
body.${printClass}.refund-document .line,
body.${printClass}.refund-document table,
body.${printClass}.refund-document .total-line,
body.${printClass}.refund-document .footer { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
body.${printClass}.refund-document .info-strip { display: block !important; }
body.${printClass}.refund-document .info-block { display: block !important; margin-bottom: 2mm !important; }
/* Match the working sale A5 renderer: one centered header and simple
   full-width label/value rows. */
body.${printClass}.refund-document .header-row { display: block !important; width: 100% !important; text-align: center !important; }
body.${printClass}.refund-document .header-left,
body.${printClass}.refund-document .header-right { display: block !important; width: 100% !important; max-width: 100% !important; min-width: 0 !important; text-align: center !important; }
body.${printClass}.refund-document .header-left { margin-bottom: 2mm !important; }
body.${printClass}.refund-document .brand-details { display: block !important; width: 100% !important; text-align: center !important; }
body.${printClass}.refund-document .header-left .logo,
body.${printClass}.refund-document .header-left .logo-img { margin: 0 auto 2px !important; }
body.${printClass}.refund-document .line { display: flex !important; flex-direction: row !important; justify-content: space-between !important; gap: 4px !important; }
body.${printClass}.refund-document .line > :first-child { flex: 0 0 38% !important; width: 38% !important; text-align: left !important; }
body.${printClass}.refund-document .line > :last-child { flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; text-align: right !important; }
body.${printClass}.refund-document table { table-layout: auto !important; }
body.${printClass}.refund-document table th,
body.${printClass}.refund-document table td { min-width: 0 !important; word-break: normal !important; overflow-wrap: normal !important; }
/* Fallback selectors intentionally omit the print-a5 class. Some browser
   print-preview paths preserve the document class but strip the generated
   print marker; the refund must remain readable in that path too. */
body.refund-document .document,
body.refund-document .page,
body.refund-document .header,
body.refund-document .header-row,
body.refund-document .info-strip,
body.refund-document table,
body.refund-document .footer { width: 100% !important; max-width: none !important; min-width: 0 !important; box-sizing: border-box !important; }
body.refund-document .page { margin: 0 !important; padding: 3mm 6mm !important; }
body.refund-document .header-row { display: block !important; text-align: center !important; }
body.refund-document .header-left,
body.refund-document .header-right,
body.refund-document .brand-details { display: block !important; width: 100% !important; max-width: none !important; min-width: 0 !important; flex: none !important; text-align: center !important; }
body.refund-document .company-name,
body.refund-document .invoice-word,
body.refund-document .invoice-no { display: block !important; width: 100% !important; max-width: none !important; white-space: normal !important; overflow-wrap: anywhere !important; word-break: normal !important; text-align: center !important; }
body.refund-document .info-strip,
body.refund-document .info-block { display: block !important; }
body.refund-document .info-block { margin-bottom: 2mm !important; }
body.refund-document .line { display: flex !important; width: 100% !important; min-width: 0 !important; }
body.refund-document .line > :first-child { flex: 0 0 38% !important; width: 38% !important; white-space: nowrap !important; }
body.refund-document .line > :last-child { flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; white-space: normal !important; overflow-wrap: anywhere !important; word-break: normal !important; }
body.refund-document table,
body.refund-document .items-table,
body.refund-document .items-table.sale-items-table { display: table !important; width: 100% !important; min-width: 100% !important; max-width: none !important; table-layout: fixed !important; margin-left: 0 !important; margin-right: 0 !important; }
body.refund-document .items-table.sale-items-table th,
body.refund-document .items-table.sale-items-table td { min-width: 0 !important; max-width: none !important; overflow: visible !important; word-break: normal !important; overflow-wrap: normal !important; }
/* Unified Sale-style A5 profile for every document. Content-specific markup
   may differ, but the paper rhythm, header, tables, totals, and footer stay
   identical across sales, refunds, orders, receiving, payments, and load
   forms. */
body.${printClass} { font-family: Arial, Helvetica, sans-serif !important; color: #000 !important; background: #fff !important; line-height: 1.4 !important; padding: 0 !important; }
body.${printClass} .page { width: 100% !important; max-width: none !important; margin: 0 !important; padding: 3mm 4mm !important; box-sizing: border-box !important; }
body.${printClass} .header { display: block !important; width: 100% !important; max-width: none !important; min-width: 0 !important; margin: 0 0 1.5mm !important; padding: 0 !important; }
body.${printClass} .header-row { display: flex !important; flex-direction: row !important; justify-content: space-between !important; align-items: flex-start !important; gap: 6mm !important; width: 100% !important; margin-bottom: 1.5mm !important; }
body.${printClass} .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; align-items: center !important; gap: 3mm !important; text-align: left !important; }
body.${printClass} .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; width: auto !important; max-width: 100% !important; text-align: left !important; }
body.${printClass} .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; min-width: 0 !important; text-align: right !important; padding-top: 2px !important; }
body.${printClass}.refund-document .header-left { flex: 0 1 57% !important; width: 57% !important; }
body.${printClass}.refund-document .header-right { flex: 0 1 43% !important; width: 43% !important; }
body.${printClass} .logo-img,
body.${printClass} .logo { display: block !important; max-height: 12mm !important; max-width: 27mm !important; object-fit: contain !important; }
body.${printClass} .company-name,
body.${printClass} .company { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass} .invoice-word,
body.${printClass} .sale-invoice-title,
body.${printClass} .doc-title,
body.${printClass} .title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass} .invoice-no,
body.${printClass} .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.${printClass}.refund-document .company-name,
body.${printClass}.refund-document .invoice-word,
body.${printClass}.refund-document .invoice-no { white-space: nowrap !important; word-break: normal !important; overflow-wrap: normal !important; }
body.${printClass} .company-sub,
body.${printClass} .company-meta,
body.${printClass} .header-address,
body.${printClass} .header-tax { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body.${printClass} .header-rule { display: block !important; border-top: 1px dashed #000 !important; margin: 0 0 2mm !important; height: 0 !important; }
body.${printClass} .info-strip { display: flex !important; flex-direction: row !important; gap: 8mm !important; width: 100% !important; margin-bottom: 2mm !important; }
body.${printClass} .info-block { flex: 1 1 0 !important; width: calc(50% - 4mm) !important; min-width: 0 !important; }
body.${printClass}.refund-document .info-strip { display: flex !important; flex-direction: row !important; }
body.${printClass}.refund-document .info-block { flex: 1 1 0 !important; width: calc(50% - 4mm) !important; min-width: 0 !important; }
body.${printClass} .info-label,
body.${printClass} .section-title,
body.${printClass} .pm-title,
body.${printClass} .section { font-size: 9px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: 1px !important; margin: 0 0 3px !important; padding: 0 !important; border: 0 !important; text-transform: uppercase !important; }
body.${printClass} .line,
body.${printClass} .info-row,
body.${printClass} .sum-row,
body.${printClass} .pm-row { display: flex !important; justify-content: space-between !important; gap: 4px !important; width: 100% !important; min-width: 0 !important; padding: .8px 0 !important; font-size: 10px !important; line-height: 1.25 !important; }
body.${printClass} .line > :first-child,
body.${printClass} .info-row > :first-child,
body.${printClass} .sum-row > :first-child,
body.${printClass} .pm-row > :first-child { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: left !important; }
body.${printClass} .line > :last-child,
body.${printClass} .info-row > :last-child,
body.${printClass} .sum-row > :last-child,
body.${printClass} .pm-row > :last-child { flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; word-break: normal !important; }
body.${printClass} table,
body.${printClass} .items-table,
body.${printClass} .sale-items-table { display: table !important; width: 100% !important; min-width: 100% !important; max-width: none !important; table-layout: fixed !important; border-collapse: collapse !important; margin: 0 0 2mm !important; }
body.${printClass} thead { display: table-header-group !important; }
body.${printClass} thead th { padding: 4px 5px !important; font-size: 9.5px !important; line-height: 1.2 !important; font-weight: 600 !important; white-space: nowrap !important; background: #e5e5e5 !important; color: #000 !important; border: 1px solid #000 !important; }
body.${printClass} tbody td { padding: 2.6px 5px !important; font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; color: #000 !important; vertical-align: middle !important; word-break: normal !important; overflow-wrap: break-word !important; }
body.${printClass} tbody tr { border-bottom: 1px solid #000 !important; break-inside: avoid !important; page-break-inside: avoid !important; }
body.${printClass} .item-name { font-weight: 700 !important; text-align: left !important; white-space: normal !important; word-break: normal !important; }
body.${printClass} .total-line,
body.${printClass} .grand-total,
body.${printClass} .grand-total-row { display: flex !important; justify-content: space-between !important; width: 100% !important; padding: 5px !important; margin: 1mm 0 0 !important; font-size: 10px !important; line-height: 1.2 !important; font-weight: 700 !important; background: #e5e5e5 !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.${printClass} .footer,
body.${printClass} .footer-bar { width: 100% !important; margin-top: 1.5mm !important; padding-top: 1mm !important; border-top: 1px solid #000 !important; font-size: 9px !important; line-height: 1.25 !important; color: #000 !important; }
body.${printClass} .hr:has(+ table),
body.${printClass} .sep:has(+ table) { display: none !important; }
body.${printClass} .section-title + table thead th,
body.${printClass} .info-label + table thead th { border-top: 0 !important; }
/* Refund/exchange uses the exact Sale A5 header alignment. */
body.${printClass}.refund-document .header { display: block !important; width: 100% !important; text-align: left !important; margin: 0 0 1.5mm !important; }
body.${printClass}.refund-document .header-row { display: flex !important; flex-direction: row !important; justify-content: space-between !important; align-items: flex-start !important; gap: 6mm !important; width: 100% !important; text-align: left !important; }
body.${printClass}.refund-document .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; max-width: none !important; align-items: center !important; gap: 3mm !important; text-align: left !important; }
body.${printClass}.refund-document .brand-details { display: block !important; flex: 1 1 auto !important; width: auto !important; max-width: 100% !important; text-align: left !important; }
body.${printClass}.refund-document .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; text-align: right !important; padding-top: 2px !important; }
body.${printClass}.refund-document .company-name,
body.${printClass}.refund-document .invoice-word,
body.${printClass}.refund-document .invoice-no { width: auto !important; text-align: left !important; white-space: normal !important; }
body.${printClass}.refund-document .info-strip { display: flex !important; flex-direction: row !important; gap: 8mm !important; width: 100% !important; }
body.${printClass}.refund-document .info-block { flex: 1 1 0 !important; width: auto !important; min-width: 0 !important; }
/* Final identical A5 header for every receipt type. */
body.${printClass} .header,
body.${printClass} .brand,
body.${printClass} .print-header { display: block !important; width: 100% !important; max-width: none !important; min-width: 0 !important; margin: 0 0 1.5mm !important; padding: 0 !important; text-align: left !important; }
body.${printClass} .header-row { display: flex !important; flex-direction: row !important; justify-content: space-between !important; align-items: flex-start !important; gap: 6mm !important; width: 100% !important; margin: 0 0 1.5mm !important; }
body.${printClass} .header-left,
body.${printClass} .print-brand { display: flex !important; flex: 1 1 auto !important; width: auto !important; max-width: none !important; min-width: 0 !important; align-items: center !important; gap: 3mm !important; text-align: left !important; }
body.${printClass} .brand-details { display: block !important; flex: 1 1 auto !important; width: auto !important; max-width: 100% !important; min-width: 0 !important; text-align: left !important; }
body.${printClass} .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; min-width: 0 !important; padding-top: 2px !important; text-align: right !important; }
body.${printClass} .header .logo,
body.${printClass} .header .logo-img,
body.${printClass} .brand .logo,
body.${printClass} .brand .logo-img,
body.${printClass} .print-brand .logo { display: block !important; width: auto !important; max-width: 27mm !important; max-height: 12mm !important; margin: 0 !important; object-fit: contain !important; }
body.${printClass} .company-name,
body.${printClass} .company { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass} .invoice-word,
body.${printClass} .sale-invoice-title,
body.${printClass} .doc-title,
body.${printClass} .title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass} .invoice-no,
body.${printClass} .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; text-align: left !important; }
body.${printClass} .company-sub,
body.${printClass} .company-meta,
body.${printClass} .header-address,
body.${printClass} .header-tax { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
/* A5 tables use clean horizontal rules only; remove internal vertical cell
   borders from Sale, Refund, Exchange, PO, receiving, and payment tables. */
body.${printClass} table thead th,
body.${printClass} table tbody td { border-left: 0 !important; border-right: 0 !important; }
body.${printClass} table thead th,
body.${printClass} .section-title + table thead th,
body.${printClass} .info-label + table thead th { border-top: 1px solid #000 !important; }
body.${printClass} .sum-row + .grand-total,
body.${printClass} .sum-row + .grand-total-row,
body.${printClass} .sum-row + .total-line { border-top: 0 !important; }
body.${printClass} .sale-payment-qr { background: transparent !important; border: 0 !important; }
body.${printClass} .sale-payment-qr img { background: transparent !important; border: 0 !important; mix-blend-mode: multiply !important; filter: grayscale(.85) contrast(1.15) brightness(1.08) !important; opacity: .88 !important; }
/* Keep Agent-rendered Sale, Exchange, and Gate Pass documents on the full
   printable A5 sheet. Legacy .half/.document widths otherwise leave a blank
   strip on the right side of portrait output. */
body.${printClass}.sale-document,
body.${printClass}.sale-pos-receipt,
body.${printClass}.gatepass-document,
body.${printClass}.exchange-document { width: ${pageWidth} !important; max-width: ${pageWidth} !important; min-width: ${pageWidth} !important; box-sizing: border-box !important; }
body.${printClass}.sale-document > .document,
body.${printClass}.sale-document > .half,
body.${printClass}.sale-pos-receipt > .document,
body.${printClass}.sale-pos-receipt > .half,
body.${printClass}.gatepass-document > .document,
body.${printClass}.gatepass-document > .half,
body.${printClass}.exchange-document > .document,
body.${printClass}.exchange-document > .half,
body.${printClass}.sale-document .document,
body.${printClass}.sale-document .half,
body.${printClass}.sale-pos-receipt .document,
body.${printClass}.sale-pos-receipt .half,
body.${printClass}.gatepass-document .document,
body.${printClass}.gatepass-document .half,
body.${printClass}.exchange-document .document,
body.${printClass}.exchange-document .half { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important; }
/* Portrait table headers stay light; legacy Gate Pass rules used a black fill. */
body.${printClass} table thead,
body.${printClass} table thead tr,
body.${printClass} table thead th { background: #fff !important; color: #111827 !important; }
/* PO uses the same typography scale as the Sale A5 receipt. */
body.${printClass}.purchase-order-document .info-label,
body.${printClass}.purchase-order-document .section-title,
body.${printClass}.purchase-order-document .pm-title,
body.${printClass}.purchase-order-document .section { font-size: 9px !important; line-height: 1.2 !important; }
body.${printClass}.purchase-order-document .line,
body.${printClass}.purchase-order-document .info-row,
body.${printClass}.purchase-order-document .sum-row,
body.${printClass}.purchase-order-document .pm-row { font-size: 10px !important; line-height: 1.25 !important; }
body.${printClass}.purchase-order-document table th,
body.${printClass}.purchase-order-document .po-items-table th { font-size: 9.5px !important; line-height: 1.2 !important; }
body.${printClass}.purchase-order-document table td,
body.${printClass}.purchase-order-document .po-items-table td { font-size: 10.5px !important; line-height: 1.25 !important; }
body.${printClass}.purchase-order-document .total-line,
body.${printClass}.purchase-order-document .grand-total,
body.${printClass}.purchase-order-document .grand-total-row { font-size: 10px !important; line-height: 1.2 !important; }
/* Final edge-to-edge A5 frame. The source templates contain their own
   portrait gutters, so override them after every document-specific rule. */
body.${printClass} { padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass} .page,
body.${printClass} .half > .page,
body.${printClass} .document > .page { width: 100% !important; max-width: 100% !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 2mm !important; padding-right: 2mm !important; box-sizing: border-box !important; }
body.${printClass} table,
body.${printClass} .header-row,
body.${printClass} .info-strip,
body.${printClass} .footer,
body.${printClass} .footer-bar { width: 100% !important; max-width: 100% !important; }
html:has(body.${printClass}) { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
body.${printClass} { width: 100% !important; min-width: 0 !important; max-width: none !important; margin: 0 !important; }
body.${printClass} > *,
body.${printClass} .half,
body.${printClass} .document,
body.${printClass} .receipt-container,
body.${printClass} .page { margin-left: 0 !important; margin-right: 0 !important; }
/* Keep a small equal printable gutter in browser preview and on paper. */
body.${printClass} .page { padding: 1mm 4mm !important; box-sizing: border-box !important; }
/* Slightly larger shared A5 type, matching the PO print scale. */
body.${printClass} { font-size: 10.5px !important; line-height: 1.3 !important; }
body.${printClass} .info-label,
body.${printClass} .section-title,
body.${printClass} .pm-title,
body.${printClass} .section { font-size: 9.5px !important; }
body.${printClass} .line,
body.${printClass} .info-row,
body.${printClass} .sum-row,
body.${printClass} .pm-row { font-size: 10.5px !important; }
body.${printClass} table th { font-size: 10px !important; }
body.${printClass} table td { font-size: 11px !important; }
@media print {
  @page { size: ${isPortrait ? '148mm 210mm' : '210mm 148mm'} !important; margin: 5mm !important; }
}
/* Browser fallback scale: match the fixed-geometry Agent without changing
   table, flex, or grid dimensions. */
body.${printClass} { font-size: 9.8px !important; }
body.${printClass} .company-name, body.${printClass} .company { font-size: 10.2px !important; }
body.${printClass} .doc-title, body.${printClass} .invoice-word, body.${printClass} .title { font-size: 14px !important; }
body.${printClass} .info-label, body.${printClass} .section-title, body.${printClass} .pm-title { font-size: 8.8px !important; }
body.${printClass} .line, body.${printClass} .info-row, body.${printClass} .sum-row, body.${printClass} .pm-row { font-size: 9.8px !important; }
body.${printClass} table th { font-size: 9.3px !important; }
body.${printClass} table td { font-size: 10.2px !important; }
body.${printClass} .total-line, body.${printClass} .grand-total, body.${printClass} .grand-total-row { font-size: 9.8px !important; }
body.${printClass} table th,
body.${printClass} table td { min-width: 0 !important; max-width: 100% !important; white-space: normal !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
body.${printClass} .header-left,
body.${printClass} .header-right,
body.${printClass} .info-block,
body.${printClass} .info-row,
body.${printClass} .line { min-width: 0 !important; max-width: 100% !important; overflow-wrap: anywhere !important; }
/* Final shared A5 frame: keep equal left/right gutters in browser preview
   and make every A5 document follow the Sale/A4 content frame. */
@page { size: ${isPortrait ? '148mm 210mm' : '210mm 148mm'} !important; margin: 5mm 0 0 !important; }
html { width: ${pageWidth} !important; max-width: ${pageWidth} !important; margin: 0 !important; padding: 0 !important; }
body.${printClass} { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; margin: 0 !important; padding: 5mm 8mm !important; box-sizing: border-box !important; }
body.${printClass} > *,
body.${printClass} .half,
body.${printClass} .page,
body.${printClass} .document,
body.${printClass} .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 0 !important; padding-right: 0 !important; box-sizing: border-box !important; }
body.${printClass} .company,
body.${printClass} .company-name { font-size: 15px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body.${printClass} .invoice-word,
body.${printClass} .sale-invoice-title,
body.${printClass} .doc-title,
body.${printClass} .title,
body.${printClass} .document-title { font-size: 12px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body.${printClass} .section,
body.${printClass} .section-title,
body.${printClass} .info-label,
body.${printClass} .pm-title { font-size: 8.5px !important; line-height: 1.15 !important; font-weight: 700 !important; }
body.${printClass} table th { font-weight: 700 !important; }
/* Final common A5 header profile for every receipt. */
body.${printClass} .header,
body.${printClass} .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 1.5mm !important; }
body.${printClass} .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; max-width: none !important; min-width: 0 !important; align-items: flex-start !important; gap: 3mm !important; text-align: left !important; }
body.${printClass} .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; min-width: 0 !important; text-align: right !important; padding-top: 0 !important; }
body.${printClass} .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body.${printClass} .header-left .logo,
body.${printClass} .header-left .logo-img { display: block !important; flex: 0 0 auto !important; max-width: 27mm !important; max-height: 12mm !important; margin: 0 !important; object-fit: contain !important; }
body.${printClass} .header-right .company-sub,
body.${printClass} .header-right .company-meta,
body.${printClass} .header-right .header-address,
body.${printClass} .header-right .header-tax,
body.${printClass} .header-right .branch-detail { font-weight: 700 !important; }
body.${printClass}.exchange-document .doc-title,
body.${printClass}.exchange-document .sale-invoice-title,
body.${printClass}.exchange-document .section-title,
body.${printClass}.exchange-document .info-label,
body.${printClass}.exchange-document .pm-title { font-weight: 600 !important; }
body.${printClass}.order-request-document table th:nth-child(3),
body.${printClass}.order-request-document table td:nth-child(3),
body.${printClass}.order-request-document table th:nth-child(5),
body.${printClass}.order-request-document table td:nth-child(5) { width: 14% !important; padding-left: 5px !important; padding-right: 5px !important; white-space: nowrap !important; }
body.${printClass}.transfer-document .section-title + table thead th,
body.${printClass}.transfer-document .info-label + table thead th { border-top: 0 !important; }
body.${printClass}.transfer-document .section-title + table { margin-top: 0 !important; }
/* PO A5 uses the same final Sale A5 type scale; the legacy PO rules above
   are intentionally overridden here. */
body.${printClass}.purchase-order-document .info-label,
body.${printClass}.purchase-order-document .section-title,
body.${printClass}.purchase-order-document .section { font-size: 8.5px !important; line-height: 1.15 !important; }
body.${printClass}.purchase-order-document .line,
body.${printClass}.purchase-order-document .info-row,
body.${printClass}.purchase-order-document .sum-row { font-size: 9.8px !important; line-height: 1.25 !important; }
body.${printClass}.purchase-order-document .po-items-table th { font-size: 9.3px !important; line-height: 1.2 !important; padding: 4px 5px !important; }
body.${printClass}.purchase-order-document .po-items-table td { font-size: 10.2px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; }
body.${printClass}.purchase-order-document .total-line,
body.${printClass}.purchase-order-document .grand-total-row { font-size: 9.8px !important; line-height: 1.2 !important; }
body.${printClass}.transfer-document .total-items { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 700 !important; }
body.${printClass}.transfer-document .total-items .sum-lbl { font-weight: 700 !important; }
body.${printClass} .info-strip .info-row { display: grid !important; grid-template-columns: 28mm minmax(0, 1fr) !important; column-gap: 3mm !important; align-items: baseline !important; width: 100% !important; }
body.${printClass} .info-strip .info-row .lbl { min-width: 0 !important; width: auto !important; white-space: nowrap !important; }
body.${printClass} .info-strip .info-row > :last-child { min-width: 0 !important; text-align: right !important; overflow-wrap: anywhere !important; }
body.${printClass} .transfer-items-heading { border-bottom: 0 !important; margin-bottom: 0 !important; padding-bottom: 0 !important; }
body.${printClass}.transfer-document .transfer-items-heading + table { margin-top: 0 !important; }
${
    !isPortrait
        ? `
/* A5 landscape uses the Sale typography as the final shared scale for
   every receipt type. Only receipt data and table columns may differ. */
body.${printClass} { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.25 !important; }
body.${printClass} .company,
body.${printClass} .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.${printClass} .invoice-word,
body.${printClass} .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; }
body.${printClass} .doc-title,
body.${printClass} .title,
body.${printClass} .document-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body.${printClass} .invoice-no,
body.${printClass} .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; }
body.${printClass} .company-meta,
body.${printClass} .company-sub,
body.${printClass} .subtitle,
body.${printClass} .branch-detail,
body.${printClass} .header-address { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body.${printClass} .header-tax { font-size: 9px !important; font-weight: 600 !important; }
body.${printClass} .section,
body.${printClass} .section-title,
body.${printClass} .info-label,
body.${printClass} .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; }
body.${printClass} .line,
body.${printClass} .info-row,
body.${printClass} .sum-row,
body.${printClass} .pm-row { font-size: 10.5px !important; line-height: 1.25 !important; }
body.${printClass} table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.${printClass} table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body.${printClass} .total,
body.${printClass} .total-line,
body.${printClass} .grand-total,
body.${printClass} .grand-total-row,
body.${printClass} .balance-row { font-size: 10px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body.${printClass} .footer,
body.${printClass} .footer-bar,
body.${printClass} .footer-message,
body.${printClass} .print-footer,
body.${printClass} .receipt-footer { font-size: 9px !important; line-height: 1.25 !important; }
`
        : ''
}
${
    !isPortrait
        ? `
/* Payment Receipt and Receiving use supplier-document markup. Keep their
   browser A5 landscape header identical to the Sale header. */
body.${printClass}.supplier-document .header-row,
body.${printClass}.supplier-document .header { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; text-align: left !important; }
body.${printClass}.supplier-document .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; align-items: flex-start !important; gap: 4mm !important; text-align: left !important; }
body.${printClass}.supplier-document .header-right { display: block !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; min-width: 0 !important; padding-top: 0 !important; text-align: right !important; }
body.${printClass}.supplier-document .header-left .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body.${printClass}.supplier-document .header-left .logo,
body.${printClass}.supplier-document .header-left .logo-img { display: block !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
body.${printClass}.supplier-document .header-left .company-name,
body.${printClass}.supplier-document .header-left .invoice-word,
body.${printClass}.supplier-document .header-left .sale-invoice-title,
body.${printClass}.supplier-document .header-left .invoice-no { text-align: left !important; margin-left: 0 !important; }
body.${printClass}.supplier-document .header-right > .invoice-word,
body.${printClass}.supplier-document .header-right > .invoice-no,
body.${printClass}.supplier-document .header-right > .logo,
body.${printClass}.supplier-document .header-right > .logo-img,
body.${printClass}.supplier-document .header-right > .company-name { display: none !important; }
body.${printClass}.supplier-document .header-rule { display: block !important; width: 100% !important; border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; }
body.${printClass}.load-form-document { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; }
body.${printClass}.load-form-document .page { width: 100% !important; max-width: 100% !important; min-height: 0 !important; height: auto !important; }
`
        : ''
}
${
    isPortrait
        ? `
/* Browser A5 Portrait final frame: use all 148mm with only equal printable
   padding; override legacy 8mm/narrow receipt frames, including refunds. */
@page { size: 148mm 210mm !important; margin: 0 !important; }
html { width: 148mm !important; max-width: 148mm !important; margin: 0 !important; padding: 0 !important; }
body.${printClass} { width: 148mm !important; min-width: 148mm !important; max-width: 148mm !important; margin: 0 !important; padding: 5mm 0 0 !important; box-sizing: border-box !important; }
body.${printClass} .half,
body.${printClass} .document,
body.${printClass} .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body.${printClass} .page { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 3mm 6mm !important; box-sizing: border-box !important; }
body.${printClass} > * { padding-left: 6mm !important; padding-right: 6mm !important; }
body.${printClass} > .half,
body.${printClass} > .document { padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass} > * { width: calc(100% - 12mm) !important; max-width: calc(100% - 12mm) !important; margin-left: auto !important; margin-right: auto !important; padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass} > .half,
body.${printClass} > .document { width: 100% !important; max-width: 100% !important; }
body.${printClass} > .half > .page,
body.${printClass} > .document > .page,
body.${printClass} > .page { width: calc(100% - 12mm) !important; max-width: calc(100% - 12mm) !important; margin-left: auto !important; margin-right: auto !important; padding-left: 0 !important; padding-right: 0 !important; }
body.${printClass}.refund-document { padding-top: 8mm !important; }
body.${printClass}.refund-document > .document > .page { padding-top: 0 !important; }
body.${printClass} .header-row,
body.${printClass} .header-rule,
body.${printClass} .info-strip,
body.${printClass} table,
body.${printClass} .bottom-section,
body.${printClass} .footer,
body.${printClass} .footer-bar { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
/* PO A5 portrait uses the same 12mm side margins as Sale, instead of the
   older nested 20mm frame that made the PO content visibly narrower. */
body.${printClass}.purchase-order-document > .half > .page,
body.${printClass}.purchase-order-document > .document > .page,
body.${printClass}.purchase-order-document > .page {
    width: calc(100% - 12mm) !important;
    max-width: calc(100% - 12mm) !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: auto !important;
    margin-right: auto !important;
}
body.${printClass}.purchase-order-document .footer-bar {
    border-top: 0 !important;
    margin-top: 0 !important;
}
body.${printClass}.order-request-document .footer-bar {
    border-top: 0 !important;
    margin-top: 0 !important;
}
body.${printClass}.purchase-order-document .info-strip + .sep {
    display: none !important;
    height: 0 !important;
    margin: 0 !important;
    border: 0 !important;
}
body.${printClass}.refund-document.exchange-document > .document > .page,
body.${printClass}.refund-document.exchange-document > .page {
    width: calc(100% - 12mm) !important;
    max-width: calc(100% - 12mm) !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: auto !important;
    margin-right: auto !important;
}
body.${printClass}.refund-document.exchange-document .returned-items-table,
body.${printClass}.refund-document.exchange-document .new-items-table {
    display: table !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    table-layout: fixed !important;
}
/* Match Sale A5 portrait table typography in both browser preview and Agent
   output for return/exchange receipts. */
body.${printClass}.refund-document .items-table th,
body.${printClass}.refund-document .items-table.sale-items-table th {
    font-size: 9px !important;
    line-height: 1.15 !important;
    padding: 2px 3px !important;
    font-weight: 600 !important;
}
body.${printClass}.refund-document .items-table td,
body.${printClass}.refund-document .items-table.sale-items-table td {
    font-size: 9px !important;
    line-height: 1.2 !important;
    padding: 2px 3px !important;
    font-weight: 400 !important;
}
`
        : ''
}
</style>`;
        return addBrowserPrintClass(String(html), printClass, styles);
    }
    if (normalizedSize === '80MM') {
        return applySharedReceiptDesign(html, '80mm');
    }
    return html;
}

function addBrowserPrintClass(html, printClass, styles) {
    const withClass = String(html).replace(/<body(\s[^>]*)?>/i, (tag, attributes = '') => {
        if (/\bclass\s*=\s*["'][^"']*["']/i.test(attributes)) {
            return tag.replace(/class\s*=\s*(["'])([^"']*)\1/i, (match, quote, classes) => `class=${quote}${classes} ${printClass}${quote}`);
        }
        return tag.replace(/>$/, ` class="${printClass}">`);
    });
    return withClass.includes('</head>') ? withClass.replace('</head>', `${styles}</head>`) : `${styles}${withClass}`;
}

function applyA5ReceiptTheme(html, receiptSize) {
    const normalizedSize = String(receiptSize || '')
        .trim()
        .toUpperCase();
    if (!['A5', 'A5P'].includes(normalizedSize)) {
        return html;
    }

    const styles = `<style data-inventree-a5-receipt-theme>
body { background: #fff !important; color: #111827 !important; }
@page { margin: 5mm 0 0 !important; }
.top-bar { background: #f3f4f6 !important; border-bottom: 1px solid #d1d5db !important; }
hr.sep { display: none !important; }
.info-label, .pm-title, .section-title, .doc-title, .section { color: #111827 !important; }
.company-name, .company, .invoice-word, .title, .document-title, .doc-title, .section-title, .info-label, .pm-title, .section, .invoice-no, table th { font-weight: 800 !important; }
.invoice-no { text-align: right !important; font-weight: 800 !important; }
.logo:not(.refund-logo):not(.order-request-logo), .logo-img:not(.refund-logo):not(.gatepass-logo):not(.order-request-logo) { max-height: 14mm !important; max-width: 42mm !important; }
table thead tr, table thead th { background: #f3f4f6 !important; color: #172033 !important; border-color: #d1d5db !important; }
table thead th.gatepass-th { border-bottom: 1px solid #111827 !important; font-weight: 700 !important; }
thead { display: table-header-group !important; }
tr, .bottom-section, .summary-block, .footer, .footer-bar { break-inside: avoid !important; page-break-inside: avoid !important; }
.grand-total, .grand-total-row, .total-line { background: #f3f4f6 !important; color: #111827 !important; border-top: 1px solid #d1d5db !important; border-bottom: 1px solid #d1d5db !important; }
.footer-bar { border-top: 1px solid #d1d5db !important; }
.load-form-document .section,
.load-form-document .section-title,
.load-form-document table th,
.load-form-document .summary { font-weight: 800 !important; }
.load-form-document .delivery-head strong:first-child { font-size: 12px !important; font-weight: 800 !important; }
.load-form-document .summary { margin-bottom: 3mm !important; }
.order-request-document .footer-bar { border-top: 0 !important; }
body.order-request-document.print-a5 { padding: 6mm 8mm 5mm !important; }
body.order-request-document.print-a5 .page { padding: 0 !important; }
body.order-request-document.print-a5 .footer-bar,
body.order-request-document.print-a5 .footer-message { border-top: 0 !important; border-bottom: 0 !important; }
body.refund-document.print-a5 .section-title,
body.refund-document.print-a5 .info-label,
body.refund-document.print-a5 table th { font-weight: 600 !important; }
body.refund-document.print-a5 .footer { border-top: 0 !important; padding-top: 1mm !important; }
</style>`;

    return String(html).includes('</head>') ? String(html).replace('</head>', `${styles}</head>`) : `${styles}${html}`;
}

// The shared receipt helper intentionally skips documents that already carry
// its marker. Print Agent output still needs this module's final 80mm rules,
// so always append them once after the shared stylesheet has been applied.
function applyAgent80mmStyles(html) {
    // Compact scale first: the shared design and the final styles below are
    // both injected after it, and CSS breaks a specificity tie on document
    // order, so this stays the base layer and neither of them loses a rule.
    const sharedHtml = applySharedReceiptDesign(apply80mmItemSpacing(String(html)), '80mm');
    // The direct supplier-voucher templates carry their document class but do
    // not carry the physical-size class. Add it here so the 80mm geometry and
    // footer-flow rules are also applied in Print Agent output.
    const sizedHtml = sharedHtml.replace(/<body\b([^>]*)>/i, (match, attributes) => {
        if (/\bclass\s*=\s*["'][^"']*\bprint-80mm\b/i.test(attributes)) return match;
        if (/\bclass\s*=\s*["']/i.test(attributes)) {
            return match.replace(/(\bclass\s*=\s*["'])([^"']*)(["'])/i, '$1$2 print-80mm$3');
        }
        return `<body${attributes} class="print-80mm">`;
    });
    const marker = 'data-inventree-print-agent-80mm';
    if (sizedHtml.includes(marker)) return sizedHtml;
    const finalStyle = `<style ${marker}="true">${sale80mmFinalStyles}</style>`;
    return sizedHtml.includes('</head>') ? sizedHtml.replace('</head>', `${finalStyle}</head>`) : `${finalStyle}${sizedHtml}`;
}

function applyA5PrintGeometry(html, receiptSize) {
    const normalizedSize = String(receiptSize || '')
        .trim()
        .toUpperCase();
    if (!['A5', 'A5P'].includes(normalizedSize)) {
        return html;
    }

    const isPortrait = normalizedSize === 'A5P';
    const isOrderRequest = String(html).includes('order-request-document');
    const pageWidth = isPortrait ? '148mm' : '210mm';
    const pageHeight = isPortrait ? '210mm' : '148mm';
    const printClass = isPortrait ? 'print-a5p' : 'print-a5';
    const styles = `<style data-inventree-a5-print-geometry>
@page { size: ${pageWidth} ${pageHeight} !important; margin: 5mm 0 0 !important; }
html { width: ${pageWidth} !important; min-height: ${pageHeight} !important; height: auto !important; margin: 0 !important; padding: 0 !important; overflow: visible !important; }
body { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; height: auto !important; min-height: ${pageHeight} !important; max-height: none !important; margin: 0 !important; padding: 0 ${isPortrait ? '10mm' : '0'} !important; overflow: visible !important; box-sizing: border-box !important; }
body > .half,
body > .document,
body > .page,
body > .receipt-container,
body .half,
body .document,
body .page,
body .receipt-container { width: ${pageWidth} !important; max-width: ${pageWidth} !important; height: auto !important; min-height: 0 !important; max-height: none !important; margin-left: 0 !important; margin-right: 0 !important; overflow: visible !important; }
body > * { width: ${pageWidth} !important; max-width: ${pageWidth} !important; }
body .header-row,
body .header-rule,
body .info-strip,
body table,
body .bottom-section,
body .total-line,
body .footer,
body .footer-bar { width: 100% !important; max-width: 100% !important; }
body.print-a5p .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
body,
body > *,
body .half,
body .page,
body .document,
body .receipt-container { width: ${pageWidth} !important; max-width: ${pageWidth} !important; }
body .page { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 3mm ${isPortrait ? '10mm' : '6mm'} !important; box-sizing: border-box !important; }
body .header-left .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body .header-left .invoice-word,
body .header-left .invoice-no { display: block !important; width: 100% !important; margin-left: 0 !important; text-align: left !important; }
body.sale-pos-receipt .header-left { display: grid !important; grid-template-columns: auto minmax(0, 1fr) !important; grid-template-rows: auto auto auto !important; align-items: start !important; column-gap: 3mm !important; }
body.sale-pos-receipt .header-left .logo-img { grid-column: 1 !important; grid-row: 1 / span 3 !important; }
body.sale-pos-receipt .header-left .company-name { grid-column: 2 !important; grid-row: 1 !important; }
body.sale-pos-receipt .header-left .invoice-word { grid-column: 2 !important; grid-row: 2 !important; display: block !important; }
body.sale-pos-receipt .header-left .invoice-no { grid-column: 2 !important; grid-row: 3 !important; display: block !important; }
body.sale-pos-receipt .header-right > .invoice-word,
body.sale-pos-receipt .header-right > .invoice-no,
body.sale-pos-receipt .header-right > .logo-img,
body.sale-pos-receipt .header-right > .company-name { display: none !important; }
body.sale-pos-receipt .header-right .company-sub { font-size: 8px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body.load-form-document,
body.load-form-document .page { height: auto !important; max-height: none !important; overflow: visible !important; }
body table,
body .header-row,
body .info-strip,
body .bottom-section,
body .footer,
body .footer-bar { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }
body .header-row { margin-bottom: 1mm !important; }
body .header-rule { margin: 1mm 0 !important; }
body { color: #111827 !important; }
body * { color: #111827 !important; }
body .info-row,
body .line,
body .sum-row,
body table td { color: #111827 !important; font-weight: 500 !important; }
body table th,
body .info-label,
body .section-title,
body .grand-total,
body .grand-total-row { color: #111827 !important; font-weight: 700 !important; }
body table th { border-left: 0 !important; border-right: 0 !important; }
/* Shared A5 portrait Agent profile: every document uses one typography and
   spacing scale, while document-specific columns and content remain intact. */
body { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.25 !important; color: #111827 !important; }
body .page { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 3mm 5mm !important; box-sizing: border-box !important; }
body .header, body .brand, body .print-header { width: 100% !important; margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; }
body .header-row, body .brand-row { width: 100% !important; margin: 0 0 1.5mm !important; gap: 5mm !important; }
body .company, body .company-name { font-size: 16px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body .invoice-word, body .sale-invoice-title, body .doc-title, body .title, body .document-title { font-size: 13px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body .invoice-no, body .number { font-size: 9px !important; line-height: 1.15 !important; }
body .company-meta, body .company-sub, body .branch-detail, body .header-address, body .header-tax { font-size: 8px !important; line-height: 1.2 !important; }
body .section, body .section-title, body .info-label, body .pm-title { font-size: 9px !important; line-height: 1.15 !important; font-weight: 600 !important; letter-spacing: .3px !important; margin: 1.5mm 0 1mm !important; }
body .info-row, body .line, body .pm-row, body .sum-row { font-size: 9.5px !important; line-height: 1.2 !important; padding: 1px 0 !important; }
body table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin: 0 0 2mm !important; }
body table th { font-size: 8.5px !important; line-height: 1.15 !important; padding: 2px 3px !important; font-weight: 600 !important; }
body table td { font-size: 9px !important; line-height: 1.2 !important; padding: 2px 3px !important; font-weight: 400 !important; }
body .total, body .total-line, body .grand-total, body .grand-total-row { font-size: 9.5px !important; line-height: 1.2 !important; padding: 2px 3px !important; font-weight: 600 !important; }
body .summary-block, body .bottom-section { margin-top: 1mm !important; }
body .footer, body .footer-bar, body .footer-message, body .print-footer, body .receipt-footer { font-size: 8px !important; line-height: 1.25 !important; margin-top: 2mm !important; padding-top: 1mm !important; }
/* A5 Agent cleanup for Sale and Gate Pass headers. */
body.sale-document .top-bar,
body.sale-pos-receipt .top-bar,
body.gatepass-document .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
/* Exchange sections keep one divider: the table header supplies the line. */
body.exchange-document .section-title { border-bottom: 0 !important; font-weight: 600 !important; font-size: 9px !important; padding-bottom: 0 !important; }
body.exchange-document .doc-title,
body.exchange-document .sale-invoice-title { font-weight: 600 !important; }
/* Final specificity layer: prevent PO, Exchange, Gate Pass, Transfer,
   Receiving, and Payment document rules from changing the shared A5 scale. */
body[class] { font-size: 10px !important; line-height: 1.25 !important; }
body[class] .company, body[class] .company-name { font-size: 16px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body[class] .invoice-word, body[class] .sale-invoice-title, body[class] .doc-title, body[class] .title, body[class] .document-title { font-size: 13px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body[class] .invoice-no, body[class] .number { font-size: 9px !important; line-height: 1.15 !important; }
body[class] .company-meta, body[class] .company-sub, body[class] .branch-detail, body[class] .header-address, body[class] .header-tax { font-size: 8px !important; line-height: 1.2 !important; }
body[class] .section, body[class] .section-title, body[class] .info-label, body[class] .pm-title { font-size: 9px !important; line-height: 1.15 !important; font-weight: 600 !important; }
body[class] .info-row, body[class] .line, body[class] .pm-row, body[class] .sum-row { font-size: 9.5px !important; line-height: 1.2 !important; }
body[class] table th { font-size: 8.5px !important; line-height: 1.15 !important; font-weight: 600 !important; }
body[class] table td { font-size: 9px !important; line-height: 1.2 !important; font-weight: 400 !important; }
body[class] .total, body[class] .total-line, body[class] .grand-total, body[class] .grand-total-row { font-size: 9.5px !important; line-height: 1.2 !important; font-weight: 600 !important; }
body[class] .footer, body[class] .footer-bar, body[class] .footer-message, body[class] .print-footer, body[class] .receipt-footer { font-size: 8px !important; line-height: 1.25 !important; }
body[class].supplier-document .section-title,
body[class].supplier-document .info-label,
body[class].supplier-document .pm-title,
body[class].supplier-document table th { font-weight: 600 !important; }
body[class].supplier-document .footer,
body[class].supplier-document .footer-bar { border-top: 0 !important; margin-top: 0 !important; padding-top: 1mm !important; }
body[class].supplier-document .payment-footer-separator,
body[class].supplier-document .receiving-footer-separator { display: block !important; width: 100% !important; height: 0 !important; border: 0 !important; border-top: 1px solid #000 !important; margin: 1.5mm 0 0 !important; }
/* One shared A5 Agent header profile. */
body[class] .header-row, body[class] .brand-row, body[class] .print-header { display: flex !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 5mm !important; }
body[class] .header-left, body[class] .brand-left { flex: 1 1 auto !important; min-width: 0 !important; }
body[class] .header-right, body[class] .brand-right { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: right !important; }
body[class] .logo, body[class] .logo-img { width: auto !important; max-width: 27mm !important; max-height: 12mm !important; object-fit: contain !important; }
body[class] .top-bar { display: none !important; height: 0 !important; border: 0 !important; margin: 0 !important; padding: 0 !important; }
body.exchange-document .header-rule { display: block !important; height: 0 !important; border: 0 !important; border-top: 1px dashed #111827 !important; margin: 1mm 0 2mm !important; padding: 0 !important; }
body.exchange-document .header-left .company-name { font-size: 16px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body.exchange-document .header-left .invoice-word,
body.exchange-document .header-left .sale-invoice-title { font-size: 13px !important; line-height: 1.1 !important; font-weight: 600 !important; }
body.exchange-document .header-left .invoice-no { font-size: 9px !important; line-height: 1.15 !important; font-weight: 400 !important; }
body.exchange-document .header-left .logo,
body.exchange-document .header-left .logo-img { max-width: 27mm !important; max-height: 12mm !important; margin: 0 !important; align-self: flex-start !important; }
body.exchange-document .header-left,
body.exchange-document .header-left .brand-details { text-align: left !important; justify-content: flex-start !important; }
body.exchange-document .header-row,
body.exchange-document .header-left,
body.exchange-document .brand-details { margin-left: 0 !important; padding-left: 0 !important; text-indent: 0 !important; }
body.exchange-document .header-left .company-name,
body.exchange-document .header-left .invoice-word,
body.exchange-document .header-left .sale-invoice-title,
body.exchange-document .header-left .invoice-no { text-align: left !important; }
body.exchange-document .items-table tbody td { border-bottom: 0 !important; }
body.exchange-document .items-table { margin-bottom: 1mm !important; }
body.exchange-document .footer { border-top: 1px solid #111827 !important; padding-top: 1.5mm !important; margin-top: 2mm !important; }
/* Final shared A5 frame: match the browser preview and the Sale/A4 gutters. */
@page { size: ${pageWidth} ${pageHeight} !important; margin: 5mm 0 0 !important; }
html { width: ${pageWidth} !important; max-width: ${pageWidth} !important; margin: 0 !important; padding: 0 !important; }
body { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; margin: 0 !important; padding: 5mm 6mm !important; box-sizing: border-box !important; }
body > *,
body .half,
body .page,
body .document,
body .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin-left: 0 !important; margin-right: 0 !important; padding-left: 0 !important; padding-right: 0 !important; box-sizing: border-box !important; }
body .company,
body .company-name { font-size: 15px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body .invoice-word,
body .sale-invoice-title,
body .doc-title,
body .title,
body .document-title { font-size: 12px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body .section,
body .section-title,
body .info-label,
body .pm-title { font-size: 8.5px !important; line-height: 1.15 !important; font-weight: 700 !important; }
body table th { font-weight: 700 !important; }
/* Final Sale-sized A5 text for Transfer, Gate Pass, and Order Request. */
body.transfer-document .section,
body.transfer-document .section-title,
body.transfer-document .info-label,
body.gatepass-document .section,
body.gatepass-document .section-title,
body.gatepass-document .info-label,
body.order-request-document .section,
body.order-request-document .section-title,
body.order-request-document .info-label { font-size: 9.5px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body.transfer-document .line,
body.transfer-document .info-row,
body.transfer-document table td,
body.gatepass-document .line,
body.gatepass-document .info-row,
body.gatepass-document table td,
body.order-request-document .line,
body.order-request-document .info-row,
body.order-request-document table td { font-size: 10.5px !important; line-height: 1.25 !important; }
body.transfer-document table th,
body.gatepass-document table th,
body.order-request-document table th { font-size: 9.5px !important; line-height: 1.2 !important; }
body.exchange-document .doc-title,
body.exchange-document .sale-invoice-title,
body.exchange-document .section-title,
body.exchange-document .info-label,
body.exchange-document .pm-title { font-weight: 600 !important; }
/* Last A5 Agent pass: PO inherits the exact Sale header/table scale. */
body.purchase-order-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; }
body.purchase-order-document .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; max-width: none !important; min-width: 0 !important; align-items: flex-start !important; gap: 3mm !important; text-align: left !important; }
body.purchase-order-document .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; min-width: 0 !important; text-align: right !important; }
body.purchase-order-document .header-right > .invoice-word,
body.purchase-order-document .header-right > .invoice-no,
body.purchase-order-document .header-right > .logo-img,
body.purchase-order-document .header-right > .company-name { display: none !important; }
body.purchase-order-document .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body.purchase-order-document .header-left .logo-img { max-width: 27mm !important; max-height: 12mm !important; margin: 0 !important; }
body.purchase-order-document .info-label,
body.purchase-order-document .section-title { font-size: 8.5px !important; line-height: 1.15 !important; font-weight: 700 !important; }
body.purchase-order-document .line,
body.purchase-order-document .info-row,
body.purchase-order-document .sum-row { font-size: 9.8px !important; line-height: 1.25 !important; }
body.purchase-order-document .po-items-table th { font-size: 9.3px !important; padding: 4px 5px !important; }
body.purchase-order-document .po-items-table td { font-size: 10.2px !important; padding: 2.6px 5px !important; }
/* Compact PO A5 landscape content so the summary stays on the same sheet. */
body.purchase-order-document .page { padding: 3mm 6mm !important; }
body.purchase-order-document .header-row { margin-bottom: 1mm !important; }
body.purchase-order-document .po-header-separator { margin-bottom: 1.5mm !important; }
body.purchase-order-document .info-strip { margin-bottom: 1.5mm !important; gap: 6mm !important; }
body.purchase-order-document .info-label,
body.purchase-order-document .section-title { margin-bottom: 1mm !important; }
body.purchase-order-document .info-row,
body.purchase-order-document .line,
body.purchase-order-document .sum-row { padding: 0 !important; margin: 0 !important; line-height: 1.1 !important; }
body.purchase-order-document table { margin-bottom: 1mm !important; }
body.purchase-order-document .po-items-table th { padding: 2px 3px !important; }
body.purchase-order-document .po-items-table td { padding: 1.5px 3px !important; line-height: 1.1 !important; }
body.purchase-order-document .bottom-section { margin-top: 0 !important; break-inside: avoid !important; page-break-inside: avoid !important; }
body.purchase-order-document .summary-block { width: 92mm !important; }
body.purchase-order-document .grand-total-row,
body.purchase-order-document .balance-row { padding: 3px 4px !important; }
body.purchase-order-document .footer-bar { margin-top: 1mm !important; padding-top: .5mm !important; }
/* One Sale reference typography scale for every A5 receipt. */
body[class] { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.25 !important; color: #000 !important; }
body[class] .company,
body[class] .company-name { font-size: 15px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body[class] .invoice-word,
body[class] .sale-invoice-title,
body[class] .doc-title,
body[class] .title,
body[class] .document-title { font-size: 12px !important; line-height: 1.1 !important; font-weight: 700 !important; }
body[class] .invoice-no,
body[class] .number { font-size: 9px !important; line-height: 1.15 !important; font-weight: 500 !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .branch-detail,
body[class] .header-address,
body[class] .header-tax { font-size: 8px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { font-size: 8.5px !important; line-height: 1.15 !important; font-weight: 700 !important; }
body[class] .info-row,
body[class] .line,
body[class] .sum-row,
body[class] .pm-row { font-size: 9.8px !important; line-height: 1.25 !important; }
body[class] table th { font-size: 9.3px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body[class] table td { font-size: 10.2px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body[class] .total,
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row,
body[class] .balance-row { font-size: 9.8px !important; line-height: 1.2 !important; }
body[class] .footer,
body[class] .footer-bar,
body[class] .footer-message,
body[class] .print-footer,
body[class] .receipt-footer { font-size: 8px !important; line-height: 1.25 !important; }
body.exchange-document .company-name,
body.exchange-document .invoice-word,
body.exchange-document .sale-invoice-title,
body.exchange-document .doc-title,
body.exchange-document .section-title,
body.exchange-document .info-label,
body.exchange-document .pm-title,
body.exchange-document .section,
body.exchange-document table th,
body.exchange-document .total-line,
body.exchange-document .grand-total,
body.exchange-document .grand-total-row { font-weight: 700 !important; }
/* Canonical A5 Payment profile. This is the single visual source of truth
   for every A5 document; receipt classes only provide content. */
body[class] {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10px !important;
    line-height: 1.3 !important;
    color: #000 !important;
    background: #fff !important;
    overflow-wrap: anywhere !important;
    padding: 2.5mm 6.5mm !important;
    box-sizing: border-box !important;
}
body[class] .page,
body[class] > .document,
body[class] > .receipt-container { padding: 0 !important; box-sizing: border-box !important; }
body[class] .page { padding: 2.5mm 6.5mm !important; box-sizing: border-box !important; }
body[class] .header,
body[class] .brand,
body[class] .print-header { margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; }
body[class] .header-row,
body[class] .brand-row { margin-bottom: 2mm !important; gap: 6mm !important; }
body[class] .header-left { align-items: center !important; gap: 4mm !important; }
body[class] .header-left .logo,
body[class] .header-left .logo-img,
body[class] .logo,
body[class] .logo-img { max-width: 26mm !important; max-height: 14mm !important; object-fit: contain !important; }
body[class] .company,
body[class] .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .subtitle,
body[class] .branch-detail,
body[class] .header-address { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body[class] .header-tax { font-size: 9px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body[class] .invoice-word,
body[class] .title,
body[class] .document-title,
body[class] .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 400 !important; letter-spacing: 0 !important; text-transform: none !important; }
body[class] .doc-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; letter-spacing: 1px !important; text-transform: uppercase !important; }
body[class] .invoice-no,
body[class] .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 400 !important; letter-spacing: .5px !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; letter-spacing: 1px !important; text-transform: uppercase !important; background: transparent !important; }
body[class] .info-name,
body[class] .customer-name { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .line,
body[class] .info-row,
body[class] .pm-row,
body[class] .sum-row { font-size: 12px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; }
body[class] table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; border-collapse: collapse !important; margin-bottom: 3mm !important; }
body[class] table thead,
body[class] table thead tr,
body[class] table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; }
body[class] table th { font-size: 12px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 600 !important; overflow-wrap: anywhere !important; }
body[class] table td { font-size: 12px !important; line-height: 1.25 !important; padding: 4px 5px !important; font-weight: 500 !important; color: #000 !important; overflow-wrap: anywhere !important; word-break: break-word !important; }
body[class] .total,
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row,
body[class] .balance-row { font-size: 15px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
body[class] .footer,
body[class] .footer-bar,
body[class] .footer-message,
body[class] .print-footer,
body[class] .receipt-footer { font-size: 11px !important; line-height: 1.25 !important; }
body[class] .header-rule { margin-bottom: 3mm !important; }
body[class] .info-strip { gap: 8mm !important; margin-bottom: 3mm !important; }
body[class] .bottom-section { margin-top: 1mm !important; }
/* Exchange and PO use the same aligned Sale header in both A5 orientations. */
body[class].exchange-document .header-row,
body[class].purchase-order-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; }
body[class].exchange-document .header-left,
body[class].purchase-order-document .header-left { display: flex !important; flex: 1 1 auto !important; align-items: flex-start !important; gap: 4mm !important; min-width: 0 !important; text-align: left !important; }
body[class].exchange-document .header-right,
body[class].purchase-order-document .header-right { flex: 0 0 38% !important; width: 38% !important; min-width: 0 !important; text-align: right !important; padding-top: 0 !important; }
body[class].exchange-document .header-left .brand-details,
body[class].purchase-order-document .header-left .brand-details { min-width: 0 !important; text-align: left !important; }
body[class].exchange-document .header-left .company-name,
body[class].exchange-document .header-left .invoice-word,
body[class].exchange-document .header-left .sale-invoice-title,
body[class].exchange-document .header-left .invoice-no,
body[class].purchase-order-document .header-left .company-name,
body[class].purchase-order-document .header-left .invoice-word,
body[class].purchase-order-document .header-left .sale-invoice-title,
body[class].purchase-order-document .header-left .invoice-no { text-align: left !important; margin-left: 0 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { background: transparent !important; }
/* Sale-style detail alignment for Transfer: fixed label column, flexible
   value column, and right-aligned values in both A5 orientations. */
body[class].transfer-document .info-strip .info-row { display: grid !important; grid-template-columns: 28mm minmax(0, 1fr) !important; column-gap: 3mm !important; align-items: baseline !important; width: 100% !important; }
body[class].transfer-document .info-strip .info-row .lbl { min-width: 0 !important; width: auto !important; white-space: nowrap !important; }
body[class].transfer-document .info-strip .info-row > :last-child { min-width: 0 !important; text-align: right !important; overflow-wrap: anywhere !important; }
/* Exchange uses the lighter Sale label weight; values remain regular. */
body[class].exchange-document .section-title,
body[class].exchange-document .info-label,
body[class].exchange-document .pm-title { font-weight: 600 !important; }
body[class].exchange-document .line > span:first-child { font-weight: 400 !important; }
${
    isPortrait
        ? `
/* A5 portrait uses the complete 148mm sheet. The printable content is the
   full inner width after equal Sale-style side padding; no nested 132mm
   frame or extra 8mm margins are allowed. */
html, body { width: 148mm !important; min-width: 148mm !important; max-width: 148mm !important; box-sizing: border-box !important; }
body[class] { width: 148mm !important; min-width: 148mm !important; max-width: 148mm !important; padding: 0 !important; margin: 0 !important; box-sizing: border-box !important; }
body[class] > .half,
body[class] > .document,
body[class] > .page,
body[class] > .receipt-container,
body[class] .half,
body[class] .document,
body[class] .receipt-container { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body[class] .page { width: 100% !important; max-width: 100% !important; min-width: 0 !important; margin: 0 !important; padding: 2.5mm 6.5mm !important; box-sizing: border-box !important; }
body[class].refund-document .line,
body[class].refund-document .line > :first-child,
body[class].refund-document .line > :last-child { font-weight: 400 !important; }
body[class] .header-row,
body[class] .header-rule,
body[class] .info-strip,
body[class] table,
body[class] .bottom-section,
body[class] .footer,
body[class] .footer-bar { width: 100% !important; max-width: 100% !important; min-width: 0 !important; box-sizing: border-box !important; }
`
        : ''
}
${
    !isPortrait
        ? `
/* Final A5 landscape Sale reference profile. All paper receipts use this
   exact Sale scale; only their data and column content differ. */
body[class] { font-size: 11.5px !important; line-height: 1.4 !important; }
body[class] .page { padding: 2.5mm 6.5mm !important; }
body[class] .header-row { margin-bottom: 2mm !important; gap: 6mm !important; }
body[class] .header-left { align-items: center !important; gap: 4mm !important; }
body[class] .header-left .logo,
body[class] .header-left .logo-img,
body[class] .logo,
body[class] .logo-img { max-width: 26mm !important; max-height: 14mm !important; }
body[class] .company,
body[class] .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body[class] .invoice-word,
body[class] .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: 0 !important; text-transform: none !important; }
body[class] .doc-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; letter-spacing: 1px !important; text-transform: uppercase !important; }
body[class] .invoice-no,
body[class] .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .subtitle,
body[class] .branch-detail,
body[class] .header-address { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body[class] .header-tax { font-size: 9px !important; font-weight: 600 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; text-transform: uppercase !important; background: transparent !important; }
body[class] .info-name,
body[class] .customer-name { font-size: 10px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .line,
body[class] .info-row,
body[class] .pm-row,
body[class] .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; }
body[class] table { margin-bottom: 3mm !important; }
body[class] table thead,
body[class] table thead tr,
body[class] table thead th { background: transparent !important; background-color: transparent !important; color: #000 !important; }
body[class] table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body[class] table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body[class] .total-items { font-size: 8.5px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .total,
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; background: transparent !important; background-color: transparent !important; }
body[class] .balance,
body[class] .balance-row { font-size: 7.5px !important; line-height: 1.2 !important; padding: 3px 5px !important; }
body[class] .footer,
body[class] .footer-bar,
body[class] .footer-message,
body[class] .print-footer,
body[class] .receipt-footer { font-size: 9px !important; line-height: 1.25 !important; }
body[class].transfer-document .info-strip .info-row { grid-template-columns: 28mm minmax(0, 1fr) !important; }
body[class].exchange-document .section-title,
body[class].exchange-document .info-label,
body[class].exchange-document .pm-title { font-weight: 600 !important; }
body[class].purchase-order-document .header-right > .invoice-word,
body[class].purchase-order-document .header-right > .invoice-no,
body[class].purchase-order-document .header-right > .logo-img,
body[class].purchase-order-document .header-right > .company-name { display: none !important; }
body[class].transfer-document .total-items { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 700 !important; }
body[class].transfer-document .total-items .sum-lbl { font-weight: 700 !important; }
body[class] .info-strip .info-row { display: grid !important; grid-template-columns: 28mm minmax(0, 1fr) !important; column-gap: 3mm !important; align-items: baseline !important; width: 100% !important; }
body[class] .info-strip .info-row .lbl { min-width: 0 !important; width: auto !important; white-space: nowrap !important; }
body[class] .info-strip .info-row > :last-child { min-width: 0 !important; text-align: right !important; overflow-wrap: anywhere !important; }
body[class].transfer-document .transfer-items-heading { border-bottom: 0 !important; margin-bottom: 0 !important; padding-bottom: 0 !important; }
body[class].transfer-document .transfer-items-heading + table { margin-top: 0 !important; }
/* Payment Receipt and Receiving use supplier-document markup. Their A5
   landscape header must use the same Sale header contract. */
body[class].supplier-document .header-row,
body[class].supplier-document .header { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 2mm !important; padding: 0 0 1.5mm !important; text-align: left !important; }
body[class].supplier-document .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; align-items: flex-start !important; gap: 4mm !important; text-align: left !important; }
body[class].supplier-document .header-right { display: block !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; min-width: 0 !important; padding-top: 0 !important; text-align: right !important; }
body[class].supplier-document .header-left .brand-details { display: block !important; flex: 1 1 auto !important; min-width: 0 !important; text-align: left !important; }
body[class].supplier-document .header-left .logo,
body[class].supplier-document .header-left .logo-img { display: block !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
body[class].supplier-document .header-left .company-name,
body[class].supplier-document .header-left .invoice-word,
body[class].supplier-document .header-left .sale-invoice-title,
body[class].supplier-document .header-left .invoice-no { text-align: left !important; margin-left: 0 !important; }
body[class].supplier-document .header-right > .invoice-word,
body[class].supplier-document .header-right > .invoice-no,
body[class].supplier-document .header-right > .logo,
body[class].supplier-document .header-right > .logo-img,
body[class].supplier-document .header-right > .company-name { display: none !important; }
body[class].supplier-document .header-rule { display: block !important; width: 100% !important; border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; }
body[class].load-form-document { width: 210mm !important; min-width: 210mm !important; max-width: 210mm !important; }
body[class].load-form-document .page { width: 100% !important; max-width: 100% !important; min-height: 0 !important; height: auto !important; }
/* Order Request and Gate Pass use the same A5 landscape header contract as
   Sale. Keep these selectors explicit because their document styles are
   appended independently and can otherwise win on the Print Agent side. */
body[class].order-request-document .header-row,
body[class].gatepass-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin-bottom: 2mm !important; }
body[class].order-request-document .header-left,
body[class].gatepass-document .header-left { display: flex !important; flex: 1 1 auto !important; align-items: center !important; gap: 4mm !important; min-width: 0 !important; text-align: left !important; }
body[class].order-request-document .brand-details,
body[class].gatepass-document .brand-details { display: block !important; min-width: 0 !important; text-align: left !important; }
body[class].order-request-document .header-right,
body[class].gatepass-document .header-right { display: block !important; flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; min-width: 0 !important; padding-top: 0 !important; text-align: right !important; }
body[class].order-request-document .header-left .logo-img,
body[class].gatepass-document .header-left .logo-img { display: block !important; max-width: 26mm !important; max-height: 14mm !important; margin: 0 !important; object-fit: contain !important; }
body[class].order-request-document .header-left .company-name,
body[class].gatepass-document .header-left .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; text-align: left !important; }
body[class].order-request-document .header-left .invoice-word,
body[class].order-request-document .header-left .sale-invoice-title,
body[class].gatepass-document .header-left .invoice-word,
body[class].gatepass-document .header-left .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: 0 !important; text-transform: none !important; text-align: left !important; margin-top: 3px !important; }
body[class].order-request-document .header-left .invoice-no,
body[class].gatepass-document .header-left .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; text-align: left !important; }
body[class].order-request-document .header-rule,
body[class].gatepass-document .header-rule { display: block !important; width: 100% !important; height: 0 !important; border: 0 !important; border-top: 1px dashed #000 !important; margin: 0 0 3mm !important; padding: 0 !important; }
/* Keep Agent A5 landscape output on the same final Sale scale as browser
   preview for every receipt type. */
body[class] { font-family: Arial, Helvetica, sans-serif !important; font-size: 10px !important; line-height: 1.25 !important; }
body[class] .company,
body[class] .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body[class] .invoice-word,
body[class] .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; }
body.print-a5.supplier-document .header-left { align-items: center !important; text-align: left !important; }
body.print-a5.supplier-document .header-left .company-name,
body.print-a5.supplier-document .header-left .invoice-word,
body.print-a5.supplier-document .header-left .sale-invoice-title { font-weight: 700 !important; text-align: left !important; }
body.print-a5.supplier-document .header-right { flex: 0 0 38% !important; width: 38% !important; max-width: 38% !important; text-align: right !important; }
body[class] .doc-title,
body[class] .title,
body[class] .document-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .invoice-no,
body[class] .number { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; }
body[class] .company-meta,
body[class] .company-sub,
body[class] .subtitle,
body[class] .branch-detail,
body[class] .header-address { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body[class] .header-tax { font-size: 9px !important; font-weight: 600 !important; }
body[class] .section,
body[class] .section-title,
body[class] .info-label,
body[class] .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; }
body[class] .line,
body[class] .info-row,
body[class] .sum-row,
body[class] .pm-row { font-size: 10.5px !important; line-height: 1.25 !important; }
body[class] table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body[class] table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body[class] .total,
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row,
body[class] .balance-row { font-size: 10px !important; line-height: 1.2 !important; font-weight: 700 !important; }
body[class] .footer,
body[class] .footer-bar,
body[class] .footer-message,
body[class] .print-footer,
body[class] .receipt-footer { font-size: 9px !important; line-height: 1.25 !important; }
body[class] table thead,
body[class] table thead tr,
body[class] table thead th,
body[class] .total,
body[class] .total-line,
body[class] .grand-total,
body[class] .grand-total-row,
body[class] .balance,
body[class] .balance-row { background: transparent !important; background-color: transparent !important; }
`
        : ''
}
/* Exchange A5: keep the empty state, reason, and value summary compact and
   readable in the Print Agent renderer. */
body[class].exchange-document table.items-table td[colspan],
body[class].exchange-document table.sale-items-table td[colspan],
body[class].exchange-document table.items-table td.col-total,
body[class].exchange-document table.sale-items-table td.col-total { font-weight: 700 !important; }
body[class].exchange-document .reason-row { display: grid !important; grid-template-columns: 24mm minmax(0, 1fr) !important; gap: 3mm !important; align-items: baseline !important; margin: 0 !important; padding: 0 !important; line-height: 1.15 !important; }
body[class].exchange-document .reason-row span:last-child { min-width: 0 !important; text-align: right !important; white-space: normal !important; overflow-wrap: anywhere !important; }
body[class].exchange-document .line { margin: 0 !important; padding: .5px 0 !important; line-height: 1.15 !important; }
body[class].exchange-document .total-line { margin: 1mm 0 !important; padding: 3px 5px !important; line-height: 1.15 !important; }
body[class][class] .company-name,
body[class][class] .company { font-size: 17px !important; line-height: 1.05 !important; }
/* A5/A5P sale and purchase summaries: emphasize tax details and item totals. */
body[class].sale-document .header-tax,
body[class].sale-document .tax-details-row,
body[class].sale-document .tax-details-row *,
body[class].purchase-order-document .header-tax,
body[class].purchase-order-document .tax-details-row,
body[class].purchase-order-document .tax-details-row * { font-weight: 800 !important; }
body[class].sale-document .total-items,
body[class].sale-document .total-items * { font-size: 14px !important; line-height: 1.25 !important; font-weight: 800 !important; }
body.print-a5p.sale-document .total-items,
body.print-a5p.sale-document .total-items * { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5.sale-document .total-items,
body.print-a5.sale-document .total-items * { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body[class].purchase-order-document .total-items-row,
body[class].purchase-order-document .total-items-row * { font-size: 12px !important; line-height: 1.25 !important; font-weight: 800 !important; }
body.${printClass}.purchase-order-document .total-items-row,
body.${printClass}.purchase-order-document .total-items-row * { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.${printClass}.purchase-order-document .footer-bar { border-top: 0 !important; margin-top: 0 !important; }
body.${printClass}.order-request-document .footer-bar { border-top: 0 !important; margin-top: 0 !important; }
body.print-a5.refund-document.exchange-document .footer {
    border-top: 1px solid #000 !important;
    margin-top: 1mm !important;
    padding-top: 1mm !important;
}
body > * { width: calc(${pageWidth} - ${isPortrait ? '20mm' : '12mm'}) !important; max-width: calc(${pageWidth} - ${isPortrait ? '20mm' : '12mm'}) !important; margin-left: auto !important; margin-right: auto !important; padding-left: 0 !important; padding-right: 0 !important; }
body > .half,
body > .document { width: ${pageWidth} !important; max-width: ${pageWidth} !important; }
body > .half > .page,
body > .document > .page,
body > .page { width: calc(${pageWidth} - ${isPortrait ? '20mm' : '12mm'}) !important; max-width: calc(${pageWidth} - ${isPortrait ? '20mm' : '12mm'}) !important; margin-left: auto !important; margin-right: auto !important; padding-left: 0 !important; padding-right: 0 !important; }
@page { size: ${pageWidth} ${pageHeight} !important; margin: 5mm 0 0 !important; }
body.load-form-document { width: ${pageWidth} !important; min-width: ${pageWidth} !important; max-width: ${pageWidth} !important; }
/* Final A5 Sale contract for PO, Return/Refund, and Order Request output. */
body.${printClass}.purchase-order-document,
body.${printClass}.refund-document,
body.${printClass}.order-request-document,
body.${printClass}.load-form-document {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    line-height: 1.4 !important;
    color: #000 !important;
    background: #fff !important;
    padding: ${isPortrait ? '3mm 10mm' : '3mm 6mm'} !important;
}
body.${printClass}.purchase-order-document .page,
body.${printClass}.refund-document .page,
body.${printClass}.order-request-document .page { padding: ${isPortrait ? '3mm 10mm' : '3mm 6mm'} !important; }
/* The inset belongs to .page, so the body carries none: a template that
   pads its body as well (the refund and sale sheets do, for the browser
   fallback) would push the fixed-width page past the paper's edge. Five
   [class] tokens outrank any template rule; :has() keeps the reset to the
   documents that actually have a .page to carry the inset. */
body.${printClass}[class][class][class][class]:has(.page) { padding: 0 !important; }
body.${printClass}.purchase-order-document .header-row,
body.${printClass}.refund-document .header-row,
body.${printClass}.order-request-document .header-row { gap: 6mm !important; margin-bottom: 1.5mm !important; }
body.${printClass}.purchase-order-document .header-right,
body.${printClass}.refund-document .header-right,
body.${printClass}.order-request-document .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; padding-top: 2px !important; text-align: right !important; }
body.${printClass}.purchase-order-document .header-left,
body.${printClass}.refund-document .header-left,
body.${printClass}.order-request-document .header-left { gap: 3mm !important; }
body.${printClass}.purchase-order-document .logo-img,
body.${printClass}.refund-document .logo,
body.${printClass}.refund-document .refund-logo,
body.${printClass}.order-request-document .logo { max-width: 27mm !important; max-height: 12mm !important; }
body.${printClass}.purchase-order-document .company-name,
body.${printClass}.refund-document .company-name,
body.${printClass}.order-request-document .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass}.purchase-order-document .invoice-word,
body.${printClass}.purchase-order-document .sale-invoice-title,
body.${printClass}.refund-document .invoice-word,
body.${printClass}.refund-document .sale-invoice-title,
body.${printClass}.order-request-document .invoice-word,
body.${printClass}.order-request-document .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.${printClass}.purchase-order-document .invoice-no,
body.${printClass}.refund-document .invoice-no,
body.${printClass}.order-request-document .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.${printClass}.purchase-order-document .info-label,
body.${printClass}.purchase-order-document .section-title,
body.${printClass}.purchase-order-document .pm-title,
body.${printClass}.refund-document .info-label,
body.${printClass}.refund-document .section-title,
body.${printClass}.refund-document .pm-title,
body.${printClass}.order-request-document .info-label,
body.${printClass}.order-request-document .section-title,
body.${printClass}.order-request-document .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; border-bottom: 0 !important; }
body.${printClass}.purchase-order-document .line,
body.${printClass}.purchase-order-document .info-row,
body.${printClass}.purchase-order-document .sum-row,
body.${printClass}.refund-document .line,
body.${printClass}.refund-document .info-row,
body.${printClass}.refund-document .sum-row,
body.${printClass}.order-request-document .line,
body.${printClass}.order-request-document .info-row,
body.${printClass}.order-request-document .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.${printClass}.purchase-order-document .info-strip,
body.${printClass}.refund-document .info-strip,
body.${printClass}.order-request-document .info-strip { gap: 8mm !important; margin-bottom: 2mm !important; }
body.${printClass}.purchase-order-document table,
body.${printClass}.refund-document table,
body.${printClass}.order-request-document table,
body.${printClass}.load-form-document table { margin-top: 2px !important; margin-bottom: 2mm !important; table-layout: fixed !important; }
body.${printClass}.purchase-order-document table th,
body.${printClass}.purchase-order-document table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; background: #e5e5e5 !important; border: 1px solid #000 !important; }
body.${printClass}.refund-document table th,
body.${printClass}.order-request-document table th,
body.${printClass}.load-form-document table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; background: #fff !important; background-color: #fff !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; border-left: 0 !important; border-right: 0 !important; }
body.${printClass}.purchase-order-document table td,
body.${printClass}.refund-document table td,
body.${printClass}.order-request-document table td,
body.${printClass}.load-form-document table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; border-bottom: 1px solid #000 !important; }
body.${printClass}.purchase-order-document table thead th,
body.${printClass}.purchase-order-document table thead th { background: #e5e5e5 !important; background-color: #e5e5e5 !important; color: #000 !important; border: 1px solid #000 !important; }
body.${printClass}.refund-document table thead th,
body.${printClass}.order-request-document table thead th,
body.${printClass}.load-form-document table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; border-left: 0 !important; border-right: 0 !important; }
body.${printClass}.purchase-order-document .summary-block,
body.${printClass}.refund-document .summary-block,
body.${printClass}.order-request-document .summary-block { flex-basis: 78mm !important; width: auto !important; }
/* PO A5 headings and summary use the same larger Sale hierarchy. */
body.${printClass}.purchase-order-document .info-label,
body.${printClass}.purchase-order-document .section-title,
body.${printClass}.purchase-order-document .pm-title { font-size: 14px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.${printClass}.purchase-order-document .sum-row { font-size: 13px !important; line-height: 1.2 !important; padding: 1.5px 5px !important; }
body.${printClass}.purchase-order-document .total-items-row { font-size: 14px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.${printClass}.purchase-order-document .grand-total-row,
body.${printClass}.purchase-order-document .total-line,
body.${printClass}.refund-document .grand-total-row,
body.${printClass}.refund-document .total-line,
body.${printClass}.order-request-document .grand-total-row,
body.${printClass}.order-request-document .total-line { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
body.${printClass}.purchase-order-document .ordered-items-heading { display: none !important; }
body.${printClass}.purchase-order-document .tax-total-separator,
body.${printClass}.purchase-order-document .footer-separator { display: none !important; }
body.${printClass}.purchase-order-document .tax-gross-separator,
body.${printClass}.purchase-order-document .gross-net-separator { display: none !important; }
body.${printClass}.purchase-order-document .summary-separator { border: 0 !important; border-top: 1px solid #000 !important; height: 0 !important; margin: 1mm 0 !important; }
body.${printClass}.sale-document .total-items,
body.${printClass}.sale-document .total-items-row,
body.${printClass}.sale-document .total-items span,
body.${printClass}.sale-document .total-items-row span { font-weight: 500 !important; }
body.${printClass}.sale-document .grand-total + .balance,
body.${printClass}.sale-document .grand-total-row + .balance-row { border-top: 0 !important; }
body.print-a5p.order-request-document .order-number-line {
    border-bottom: 0 !important;
    padding-bottom: 1.5px !important;
    margin-bottom: 0 !important;
}
/* Final A5 Portrait Sale contract for every paper document. */
body.print-a5p.purchase-order-document,
body.print-a5p.order-request-document,
body.print-a5p.refund-document,
body.print-a5p.exchange-document,
body.print-a5p.load-form-document,
body.print-a5p.transfer-document,
body.print-a5p.gatepass-document,
body.print-a5p.supplier-document {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    line-height: 1.4 !important;
    color: #000 !important;
}
body.print-a5p.purchase-order-document .company-name,
body.print-a5p.order-request-document .company-name,
body.print-a5p.refund-document .company-name,
body.print-a5p.exchange-document .company-name,
body.print-a5p.load-form-document .company-name,
body.print-a5p.transfer-document .company-name,
body.print-a5p.gatepass-document .company-name,
body.print-a5p.supplier-document .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.print-a5p.purchase-order-document .invoice-word,
body.print-a5p.purchase-order-document .sale-invoice-title,
body.print-a5p.order-request-document .invoice-word,
body.print-a5p.order-request-document .sale-invoice-title,
body.print-a5p.refund-document .invoice-word,
body.print-a5p.refund-document .sale-invoice-title,
body.print-a5p.exchange-document .invoice-word,
body.print-a5p.exchange-document .sale-invoice-title,
body.print-a5p.load-form-document .invoice-word,
body.print-a5p.load-form-document .sale-invoice-title,
body.print-a5p.transfer-document .invoice-word,
body.print-a5p.transfer-document .sale-invoice-title,
body.print-a5p.gatepass-document .invoice-word,
body.print-a5p.gatepass-document .sale-invoice-title,
body.print-a5p.supplier-document .invoice-word,
body.print-a5p.supplier-document .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; text-transform: none !important; }
body.print-a5p.purchase-order-document .invoice-no,
body.print-a5p.order-request-document .invoice-no,
body.print-a5p.refund-document .invoice-no,
body.print-a5p.exchange-document .invoice-no,
body.print-a5p.load-form-document .invoice-no,
body.print-a5p.transfer-document .invoice-no,
body.print-a5p.gatepass-document .invoice-no,
body.print-a5p.supplier-document .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.print-a5p.purchase-order-document .company-meta,
body.print-a5p.order-request-document .company-meta,
body.print-a5p.refund-document .company-meta,
body.print-a5p.exchange-document .company-meta,
body.print-a5p.load-form-document .company-meta,
body.print-a5p.transfer-document .company-meta,
body.print-a5p.gatepass-document .company-meta,
body.print-a5p.supplier-document .company-meta { font-size: 10px !important; line-height: 1.25 !important; font-weight: 600 !important; }
body.print-a5p.purchase-order-document .section,
body.print-a5p.purchase-order-document .section-title,
body.print-a5p.purchase-order-document .info-label,
body.print-a5p.purchase-order-document .pm-title,
body.print-a5p.order-request-document .section,
body.print-a5p.order-request-document .section-title,
body.print-a5p.order-request-document .info-label,
body.print-a5p.order-request-document .pm-title,
body.print-a5p.refund-document .section,
body.print-a5p.refund-document .section-title,
body.print-a5p.refund-document .info-label,
body.print-a5p.refund-document .pm-title,
body.print-a5p.exchange-document .section,
body.print-a5p.exchange-document .section-title,
body.print-a5p.exchange-document .info-label,
body.print-a5p.exchange-document .pm-title,
body.print-a5p.load-form-document .section,
body.print-a5p.load-form-document .section-title,
body.print-a5p.load-form-document .info-label,
body.print-a5p.load-form-document .pm-title,
body.print-a5p.transfer-document .section,
body.print-a5p.transfer-document .section-title,
body.print-a5p.transfer-document .info-label,
body.print-a5p.transfer-document .pm-title,
body.print-a5p.gatepass-document .section,
body.print-a5p.gatepass-document .section-title,
body.print-a5p.gatepass-document .info-label,
body.print-a5p.gatepass-document .pm-title,
body.print-a5p.supplier-document .section,
body.print-a5p.supplier-document .section-title,
body.print-a5p.supplier-document .info-label,
body.print-a5p.supplier-document .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.print-a5p.purchase-order-document .line,
body.print-a5p.purchase-order-document .info-row,
body.print-a5p.purchase-order-document .sum-row,
body.print-a5p.order-request-document .line,
body.print-a5p.order-request-document .info-row,
body.print-a5p.order-request-document .sum-row,
body.print-a5p.refund-document .line,
body.print-a5p.refund-document .info-row,
body.print-a5p.refund-document .sum-row,
body.print-a5p.exchange-document .line,
body.print-a5p.exchange-document .info-row,
body.print-a5p.exchange-document .sum-row,
body.print-a5p.load-form-document .line,
body.print-a5p.load-form-document .info-row,
body.print-a5p.load-form-document .sum-row,
body.print-a5p.transfer-document .line,
body.print-a5p.transfer-document .info-row,
body.print-a5p.transfer-document .sum-row,
body.print-a5p.gatepass-document .line,
body.print-a5p.gatepass-document .info-row,
body.print-a5p.gatepass-document .sum-row,
body.print-a5p.supplier-document .line,
body.print-a5p.supplier-document .info-row,
body.print-a5p.supplier-document .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document table th,
body.print-a5p.order-request-document table th,
body.print-a5p.refund-document table th,
body.print-a5p.exchange-document table th,
body.print-a5p.load-form-document table th,
body.print-a5p.transfer-document table th,
body.print-a5p.gatepass-document table th,
body.print-a5p.supplier-document table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.print-a5p.purchase-order-document table td,
body.print-a5p.order-request-document table td,
body.print-a5p.refund-document table td,
body.print-a5p.exchange-document table td,
body.print-a5p.load-form-document table td,
body.print-a5p.transfer-document table td,
body.print-a5p.gatepass-document table td,
body.print-a5p.supplier-document table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .total-items-row,
body.print-a5p.order-request-document .total-items,
body.print-a5p.order-request-document .total-items-row,
body.print-a5p.refund-document .total-items,
body.print-a5p.refund-document .total-items-row,
body.print-a5p.load-form-document .total-items,
body.print-a5p.load-form-document .total-items-row,
body.print-a5p.transfer-document .total-items,
body.print-a5p.gatepass-document .total-items { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .sum-row,
body.print-a5p.order-request-document .grand-total-row,
body.print-a5p.order-request-document .total-line,
body.print-a5p.refund-document .grand-total-row,
body.print-a5p.refund-document .total-line,
body.print-a5p.load-form-document .grand-total-row,
body.print-a5p.load-form-document .total-line,
body.print-a5p.transfer-document .grand-total-row,
body.print-a5p.gatepass-document .grand-total-row,
body.print-a5p.supplier-document .grand-total,
body.print-a5p.supplier-document .total-line { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
/* Final portrait geometry: use the Sale A5 frame and remove legacy template
   rules that caused PO, Order Request, and Return/Refund to drift. */
body.print-a5p .page { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 3mm 10mm !important; box-sizing: border-box !important; }
body.print-a5p .header-row { width: 100% !important; gap: 6mm !important; margin-bottom: 1.5mm !important; }
body.print-a5p .header-left { gap: 3mm !important; }
body.print-a5p .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; padding-top: 2px !important; }
body.print-a5p .info-strip { width: 100% !important; gap: 8mm !important; margin-bottom: 2mm !important; }
body.print-a5p.refund-document .info-strip,
body.print-a5p.order-request-document .info-strip { display: flex !important; align-items: flex-start !important; }
body.print-a5p.refund-document .info-strip .info-block,
body.print-a5p.order-request-document .info-strip .info-block,
body.print-a5p.supplier-document .info-strip .info-block { flex: 1 1 0 !important; width: 0 !important; min-width: 0 !important; }
body.print-a5p.supplier-document .header-row { align-items: flex-start !important; }
body.print-a5p.supplier-document .header-left { align-items: center !important; gap: 3mm !important; text-align: left !important; }
body.print-a5p.supplier-document .header-left .company-name,
body.print-a5p.supplier-document .header-left .invoice-word,
body.print-a5p.supplier-document .header-left .sale-invoice-title { text-align: left !important; }
body.print-a5p.supplier-document .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; padding-top: 2px !important; text-align: right !important; }
body.print-a5p.supplier-document .supplier-email { white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
body.print-a5.supplier-document .supplier-email { white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
body.print-a5p table { width: 100% !important; max-width: 100% !important; margin-top: 2px !important; margin-bottom: 2mm !important; table-layout: fixed !important; }
body.print-a5p table thead,
body.print-a5p table thead tr,
body.print-a5p table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; }
body.print-a5p table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; border-left: 0 !important; border-right: 0 !important; }
body.print-a5p table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
/* One portrait grid for Purchase Order, Sale, Refund/Return, and Order
   Request. Keep short numeric columns wide enough to stay horizontal. */
body.print-a5p.purchase-order-document .po-items-table th:nth-child(1),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(1) { width: 8% !important; text-align: center !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(2) { width: 34% !important; text-align: left !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(4),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(4) { width: 12% !important; text-align: center !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(3),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(3) { width: 14% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(5) { width: 10% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(6),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(6) { width: 10% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:last-child,
body.print-a5p.purchase-order-document .po-items-table td:last-child { width: 12% !important; text-align: right !important; }
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(2) { width: 42% !important; }
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:last-child,
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:last-child { width: 24% !important; }
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table td:nth-child(5),
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table td:nth-child(5) { width: 10% !important; }
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table th:last-child,
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table td:last-child,
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table th:last-child,
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table td:last-child { width: 22% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table th,
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table td { text-align: right !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table .col-rate { width: 20% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table .col-qty { width: 14% !important; text-align: center !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table .col-tax,
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table .col-discount { width: 20% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table .col-total { width: 26% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table.items-3col .col-rate { width: 28% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table.items-3col .col-qty { width: 18% !important; }
body.print-a5p.sale-document:not(.purchase-order-document):not(.refund-document) .sale-items-table.items-3col .col-total { width: 54% !important; }
body.print-a5p .summary-block { flex-basis: 78mm !important; width: 78mm !important; max-width: 100% !important; }
body.print-a5p .total-items,
body.print-a5p .total-items-row { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5p .sum-row { font-size: 9.5px !important; line-height: 1.2 !important; padding: 1.5px 5px !important; font-weight: 500 !important; }
body.print-a5p .total-line,
body.print-a5p .grand-total,
body.print-a5p .grand-total-row { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
body.print-a5p .footer,
body.print-a5p .footer-bar { font-size: 9px !important; line-height: 1.25 !important; border-top: 1px solid #000 !important; padding-top: 1mm !important; }
body.print-a5p.order-request-document .header-rule { display: block !important; border: 0 !important; border-top: 1px dashed #000 !important; height: 0 !important; margin: 0 0 2mm !important; }
body.print-a5p.order-request-document table + .hr { display: none !important; }
body.print-a5p.order-request-document .order-number-line { border-bottom: 0 !important; }
body.print-a5p.refund-document:not(.exchange-document) .returned-items-title,
body.print-a5p.refund-document:not(.exchange-document) .returned-items-heading { display: none !important; }
body.print-a5p.refund-document table thead,
body.print-a5p.refund-document table thead tr,
body.print-a5p.refund-document table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
/* A5 portrait Refund and Order Request tables share equal, predictable
   columns so labels and values line up consistently on narrow paper. */
body.print-a5p.refund-document .items-table th:nth-child(1),
body.print-a5p.refund-document .items-table td:nth-child(1),
body.print-a5p.order-request-document table th:nth-child(1),
body.print-a5p.order-request-document table td:nth-child(1) { width: 8% !important; text-align: center !important; }
body.print-a5p.refund-document .items-table th:nth-child(2),
body.print-a5p.refund-document .items-table td:nth-child(2),
body.print-a5p.order-request-document table th:nth-child(2),
body.print-a5p.order-request-document table td:nth-child(2) { width: 30% !important; text-align: left !important; }
body.print-a5p.refund-document .items-table th:nth-child(4),
body.print-a5p.refund-document .items-table td:nth-child(4),
body.print-a5p.order-request-document table th:nth-child(4),
body.print-a5p.order-request-document table td:nth-child(4) { width: 9% !important; text-align: center !important; }
body.print-a5p.refund-document .items-table th:nth-child(3),
body.print-a5p.refund-document .items-table td:nth-child(3),
body.print-a5p.order-request-document table th:nth-child(3),
body.print-a5p.order-request-document table td:nth-child(3) { width: 15% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table th:nth-child(5),
body.print-a5p.refund-document .items-table td:nth-child(5),
body.print-a5p.order-request-document table th:nth-child(5),
body.print-a5p.order-request-document table td:nth-child(5) { width: 14% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table th:nth-child(6),
body.print-a5p.refund-document .items-table td:nth-child(6),
body.print-a5p.order-request-document table th:nth-child(6),
body.print-a5p.order-request-document table td:nth-child(6) { width: 14% !important; text-align: center !important; }
body.print-a5p.refund-document .items-table th:nth-child(7),
body.print-a5p.refund-document .items-table td:nth-child(7),
body.print-a5p.order-request-document table th:nth-child(7),
body.print-a5p.order-request-document table td:nth-child(7) { width: 18% !important; text-align: right !important; }
/* The amount is the last column whichever optional columns print, so it is
   sized and aligned by name: by position it took the discount's centring
   whenever the tax or the discount column was hidden. */
body.print-a5p.refund-document .items-table th.col-total,
body.print-a5p.refund-document .items-table td.col-total { width: 18% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table.items-3col th:nth-child(5),
body.print-a5p.refund-document .items-table.items-3col td:nth-child(5),
body.print-a5p.order-request-document table th:nth-child(5):last-child,
body.print-a5p.order-request-document table td:nth-child(5):last-child { width: 24% !important; }
body.print-a5p.refund-document .items-table.items-3col th:nth-child(2),
body.print-a5p.refund-document .items-table.items-3col td:nth-child(2) { width: 52% !important; }
body.print-a5p.refund-document .items-table .col-item,
body.print-a5p.order-request-document table .col-item { width: 30% !important; text-align: left !important; }
body.print-a5p.refund-document .items-table .col-qty,
body.print-a5p.order-request-document table .col-qty { width: 9% !important; text-align: center !important; }
body.print-a5p.refund-document .items-table .col-rate,
body.print-a5p.order-request-document table .col-rate { width: 15% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table .col-tax,
body.print-a5p.refund-document .items-table .col-discount,
body.print-a5p.order-request-document table .col-tax,
body.print-a5p.order-request-document table .col-discount { width: 14% !important; }
body.print-a5p.refund-document .items-table .col-total,
body.print-a5p.order-request-document table .col-total { width: 18% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table.items-3col .col-total,
body.print-a5p.order-request-document table.items-3col .col-total { width: 24% !important; }
body.print-a5p.refund-document .footer,
body.print-a5p.refund-document .document > .page > .footer { display: block !important; width: 100% !important; border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
body.print-a5p.exchange-document .footer,
body.print-a5p.exchange-document .document > .page > .footer { display: block !important; width: 100% !important; border-top: 0 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
body.print-a5p.exchange-document .footer::before { content: '' !important; display: block !important; width: 100% !important; height: 0 !important; border-top: 1px solid #000 !important; margin: 0 0 1mm !important; }
body.print-a5p.load-form-document table thead,
body.print-a5p.load-form-document table thead tr,
body.print-a5p.load-form-document table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5p.load-form-document .load-summary-table thead,
body.print-a5p.load-form-document .load-summary-table thead tr,
body.print-a5p.load-form-document .load-summary-table thead th,
body.print-a5p.refund-document .items-table thead,
body.print-a5p.refund-document .items-table thead tr,
body.print-a5p.refund-document .items-table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5p.load-form-document .load-summary-table thead th,
body.print-a5p.refund-document .items-table thead th { border-inline: 0 !important; }
body.print-a5p.purchase-order-document .po-items-table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
body.print-a5p.purchase-order-document .summary-block { width: 78mm !important; flex-basis: 78mm !important; }
body.print-a5p.purchase-order-document .footer,
body.print-a5p.purchase-order-document .footer-bar { display: block !important; width: 100% !important; border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
/* Sale A5 Portrait is the reference document; keep its final Agent output
   identical to the PO Portrait frame and table treatment. */
body.print-a5p.sale-document .page { width: 100% !important; max-width: 100% !important; padding: 3mm 10mm !important; }
body.print-a5p.sale-document .header-row { gap: 6mm !important; margin-bottom: 1.5mm !important; }
body.print-a5p.sale-document .header-left { gap: 3mm !important; }
body.print-a5p.sale-document .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; }
body.print-a5p.sale-document .info-strip { gap: 8mm !important; margin-bottom: 2mm !important; }
body.print-a5p.sale-document table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin-bottom: 2mm !important; }
body.print-a5p.sale-document table thead,
body.print-a5p.sale-document table thead tr,
body.print-a5p.sale-document table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5p.sale-document table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.print-a5p.sale-document table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body.print-a5p.sale-document .summary-block { width: 78mm !important; flex-basis: 78mm !important; }
/* Refund/Return uses an extra .document wrapper; remove its duplicate
   horizontal inset so its content starts on the same Sale/PO frame. */
body.print-a5p.refund-document > .document { width: 148mm !important; max-width: 148mm !important; min-width: 148mm !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body.print-a5p.refund-document > .document > .page { width: 148mm !important; max-width: 148mm !important; min-width: 148mm !important; margin: 0 !important; padding: 3mm 10mm !important; box-sizing: border-box !important; }
body.print-a5p.refund-document .header,
body.print-a5p.refund-document .header-row,
body.print-a5p.refund-document .info-strip,
body.print-a5p.refund-document table,
body.print-a5p.refund-document .footer { width: 100% !important; max-width: 100% !important; box-sizing: border-box !important; }
/* Keep tax and discount values aligned with the Sale/PO columns. */
body.print-a5p.sale-document table th:nth-child(1),
body.print-a5p.sale-document table td:nth-child(1),
body.print-a5p.purchase-order-document table th:nth-child(1),
body.print-a5p.purchase-order-document table td:nth-child(1),
body.print-a5p.refund-document table th:nth-child(1),
body.print-a5p.refund-document table td:nth-child(1),
body.print-a5p.order-request-document table th:nth-child(1),
body.print-a5p.order-request-document table td:nth-child(1) { text-align: center !important; }
body.print-a5p.sale-document table th:nth-child(2),
body.print-a5p.sale-document table td:nth-child(2),
body.print-a5p.purchase-order-document table th:nth-child(2),
body.print-a5p.purchase-order-document table td:nth-child(2),
body.print-a5p.refund-document table th:nth-child(2),
body.print-a5p.refund-document table td:nth-child(2),
body.print-a5p.order-request-document table th:nth-child(2),
body.print-a5p.order-request-document table td:nth-child(2) { text-align: left !important; }
body.print-a5p.sale-document table .col-qty,
body.print-a5p.purchase-order-document table .col-qty,
body.print-a5p.refund-document table .col-qty,
body.print-a5p.order-request-document table .col-qty { text-align: center !important; }
body.print-a5p.sale-document table .col-rate,
body.print-a5p.sale-document table .col-tax,
body.print-a5p.sale-document table .col-total,
body.print-a5p.purchase-order-document table .col-rate,
body.print-a5p.purchase-order-document table .col-tax,
body.print-a5p.purchase-order-document table .col-total,
body.print-a5p.refund-document table .col-rate,
body.print-a5p.refund-document table .col-tax,
body.print-a5p.refund-document table .col-total,
body.print-a5p.order-request-document table .col-rate,
body.print-a5p.order-request-document table .col-tax,
body.print-a5p.order-request-document table .col-total { text-align: right !important; }
body.print-a5p.sale-document table .col-discount,
body.print-a5p.purchase-order-document table .col-discount,
body.print-a5p.refund-document table .col-discount,
body.print-a5p.order-request-document table .col-discount { text-align: center !important; }
body.print-a5p.sale-document .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.print-a5p.sale-document .invoice-word,
body.print-a5p.sale-document .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; }
body.print-a5p.sale-document .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.print-a5p.sale-document .section,
body.print-a5p.sale-document .section-title,
body.print-a5p.sale-document .info-label,
body.print-a5p.sale-document .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.print-a5p.sale-document .line,
body.print-a5p.sale-document .info-row,
body.print-a5p.sale-document .pm-row,
body.print-a5p.sale-document .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.print-a5p.sale-document .total-items,
body.print-a5p.sale-document .total-items-row { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5p.sale-document .total-line,
body.print-a5p.sale-document .grand-total,
body.print-a5p.sale-document .grand-total-row { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
/* Definitive PO = Sale A5 Portrait contract. Keep this last so legacy PO
   template rules cannot override the Sale reference values. */
body.print-a5p.purchase-order-document .page { width: 100% !important; max-width: 100% !important; padding: 3mm 10mm !important; }
body.print-a5p.purchase-order-document .header-row { width: 100% !important; gap: 6mm !important; margin-bottom: 1.5mm !important; }
body.print-a5p.purchase-order-document .header-left { gap: 3mm !important; }
body.print-a5p.purchase-order-document .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; padding-top: 2px !important; }
body.print-a5p.purchase-order-document .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.print-a5p.purchase-order-document .invoice-word,
body.print-a5p.purchase-order-document .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; }
body.print-a5p.purchase-order-document .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.print-a5p.purchase-order-document .section,
body.print-a5p.purchase-order-document .section-title,
body.print-a5p.purchase-order-document .info-label,
body.print-a5p.purchase-order-document .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.print-a5p.purchase-order-document .line,
body.print-a5p.purchase-order-document .info-row,
body.print-a5p.purchase-order-document .pm-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .po-items-table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin-top: 2px !important; margin-bottom: 2mm !important; }
body.print-a5p.purchase-order-document .po-items-table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.print-a5p.purchase-order-document .po-items-table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .po-items-table thead,
body.print-a5p.purchase-order-document .po-items-table thead tr,
body.print-a5p.purchase-order-document .po-items-table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5p.purchase-order-document .summary-block { width: 78mm !important; max-width: 100% !important; flex: 0 0 78mm !important; }
body.print-a5p.purchase-order-document .total-items-row { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .sum-row { font-size: 9.5px !important; line-height: 1.2 !important; padding: 1.5px 5px !important; font-weight: 500 !important; }
body.print-a5p.purchase-order-document .grand-total-row,
body.print-a5p.purchase-order-document .total-line { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
body.print-a5p.purchase-order-document .footer,
body.print-a5p.purchase-order-document .footer-bar { width: 100% !important; border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
/* Sale and PO use the same A5 Portrait five-column geometry. */
body.print-a5p.sale-document .sale-items-table,
body.print-a5p.purchase-order-document .po-items-table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table th:nth-child(1),
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table td:nth-child(1),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(1),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(1) { width: 5% !important; }
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table th:nth-child(2),
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table td:nth-child(2),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(2) { width: 42% !important; text-align: left !important; }
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table th:nth-child(4),
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table td:nth-child(4),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(4),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(4) { width: 14% !important; }
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table th:nth-child(3),
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table td:nth-child(3),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(3),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(3) { width: 13% !important; }
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table th:nth-child(5),
body.print-a5p.sale-document:not(.refund-document):not(.sale-tax):not(.sale-discount) .sale-items-table td:nth-child(5),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(5) { width: 13% !important; }
/* Final Sale-to-PO A5 Portrait width lock. */
body.print-a5p.sale-document:not(.refund-document) .sale-items-table th:nth-child(2),
body.print-a5p.sale-document:not(.refund-document) .sale-items-table td:nth-child(2) { width: 34% !important; text-align: left !important; }
body.print-a5p.sale-document:not(.refund-document) .sale-items-table th:last-child,
body.print-a5p.sale-document:not(.refund-document) .sale-items-table td:last-child { width: 26% !important; }
/* The invoice says which optional columns it prints (sale-tax / sale-discount),
   so the five-column geometry above is the bare table alone. With a column
   added, every figure keeps a width that holds a six-figure amount on one line. */
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-no,
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .sno { width: 4% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-item { width: 24% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-rate { width: 14% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-qty { width: 8% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-tax { width: 20% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-discount { width: 13% !important; }
body.print-a5p.sale-document.sale-tax.sale-discount:not(.refund-document) .sale-items-table .col-total { width: 17% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-no,
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .sno { width: 4% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-item { width: 33% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-rate { width: 15% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-qty { width: 8% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-tax { width: 21% !important; }
body.print-a5p.sale-document.sale-tax:not(.sale-discount):not(.refund-document) .sale-items-table .col-total { width: 19% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-no,
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .sno { width: 4% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-item { width: 38% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-rate { width: 16% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-qty { width: 9% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-discount { width: 14% !important; }
body.print-a5p.sale-document.sale-discount:not(.sale-tax):not(.refund-document) .sale-items-table .col-total { width: 19% !important; }
/* Lock both nested paper wrappers to the same physical A5 Portrait frame. */
body.print-a5p.sale-document > .document,
body.print-a5p.purchase-order-document > .document,
body.print-a5p.sale-document > .half,
body.print-a5p.purchase-order-document > .half { width: 148mm !important; max-width: 148mm !important; min-width: 148mm !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important; }
body.print-a5p.sale-document > .document > .page,
body.print-a5p.purchase-order-document > .document > .page,
body.print-a5p.sale-document > .half > .page,
body.print-a5p.purchase-order-document > .half > .page { width: 148mm !important; max-width: 148mm !important; min-width: 148mm !important; margin: 0 !important; padding: 3mm 10mm !important; box-sizing: border-box !important; }
/* Final A5 Portrait column alignment for PO, Return/Refund, and Order Request. */
body.print-a5p.purchase-order-document table th,
body.print-a5p.purchase-order-document table td,
body.print-a5p.refund-document table th,
body.print-a5p.refund-document table td,
body.print-a5p.order-request-document table th,
body.print-a5p.order-request-document table td { box-sizing: border-box !important; vertical-align: middle !important; }
body.print-a5p.purchase-order-document table th:nth-child(1),
body.print-a5p.purchase-order-document table td:nth-child(1),
body.print-a5p.refund-document table th:nth-child(1),
body.print-a5p.refund-document table td:nth-child(1),
body.print-a5p.order-request-document table th:nth-child(1),
body.print-a5p.order-request-document table td:nth-child(1) { text-align: center !important; }
body.print-a5p.purchase-order-document table th:nth-child(2),
body.print-a5p.purchase-order-document table td:nth-child(2),
body.print-a5p.refund-document table th:nth-child(2),
body.print-a5p.refund-document table td:nth-child(2),
body.print-a5p.order-request-document table th:nth-child(2),
body.print-a5p.order-request-document table td:nth-child(2) { text-align: left !important; }
body.print-a5p.purchase-order-document table .col-qty,
body.print-a5p.refund-document table .col-qty,
body.print-a5p.order-request-document table .col-qty { text-align: center !important; }
body.print-a5p.purchase-order-document table .col-rate,
body.print-a5p.purchase-order-document table .col-tax,
body.print-a5p.purchase-order-document table .col-total,
body.print-a5p.refund-document table .col-rate,
body.print-a5p.refund-document table .col-tax,
body.print-a5p.refund-document table .col-total,
body.print-a5p.order-request-document table .col-rate,
body.print-a5p.order-request-document table .col-tax,
body.print-a5p.order-request-document table .col-total { text-align: right !important; }
body.print-a5p.purchase-order-document table .col-discount,
body.print-a5p.refund-document table .col-discount,
body.print-a5p.order-request-document table .col-discount { text-align: center !important; }
body.print-a5p.purchase-order-document table th:nth-child(4),
body.print-a5p.purchase-order-document table td:nth-child(4),
body.print-a5p.refund-document table th:nth-child(4),
body.print-a5p.refund-document table td:nth-child(4),
body.print-a5p.order-request-document table th:nth-child(4),
body.print-a5p.order-request-document table td:nth-child(4) { text-align: center !important; }
body.print-a5p.purchase-order-document table th:nth-child(3),
body.print-a5p.purchase-order-document table td:nth-child(3),
body.print-a5p.refund-document table th:nth-child(3),
body.print-a5p.refund-document table td:nth-child(3),
body.print-a5p.order-request-document table th:nth-child(3),
body.print-a5p.order-request-document table td:nth-child(3) { text-align: right !important; }
/* A5 Landscape Sale reference contract for PO, Return/Refund, Order Request,
   and Load Form. */
body.print-a5.sale-document,
body.print-a5.purchase-order-document,
body.print-a5.refund-document,
body.print-a5.exchange-document,
body.print-a5.order-request-document,
body.print-a5.load-form-document { font-family: Arial, Helvetica, sans-serif !important; font-size: 11.5px !important; line-height: 1.4 !important; color: #000 !important; }
body.print-a5 .page { width: 100% !important; max-width: 100% !important; padding: 3mm 6mm !important; box-sizing: border-box !important; }
body.print-a5 .header-row { gap: 6mm !important; margin-bottom: 1.5mm !important; }
body.print-a5 .header-left { gap: 3mm !important; }
body.print-a5 .header-right { flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; padding-top: 2px !important; }
body.print-a5 .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.print-a5 .invoice-word,
body.print-a5 .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; }
body.print-a5 .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; }
body.print-a5 .section,
body.print-a5 .section-title,
body.print-a5 .info-label,
body.print-a5 .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.print-a5 .line,
body.print-a5 .info-row,
body.print-a5 .pm-row,
body.print-a5 .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.print-a5 table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; margin-top: 2px !important; margin-bottom: 2mm !important; }
body.print-a5 table th { font-size: 9.5px !important; line-height: 1.2 !important; padding: 4px 5px !important; font-weight: 700 !important; }
body.print-a5 table td { font-size: 10.5px !important; line-height: 1.25 !important; padding: 2.6px 5px !important; font-weight: 500 !important; }
body.print-a5 table thead,
body.print-a5 table thead tr,
body.print-a5 table thead th { background: #fff !important; background-color: #fff !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5 .summary-block { width: 78mm !important; flex: 0 0 78mm !important; max-width: 100% !important; }
body.print-a5 .total-items,
body.print-a5 .total-items-row { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5 .total-line,
body.print-a5 .grand-total,
body.print-a5 .grand-total-row { font-size: 10px !important; line-height: 1.2 !important; padding: 5px !important; font-weight: 700 !important; }
body.print-a5 .footer,
body.print-a5 .footer-bar { border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
body.print-a5.load-form-document table thead,
body.print-a5.load-form-document table thead tr,
body.print-a5.load-form-document table thead th,
body.print-a5.order-request-document table thead,
body.print-a5.order-request-document table thead tr,
body.print-a5.order-request-document table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
/* PO and Order Request share one exact A5 Landscape document contract. */
body.print-a5.purchase-order-document .section,
body.print-a5.purchase-order-document .section-title,
body.print-a5.purchase-order-document .info-label,
body.print-a5.purchase-order-document .pm-title { font-size: 9px !important; line-height: 1.2 !important; font-weight: 800 !important; letter-spacing: 1px !important; }
body.print-a5.purchase-order-document .line,
body.print-a5.purchase-order-document .info-row,
body.print-a5.purchase-order-document .pm-row,
body.print-a5.purchase-order-document .sum-row { font-size: 10.5px !important; line-height: 1.25 !important; padding: 1.5px 0 !important; font-weight: 500 !important; }
body.print-a5.purchase-order-document .po-items-table,
body.print-a5.order-request-document table { width: 100% !important; max-width: 100% !important; table-layout: fixed !important; }
body.print-a5.purchase-order-document .po-items-table th,
body.print-a5.purchase-order-document .po-items-table td { box-sizing: border-box !important; }
body.print-a5.purchase-order-document .po-items-table thead,
body.print-a5.purchase-order-document .po-items-table thead tr,
body.print-a5.purchase-order-document .po-items-table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5.purchase-order-document .summary-block { width: 78mm !important; flex: 0 0 78mm !important; }
body.print-a5.purchase-order-document .total-items-row { font-size: 10.5px !important; line-height: 1.25 !important; font-weight: 500 !important; }
body.print-a5.purchase-order-document .sum-row { font-size: 9.5px !important; line-height: 1.2 !important; padding: 1.5px 5px !important; }
body.print-a5.purchase-order-document .footer,
body.print-a5.purchase-order-document .footer-bar { border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
body.print-a5.refund-document table thead,
body.print-a5.refund-document table thead tr,
body.print-a5.refund-document table thead th,
body.print-a5.load-form-document table thead,
body.print-a5.load-form-document table thead tr,
body.print-a5.load-form-document table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5.refund-document .items-table thead,
body.print-a5.refund-document .items-table thead tr,
body.print-a5.refund-document .items-table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; box-shadow: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
/* Supplier Payment and Receiving receipts use .header instead of
   .header-row; give them the same Order Request A5 Landscape header frame. */
body.print-a5.supplier-document .header,
body.print-a5.supplier-document .header-row { display: flex !important; flex-direction: row !important; align-items: flex-start !important; justify-content: space-between !important; width: 100% !important; gap: 6mm !important; margin: 0 0 1.5mm !important; padding: 0 !important; text-align: left !important; }
body.print-a5.supplier-document .header-left { display: flex !important; flex: 1 1 auto !important; width: auto !important; min-width: 0 !important; align-items: center !important; gap: 3mm !important; text-align: left !important; }
body.print-a5.supplier-document .header-right { display: block !important; flex: 0 0 43% !important; width: 43% !important; max-width: 43% !important; min-width: 0 !important; padding-top: 2px !important; text-align: right !important; }
body.print-a5.supplier-document .header-left .company-name { font-size: 15px !important; line-height: 1.05 !important; font-weight: 600 !important; }
body.print-a5.supplier-document .header-left .invoice-word,
body.print-a5.supplier-document .header-left .sale-invoice-title { font-size: 15px !important; line-height: 1.2 !important; font-weight: 600 !important; letter-spacing: .2px !important; }
body.print-a5.supplier-document .header-left .invoice-no { font-size: 10px !important; line-height: 1.2 !important; font-weight: 500 !important; letter-spacing: .5px !important; }
body.print-a5.supplier-document .header-rule { width: 100% !important; border-top: 1px dashed #000 !important; margin: 0 0 2mm !important; }
body.print-a5.supplier-document table thead,
body.print-a5.supplier-document table thead tr,
body.print-a5.supplier-document table thead th { background: #fff !important; background-color: #fff !important; background-image: none !important; color: #000 !important; border-left: 0 !important; border-right: 0 !important; border-top: 1px solid #000 !important; border-bottom: 1px solid #000 !important; }
body.print-a5.supplier-document .summary-block { width: 78mm !important; flex: 0 0 78mm !important; }
body.print-a5.supplier-document .footer,
body.print-a5.supplier-document .footer-bar { border-top: 1px solid #000 !important; padding-top: 1mm !important; margin-top: 1.5mm !important; }
/* Final A5 portrait supplier grid: keep the item/value columns balanced and
   use the same left/center/right alignment as Sale. */
body.print-a5p.supplier-document .info-strip { display: flex !important; width: 100% !important; gap: 8mm !important; align-items: flex-start !important; }
body.print-a5p.supplier-document .info-strip { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 8mm !important; }
body.print-a5p.supplier-document .info-strip .info-block { display: block !important; width: auto !important; min-width: 0 !important; max-width: none !important; }
body.print-a5p.supplier-document .info-strip .line { display: grid !important; grid-template-columns: 32% minmax(0, 1fr) !important; column-gap: 2mm !important; align-items: baseline !important; }
body.print-a5p.supplier-document .info-strip .line > :first-child { min-width: 0 !important; text-align: left !important; white-space: nowrap !important; }
body.print-a5p.supplier-document .info-strip .line > :last-child { min-width: 0 !important; text-align: right !important; white-space: nowrap !important; overflow: visible !important; }
body.print-a5p.supplier-document table th:nth-child(1),
body.print-a5p.supplier-document table td:nth-child(1) { width: 10% !important; text-align: center !important; }
body.print-a5p.supplier-document table th:nth-child(2),
body.print-a5p.supplier-document table td:nth-child(2) { width: 65% !important; text-align: left !important; }
body.print-a5p.supplier-document table th:nth-child(3),
body.print-a5p.supplier-document table td:nth-child(3) { width: 25% !important; text-align: right !important; }
body.print-a5p.supplier-document.receiving-document table th:nth-child(3),
body.print-a5p.supplier-document.receiving-document table td:nth-child(3) { text-align: center !important; }
body.print-a5p.supplier-document table td.col-item { white-space: normal !important; overflow-wrap: break-word !important; word-break: normal !important; }
body.print-a5p.supplier-document .supplier-email { white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
body.print-a5p.supplier-document .info-strip .line > :last-child,
body.print-a5.supplier-document .info-strip .line > :last-child {
    display: block !important;
    width: max-content !important;
    max-width: none !important;
    justify-self: end !important;
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: keep-all !important;
}
body.print-a5p.supplier-document .supplier-email,
body.print-a5.supplier-document .supplier-email { font-size: 9px !important; }
/* A5 portrait paper tables do not need a separate No. column. Removing it
   gives the actual data columns enough room to stay aligned and readable. */
body.print-a5p.purchase-order-document .po-items-table th:first-child,
body.print-a5p.purchase-order-document .po-items-table td:first-child,
body.print-a5p.order-request-document table th:first-child,
body.print-a5p.order-request-document table td:first-child,
body.print-a5p.refund-document .items-table th:first-child,
body.print-a5p.refund-document .items-table td:first-child,
body.print-a5p.supplier-document table th:first-child,
body.print-a5p.supplier-document table td:first-child { display: none !important; }
body.print-a5p.supplier-document table th:first-child,
body.print-a5p.supplier-document table td:first-child { display: table-cell !important; }
body.print-a5p.supplier-document table th:first-child,
body.print-a5p.supplier-document table td:first-child { width: 70% !important; text-align: left !important; padding-left: 0 !important; }
/* A purchase order is also an order-request document, whose only table is its
   item list. Its Receiving Log and Payments tables are not item lists: they
   keep their first column (Branch / Receipt #) and their own alignment. */
body.print-a5p.purchase-order-document table:not(.po-items-table) th:first-child,
body.print-a5p.purchase-order-document table:not(.po-items-table) td:first-child { display: table-cell !important; width: 46% !important; text-align: left !important; }
body.print-a5p.purchase-order-document table:not(.po-items-table) th:nth-child(2),
body.print-a5p.purchase-order-document table:not(.po-items-table) td:nth-child(2) { width: 27% !important; text-align: center !important; }
body.print-a5p.purchase-order-document table:not(.po-items-table) th:nth-child(3),
body.print-a5p.purchase-order-document table:not(.po-items-table) td:nth-child(3) { width: 27% !important; text-align: right !important; }
body.print-a5p.purchase-order-document table:not(.po-items-table) th.right:nth-child(2),
body.print-a5p.purchase-order-document table:not(.po-items-table) td.right:nth-child(2) { text-align: right !important; }
body.print-a5p.purchase-order-document table:not(.po-items-table) td[colspan]:first-child { width: auto !important; text-align: center !important; }
body.print-a5p.supplier-document table th:last-child,
body.print-a5p.supplier-document table td:last-child { width: 30% !important; text-align: right !important; padding-right: 0 !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(2) { width: 40% !important; text-align: left !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(4),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(4) { width: 14% !important; text-align: center !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(3),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(3) { width: 16% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(5) { width: 10% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:nth-child(6),
body.print-a5p.purchase-order-document .po-items-table td:nth-child(6) { width: 10% !important; text-align: right !important; }
body.print-a5p.purchase-order-document .po-items-table th:last-child,
body.print-a5p.purchase-order-document .po-items-table td:last-child { width: 10% !important; text-align: right !important; }
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:nth-child(2) { width: 50% !important; }
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table th:last-child,
body.print-a5p.purchase-order-document:not(.po-tax):not(.po-discount) .po-items-table td:last-child { width: 20% !important; }
body.print-a5p.refund-document .items-table .col-item,
body.print-a5p.order-request-document table .col-item { width: 30% !important; text-align: left !important; }
body.print-a5p.refund-document .items-table .col-qty,
body.print-a5p.order-request-document table .col-qty { width: 9% !important; text-align: center !important; }
body.print-a5p.refund-document .items-table .col-rate,
body.print-a5p.order-request-document table .col-rate { width: 15% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table .col-tax,
body.print-a5p.refund-document .items-table .col-discount,
body.print-a5p.order-request-document table .col-tax,
body.print-a5p.order-request-document table .col-discount { width: 14% !important; }
body.print-a5p.refund-document .items-table .col-total,
body.print-a5p.order-request-document table .col-total { width: 18% !important; text-align: right !important; }
body.print-a5p.refund-document .items-table.items-3col .col-item,
body.print-a5p.order-request-document table.items-3col .col-item { width: 50% !important; }
body.print-a5p.refund-document .items-table.items-3col .col-total,
body.print-a5p.order-request-document table.items-3col .col-total { width: 20% !important; }
body.print-a5p.order-request-document table th:nth-child(2),
body.print-a5p.order-request-document table td:nth-child(2) { width: 30% !important; text-align: left !important; }
body.print-a5p.order-request-document table th:nth-child(4),
body.print-a5p.order-request-document table td:nth-child(4) { width: 9% !important; text-align: center !important; }
body.print-a5p.order-request-document table th:nth-child(3),
body.print-a5p.order-request-document table td:nth-child(3) { width: 15% !important; text-align: right !important; }
body.print-a5p.order-request-document table th:nth-child(5),
body.print-a5p.order-request-document table td:nth-child(5) { width: 14% !important; text-align: right !important; }
body.print-a5p.order-request-document table th:nth-child(6),
body.print-a5p.order-request-document table td:nth-child(6) { width: 14% !important; text-align: right !important; }
body.print-a5p.order-request-document table th:last-child,
body.print-a5p.order-request-document table td:last-child { width: 18% !important; text-align: right !important; }
body.print-a5p.order-request-document table.items-3col th:nth-child(2),
body.print-a5p.order-request-document table.items-3col td:nth-child(2) { width: 50% !important; }
body.print-a5p.order-request-document table.items-3col th:last-child,
body.print-a5p.order-request-document table.items-3col td:last-child { width: 20% !important; }
body.print-a5p.order-request-document table.items-tax th:last-child,
body.print-a5p.order-request-document table.items-tax td:last-child,
body.print-a5p.order-request-document table.items-discount th:last-child,
body.print-a5p.order-request-document table.items-discount td:last-child { width: 18% !important; }
body.print-a5p.supplier-document.payment-document table th:nth-child(2),
body.print-a5p.supplier-document.payment-document table td:nth-child(2),
body.print-a5p.supplier-document.receiving-document table th:nth-child(2),
body.print-a5p.supplier-document.receiving-document table td:nth-child(2) { width: 70% !important; text-align: left !important; }
body.print-a5p.supplier-document table th:nth-child(3),
body.print-a5p.supplier-document table td:nth-child(3) { width: 30% !important; }
body.print-a5p.supplier-document.payment-document table th:nth-child(2),
body.print-a5p.supplier-document.payment-document table td:nth-child(2) { text-align: left !important; padding-left: 0 !important; }
body.print-a5p.supplier-document.payment-document table th:nth-child(3),
body.print-a5p.supplier-document.payment-document table td:nth-child(3) { text-align: right !important; padding-right: 0 !important; }
body.print-a5p.supplier-document.receiving-document table th:nth-child(2),
body.print-a5p.supplier-document.receiving-document table td:nth-child(2) { text-align: left !important; padding-left: 0 !important; }
body.print-a5p.supplier-document.receiving-document table th:nth-child(3),
body.print-a5p.supplier-document.receiving-document table td:nth-child(3) { text-align: right !important; padding-right: 0 !important; }
body.print-a5p.supplier-document.payment-document .supplier-email {
    display: inline-block !important;
    max-width: none !important;
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
    font-size: 9px !important;
}
/* Supplier Payment and Receiving A5 landscape tables use the same compact
   two-column alignment as the portrait reference. */
body.print-a5.supplier-document.payment-document table th:first-child,
body.print-a5.supplier-document.payment-document table td:first-child,
body.print-a5.supplier-document.receiving-document table th:first-child,
body.print-a5.supplier-document.receiving-document table td:first-child { display: none !important; }
body.print-a5.supplier-document.payment-document table th:nth-child(2),
body.print-a5.supplier-document.payment-document table td:nth-child(2),
body.print-a5.supplier-document.receiving-document table th:nth-child(2),
body.print-a5.supplier-document.receiving-document table td:nth-child(2) { width: 70% !important; text-align: left !important; padding-left: 0 !important; }
body.print-a5.supplier-document.payment-document table th:nth-child(3),
body.print-a5.supplier-document.payment-document table td:nth-child(3),
body.print-a5.supplier-document.receiving-document table th:nth-child(3),
body.print-a5.supplier-document.receiving-document table td:nth-child(3) { width: 30% !important; text-align: right !important; padding-right: 0 !important; }
body.print-a5.supplier-document.receiving-document table td:nth-child(3) { text-align: right !important; }
body.print-a5.supplier-document table th:first-child,
body.print-a5.supplier-document table td:first-child { display: table-cell !important; width: 70% !important; text-align: left !important; padding-left: 0 !important; }
body.print-a5.supplier-document table th:last-child,
body.print-a5.supplier-document table td:last-child { width: 30% !important; text-align: right !important; padding-right: 0 !important; }
body.print-a5.supplier-document .supplier-email,
body.print-a5p.supplier-document .supplier-email { white-space: nowrap !important; overflow: visible !important; overflow-wrap: normal !important; word-break: normal !important; }
/* Final A5 portrait supplier-detail wrapping contract. Keep receipt numbers,
   dates, account values, and supplier email on one line and aligned to the
   right; allow long values to extend visibly instead of clipping. */
body.print-a5p.supplier-document .info-strip .line,
body.print-a5.supplier-document .info-strip .line {
    display: grid !important;
    grid-template-columns: max-content max-content !important;
    justify-content: space-between !important;
    align-items: baseline !important;
    column-gap: 2mm !important;
    overflow: visible !important;
}
body.print-a5p.supplier-document .info-strip .line > :last-child,
body.print-a5.supplier-document .info-strip .line > :last-child {
    display: block !important;
    justify-self: end !important;
    width: max-content !important;
    max-width: none !important;
    min-width: max-content !important;
    text-align: right !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
/* The browser preview can apply the A5 class after the supplier template's
   own flex rules. Remove shrinking at the value itself so hyphenated receipt
   numbers cannot break between lines. */
body.supplier-document .info-strip .line > :last-child {
    flex: 0 0 auto !important;
    justify-self: end !important;
    display: inline-block !important;
    width: auto !important;
    min-width: max-content !important;
    max-width: none !important;
    white-space: nowrap !important;
    overflow: visible !important;
    text-overflow: clip !important;
    overflow-wrap: normal !important;
    word-break: keep-all !important;
}
/* Long supplier values need a compact intrinsic width on A5 portrait. This
   keeps email and receipt/receiving numbers readable on one line without
   changing the label column or the right-edge alignment. */
body.print-a5p.supplier-document .supplier-email,
body.print-a5.supplier-document .supplier-email,
body.print-a5p.supplier-document .supplier-reference-value,
body.print-a5.supplier-document .supplier-reference-value,
body.print-a5p.supplier-document .info-block:nth-child(2) .line:nth-child(2) > :last-child,
body.print-a5.supplier-document .info-block:nth-child(2) .line:nth-child(2) > :last-child {
    font-size: 8.5px !important;
    letter-spacing: -0.1px !important;
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: keep-all !important;
}
body.print-a5p.supplier-document .info-strip .line > :first-child,
body.print-a5.supplier-document .info-strip .line > :first-child {
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
/* Final Purchase Order portrait grid. Keep Tax/Disc/Total readable instead
   of allowing the short columns to wrap their headings vertically. */
body.print-a5p.purchase-order-document .po-items-table th,
body.print-a5p.purchase-order-document .po-items-table th { white-space: nowrap !important; overflow-wrap: normal !important; word-break: normal !important; }
body.print-a5p.purchase-order-document .po-items-table td { white-space: normal !important; overflow-wrap: break-word !important; word-break: normal !important; }
body.print-a5p.purchase-order-document .po-items-table td:nth-child(n + 3) { white-space: nowrap !important; overflow-wrap: normal !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:nth-child(2) { width: 28% !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:nth-child(4),
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:nth-child(4) { width: 9% !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:nth-child(3),
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:nth-child(3) { width: 14% !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:nth-child(5) { width: 21% !important; }
/* The tax cell carries an amount and a rate; let the rate drop to a second
   line rather than run into the Dis column on a long amount. */
body.print-a5p.purchase-order-document.po-tax .po-items-table td:nth-child(5) { white-space: normal !important; overflow-wrap: anywhere !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:nth-child(6),
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:nth-child(6) { width: 13% !important; }
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table th:last-child,
body.print-a5p.purchase-order-document.po-tax.po-discount .po-items-table td:last-child { width: 15% !important; }
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table td:nth-child(2) { width: 34% !important; }
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table th:last-child,
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table td:last-child { width: 15% !important; }
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table th:nth-child(2),
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table td:nth-child(2) { width: 42% !important; }
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table th:last-child,
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table td:last-child { width: 15% !important; }
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document.po-tax:not(.po-discount) .po-items-table td:nth-child(5) { width: 21% !important; }
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table th:nth-child(5),
body.print-a5p.purchase-order-document.po-discount:not(.po-tax) .po-items-table td:nth-child(5) { width: 13% !important; }
/* Load Form A5 landscape: use clean white table headers. */
body.print-a5.load-form-document table thead,
body.print-a5.load-form-document table thead tr,
body.print-a5.load-form-document table thead th {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    box-shadow: none !important;
    color: #000 !important;
    border-left: 0 !important;
    border-right: 0 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
}
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document table thead,
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document table thead tr,
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document table thead th,
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document .load-summary-table th,
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document .delivery-items-table th {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    box-shadow: none !important;
    color: #000 !important;
    border-left: 0 !important;
    border-right: 0 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
}
body.print-a5.print-a5.print-a5.print-a5.print-a5.load-form-document .summary {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    color: #000 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
}
body.print-a5p.print-a5p.print-a5p.print-a5p.print-a5p.load-form-document .summary {
    background: #fff !important;
    background-color: #fff !important;
    background-image: none !important;
    color: #000 !important;
    border-top: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
}
</style>`;

    // Normalize only the printed A5 table label; keep source data and other
    // paper sizes unchanged.
    const normalizedHtml = String(html)
        .replace(/(<th\b[^>]*>)\s*No\.\s*(<\/th>)/gi, '$1No$2')
        .replace(/(<th\b[^>]*>)\s*Discount\s*(<\/th>)/gi, '$1Disc$2');
    // Add the marker used by the final A5 selectors before injecting them.
    return addBrowserPrintClass(normalizedHtml, printClass, styles);
}

// Serialise print requests instead of coalescing them. Returning the in-flight
// promise to *every* caller meant a second, different document (print a sale,
// then its gate pass) silently resolved as the first and never reached the
// printer. Identical documents are still coalesced, because that is a
// double-clicked button rather than two things to print.
let receiptPrintChain = Promise.resolve();
let receiptPrintInFlight = null;
let receiptPrintInFlightKey = null;

export function printHtmlReceiptViaAgent(html, receiptSize = '80mm') {
    // A literal separator, not a control character: a NUL here made grep
    // treat this whole file as binary and silently truncate its matches.
    const key = `${String(receiptSize || '')}::${String(html || '')}`;
    if (receiptPrintInFlight && receiptPrintInFlightKey === key) return receiptPrintInFlight;

    const run = receiptPrintChain.then(() => printHtmlReceiptViaAgentOnce(html, receiptSize));

    // One printer, one queue: a failed job must not stop the next one, so the
    // chain itself never carries a rejection.
    receiptPrintChain = run.catch(() => {});
    receiptPrintInFlight = run;
    receiptPrintInFlightKey = key;
    run.catch(() => {}).finally(() => {
        if (receiptPrintInFlight === run) {
            receiptPrintInFlight = null;
            receiptPrintInFlightKey = null;
        }
    });

    return run;
}

// The Agent has no content-height measurement and defaults an 80mm print to
// its driver page height, so a long slip would paginate and leave the footer
// alone at the top of the next sheet. When the document declares an explicit
// roll height (the POS receipt and the multi-sale slip both estimate one),
// hand it to the Agent as the paper height so the whole document prints as a
// single feed.
function thermalPageOptions(html) {
    const match = String(html || '').match(/@page[^}]*?size\s*:\s*80mm\s+(\d+(?:\.\d+)?)mm/i);
    if (!match) return { paperSize: '80mm' };
    const heightMm = Number(match[1]);
    return Number.isFinite(heightMm) && heightMm > 0 ? { paperSize: '80mm', heightMm } : { paperSize: '80mm' };
}

// The Agent renders the document itself and cannot measure it, and the payload
// only carries paper_size/orientation, so the roll height that decides whether
// the slip paginates is whatever @page rule the document ships. A document
// declaring `80mm auto` falls back to the driver's page height, paginates, and
// leaves its footer — a break-inside: avoid block — alone at the top of the
// next cut. The browser fallback below measures the real rendered height and
// stamps a numeric @page; the Agent must be handed the same figure. Render the
// final document off-screen, read its scrollHeight, and return the same
// millimetre value the browser single-page path uses.
function measurePrintableContentHeightMm(html) {
    return new Promise((resolve) => {
        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.left = '-10000px';
        frame.style.top = '0';
        frame.style.width = '100vw';
        frame.style.height = '100vh';
        frame.style.opacity = '0';
        frame.style.pointerEvents = 'none';
        frame.style.border = '0';
        frame.style.overflow = 'hidden';
        frame.setAttribute('scrolling', 'no');

        let finished = false;
        let watchdog = null;
        const settle = (value) => {
            if (finished) return;
            finished = true;
            window.clearTimeout(watchdog);
            setTimeout(() => frame.remove(), 10000);
            resolve(value);
        };
        // A document that cannot be laid out must not hang the till; fall back
        // to whatever height the builder declared for itself. Short enough that
        // a jsdom or non-rendering environment settles before any caller's own
        // timeout.
        watchdog = window.setTimeout(() => settle(null), 3000);

        frame.onload = async () => {
            try {
                const doc = frame.contentDocument;
                // Receipt images are data-URIs or same-origin and settle in
                // milliseconds; cap the wait anyway so an environment that never
                // fires load/error on an <img> (such as jsdom) cannot stall the
                // till behind an unmeasurable document.
                const imageLoads = Array.from(doc.images || []).map((image) => {
                    if (image.complete) return Promise.resolve();
                    return new Promise((done) => {
                        image.addEventListener('load', done, { once: true });
                        image.addEventListener('error', done, { once: true });
                    });
                });
                if (imageLoads.length) {
                    await Promise.race([Promise.all(imageLoads), new Promise((done) => window.setTimeout(done, 400))]);
                }
                if (doc.fonts?.ready) await doc.fonts.ready;
                await new Promise((done) => window.setTimeout(done, 150));
                const contentHeightPx = Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0);
                if (!contentHeightPx) return settle(null);
                settle(Math.max(100, Math.ceil(((contentHeightPx * 25.4) / 96) * 1.15 + 12)));
            } catch {
                settle(null);
            }
        };

        document.body.appendChild(frame);
        frame.contentDocument.open();
        frame.contentDocument.write(String(html));
        frame.contentDocument.close();
    });
}

// Overwrite the document's roll height with the measured one. `!important`
// plus document order beats the `80mm auto` the shared design injects, so the
// Agent is told the real paper length and feeds the whole slip as one piece.
function stampThermalRollHeight(html, heightMm) {
    const style = `<style data-inventree-agent-roll-height="true">@page { size: 80mm ${heightMm}mm !important; margin: 0 !important; }</style>`;
    const source = String(html);
    if (/<\/body>/i.test(source)) return source.replace(/<\/body>/i, `${style}</body>`);
    if (/<\/head>/i.test(source)) return source.replace(/<\/head>/i, `${style}</head>`);
    return source + style;
}

async function printHtmlReceiptViaAgentOnce(html, receiptSize = '80mm') {
    if (!String(html || '').trim()) {
        throw new Error('Receipt has no printable content.');
    }

    const requestedReceiptSize = String(receiptSize || '')
        .trim()
        .toUpperCase();
    // A few legacy print routes pass the generic A5 value even though their
    // generated document explicitly declares A5 portrait. Prefer the
    // document's physical @page declaration in that mismatch case, otherwise
    // the shared Agent geometry changes a configured A5P receipt to landscape.
    const documentDeclaresA5Portrait = /@page[^}]*size\s*:\s*(?:A5\s+portrait|148mm\s+210mm)/i.test(String(html));
    const normalizedReceiptSize = requestedReceiptSize === 'A5' && documentDeclaresA5Portrait ? 'A5P' : requestedReceiptSize;

    // The operator's header and text-size choices go on last, over every
    // layer below, so the Agent and the browser dialog print the same document.
    const preferences = storedConfigurations();

    const browserHtml = applyReceiptPreferences(
        normalizedReceiptSize === '80MM'
            ? applyAgent80mmStyles(String(html))
            : normalizedReceiptSize === 'A4'
              ? applyA4Typography(applyBrowser80mmLayout(String(html), receiptSize), receiptSize)
              : ['A5', 'A5P'].includes(normalizedReceiptSize)
                ? applyBrowser80mmLayout(applyA5PrintGeometry(String(html), receiptSize), receiptSize)
                : String(html),
        normalizedReceiptSize,
        preferences,
    );

    // Agent output must not reuse browser print-dialog compensation CSS.
    let printableHtml = applyReceiptPreferences(
        normalizedReceiptSize === '80MM'
            ? applyAgent80mmStyles(String(html))
            : normalizedReceiptSize === 'A4'
              ? applyA4Typography(String(html), receiptSize)
              : ['A5', 'A5P'].includes(normalizedReceiptSize)
                ? applyA5PrintGeometry(applyA5ReceiptTheme(String(html), receiptSize), receiptSize)
                : String(html),
        normalizedReceiptSize,
        preferences,
    );

    // The Agent cannot measure content, so every 80mm document — not just the
    // POS slip — is handed a numeric roll height measured the same way the
    // browser single-page path measures it. Without this the slip paginates and
    // its footer lands at the top of the next cut.
    if (normalizedReceiptSize === '80MM') {
        try {
            const measuredHeightMm = await measurePrintableContentHeightMm(printableHtml);
            if (Number.isFinite(measuredHeightMm) && measuredHeightMm > 0) {
                printableHtml = stampThermalRollHeight(printableHtml, measuredHeightMm);
            }
        } catch {
            /* keep the height the builder declared for itself */
        }
    }

    try {
        const normalizedSize = String(receiptSize || '')
            .trim()
            .toUpperCase();
        const printerType = ['A4', 'A5', 'A5P'].includes(normalizedSize) ? (normalizedSize === 'A5P' ? 'A5' : normalizedSize) : 'thermal';
        const printer = getSelectedPrinter(printerType);
        if (!printer) {
            throw new Error(`No printer is selected for ${printerType === 'thermal' ? 'POS / Thermal' : printerType}.`);
        }

        // The selected printer is already known locally. Avoid the extra
        // sequential /printers request on every receipt; the /print request
        // validates the printer and the existing browser fallback handles
        // Agent errors.
        const pageOptions =
            normalizedReceiptSize === 'A5'
                ? {
                      paperSize: 'A5',
                      orientation: 'landscape',
                      widthMm: 210,
                      heightMm: 148,
                  }
                : normalizedReceiptSize === 'A5P'
                  ? {
                        paperSize: 'A5',
                        orientation: 'portrait',
                        widthMm: 148,
                        heightMm: 210,
                    }
                  : normalizedReceiptSize === 'A4'
                    ? {
                          paperSize: 'A4',
                          orientation: 'portrait',
                          widthMm: 210,
                          heightMm: 297,
                      }
                    : // Say it is a till roll rather than leaving the agent to
                      // infer it from the printer's name. That guess held on
                      // Windows, where these install as "POS80", and misses on
                      // CUPS, where the same printer is `EPSON_TM_T82`.
                      thermalPageOptions(printableHtml);
        return await agentRequest('/print', {
            method: 'POST',
            body: JSON.stringify(
                buildPrintPayload(
                    printer,
                    encodePrintContent(printableHtml),
                    1,
                    'html',
                    true,
                    getPrinterTransport(printer),
                    pageOptions,
                    documentNameFromHtml(printableHtml),
                ),
            ),
        });
    } catch (error) {
        console.error('Print Agent failed; opening browser print dialog.', error);
        try {
            await printHtmlInBrowser(browserHtml);
            return { success: true, fallback: 'browser', agentError: error?.message };
        } catch (dialogError) {
            throw new Error(
                `${error?.message || 'Print Agent failed.'} Browser print dialog also failed: ${dialogError?.message || 'Unknown error'}`,
            );
        }
    }
}

// Same contract as the Agent path above: queue distinct documents, coalesce a
// repeat of the one already printing.
let browserPrintChain = Promise.resolve();
let browserPrintInFlight = null;
let browserPrintInFlightKey = null;

function printHtmlInBrowser(html) {
    const key = String(html || '');
    if (browserPrintInFlight && browserPrintInFlightKey === key) return browserPrintInFlight;

    const run = browserPrintChain.then(() => printHtmlInBrowserOnce(html));

    browserPrintChain = run.catch(() => {});
    browserPrintInFlight = run;
    browserPrintInFlightKey = key;
    run.catch(() => {}).finally(() => {
        if (browserPrintInFlight === run) {
            browserPrintInFlight = null;
            browserPrintInFlightKey = null;
        }
    });

    return run;
}

function printHtmlInBrowserOnce(html) {
    return new Promise((resolve, reject) => {
        const frame = document.createElement('iframe');
        frame.style.position = 'fixed';
        frame.style.left = '-10000px';
        frame.style.top = '0';

        frame.style.width = '100vw';
        frame.style.height = '100vh';
        frame.style.opacity = '0';
        frame.style.pointerEvents = 'none';
        frame.style.border = '0';
        frame.style.overflow = 'hidden';
        frame.setAttribute('scrolling', 'no');

        const cleanup = () => setTimeout(() => frame.remove(), 10000);

        // The watchdog is armed here rather than inside onload. Armed there, a
        // frame whose load event never fired left this promise pending forever,
        // and because the in-flight guard only clears on settle, every later
        // browser fallback returned that same dead promise.
        let finished = false;
        let watchdog = null;
        const settle = (fn, value) => {
            if (finished) return;
            finished = true;
            window.clearTimeout(watchdog);
            cleanup();
            fn(value);
        };
        const finishPrint = () => settle(resolve, undefined);
        watchdog = window.setTimeout(() => settle(reject, new Error('The browser print dialog did not open.')), 20000);

        let printStarted = false;
        frame.onload = async () => {
            if (printStarted) return;
            printStarted = true;
            // Load fired, so the short watchdog has done its job. Hand over to
            // the long one before any awaiting below, or slow images could trip
            // the load watchdog while the document is still being prepared.
            window.clearTimeout(watchdog);
            watchdog = window.setTimeout(finishPrint, 120000);
            try {
                const doc = frame.contentDocument;
                const imageLoads = Array.from(doc.images || []).map((image) => {
                    if (image.complete) return Promise.resolve();
                    return new Promise((resolve) => {
                        image.addEventListener('load', resolve, { once: true });
                        image.addEventListener('error', resolve, { once: true });
                    });
                });
                await Promise.all(imageLoads);
                if (doc.fonts?.ready) await doc.fonts.ready;
                await new Promise((resolve) => window.setTimeout(resolve, 150));
                if (doc.body?.classList.contains('print-80mm')) {
                    const contentHeightPx = Math.max(doc.documentElement?.scrollHeight || 0, doc.body?.scrollHeight || 0);

                    const contentHeightMm = Math.max(100, Math.ceil(((contentHeightPx * 25.4) / 96) * 1.15 + 12));
                    const singlePageStyle = doc.createElement('style');
                    singlePageStyle.setAttribute('data-inventree-browser-single-page', '');
                    singlePageStyle.textContent = `
@media print { @page { size: 80mm ${contentHeightMm}mm !important; margin: 0 !important; } }
html,
body {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
}
html::-webkit-scrollbar,
html::-webkit-scrollbar-button,
html::-webkit-scrollbar-track,
html::-webkit-scrollbar-thumb,
body::-webkit-scrollbar,
body::-webkit-scrollbar-button,
body::-webkit-scrollbar-track,
body::-webkit-scrollbar-thumb {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    background: transparent !important;
}
body.print-80mm.sale-pos-receipt,
body.print-80mm.sale-pos-receipt .line,
body.print-80mm.sale-pos-receipt .info-row,
body.print-80mm.sale-pos-receipt .pm-row,
body.print-80mm.sale-pos-receipt .sum-row,
body.print-80mm.sale-pos-receipt table tbody td,
body.print-80mm.sale-pos-receipt .footer,
body.print-80mm.sale-pos-receipt .footer-bar,
body.print-80mm.sale-pos-receipt .footer-message {
    color: #000 !important;
    font-weight: 700 !important;
    -webkit-font-smoothing: none !important;
    text-rendering: geometricPrecision !important;
}
body.print-80mm.sale-pos-receipt .company,
body.print-80mm.sale-pos-receipt .company-name,
body.print-80mm.sale-pos-receipt .sale-invoice-title,
body.print-80mm.sale-pos-receipt .doc-title,
body.print-80mm.sale-pos-receipt .section-title,
body.print-80mm.sale-pos-receipt .info-label,
body.print-80mm.sale-pos-receipt .pm-title,
body.print-80mm.sale-pos-receipt table thead th {
    color: #000 !important;
    font-weight: 800 !important;
    -webkit-font-smoothing: none !important;
}
body.print-80mm.sale-pos-receipt .sale-items-table th,
body.print-80mm.sale-pos-receipt .sale-items-table td,
body.print-80mm.sale-pos-receipt .item-list th,
body.print-80mm.sale-pos-receipt .item-list td,
body.print-80mm.sale-pos-receipt .sale-items-table th *,
body.print-80mm.sale-pos-receipt .sale-items-table td *,
body.print-80mm.sale-pos-receipt .item-list th *,
body.print-80mm.sale-pos-receipt .item-list td * {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
}
body.print-80mm.sale-pos-receipt .sale-items-table thead th,
body.print-80mm.sale-pos-receipt .item-list thead th,
body.print-80mm.sale-pos-receipt .sale-items-table th:nth-child(1),
body.print-80mm.sale-pos-receipt .sale-items-table th:nth-child(2),
body.print-80mm.sale-pos-receipt .sale-items-table th:nth-child(3),
body.print-80mm.sale-pos-receipt .sale-items-table th:nth-child(4),
body.print-80mm.sale-pos-receipt .sale-items-table th:nth-child(5),
body.print-80mm.sale-pos-receipt .item-list th:nth-child(1),
body.print-80mm.sale-pos-receipt .item-list th:nth-child(2),
body.print-80mm.sale-pos-receipt .item-list th:nth-child(3),
body.print-80mm.sale-pos-receipt .item-list th:nth-child(4),
body.print-80mm.sale-pos-receipt .item-list th:nth-child(5) {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
    font-weight: 800 !important;
    text-align: right !important;
    letter-spacing: 0.3px !important;
    padding: 1.5px 1px !important;
}
body.print-80mm.sale-pos-receipt .sale-items-table tbody tr:not(.product-name-row) td,
body.print-80mm.sale-pos-receipt .item-list tbody tr:not(.product-name-row) td {
    font-size: 9.5px !important;
    line-height: 1.15 !important;
    font-weight: 700 !important;
    text-align: right !important;
    border-bottom: 0 !important;
}
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(1),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(1),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) th:nth-child(1),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(1) { width: 18% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(2),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(2),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) th:nth-child(2),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(2) { width: 12% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(3),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(3),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) th:nth-child(3),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(3) { width: 25% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(4),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(4),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) th:nth-child(4),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(4) { width: 20% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(5),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(5),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) th:nth-child(5),
body.print-80mm.sale-pos-receipt .item-list:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(5) { width: 25% !important; }
/* 3-column sale table (tax & discount hidden): Rate | Qty | Total */
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(1),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(1),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) th:nth-child(1),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(1) { width: 30% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(2),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(2),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) th:nth-child(2),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(2) { width: 16% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(3),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(3),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) th:nth-child(3),
body.print-80mm.sale-pos-receipt .item-list.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(3) { width: 54% !important; }
body.print-80mm.sale-pos-receipt .sale-items-table + .hr,
body.print-80mm.sale-pos-receipt .item-list + .hr {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm.sale-pos-receipt .sale-payment-qr img,
body.print-80mm.sale-pos-receipt .qr-section img,
body.print-80mm.sale-pos-receipt img.qr-code,
body.print-80mm.sale-pos-receipt img[class*="qr"] {
    image-rendering: pixelated !important;
    background: transparent !important;
    mix-blend-mode: multiply !important;
    filter: grayscale(1) contrast(5) brightness(0.75) !important;
}
${sale80mmFinalStyles}`;
                    singlePageStyle.textContent += `
body.print-80mm.sale-pos-receipt .hr,
body.print-80mm.sale-pos-receipt .summary-separator,
body.print-80mm.sale-pos-receipt .separator {
    display: none !important;
    border: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
}
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(1),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(1) { text-align: right !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(2),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(2) { text-align: center !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(3),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(3) { text-align: center !important; }
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(4),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(4),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) th:nth-child(5),
body.print-80mm.sale-pos-receipt .sale-items-table:not(.items-3col) tbody tr:not(.product-name-row) td:nth-child(5) { text-align: right !important; }
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(1),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(1) { text-align: right !important; }
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(2),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(2) { text-align: center !important; }
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) th:nth-child(3),
body.print-80mm.sale-pos-receipt .sale-items-table.items-3col:not(.new-items-table) tbody tr:not(.product-name-row) td:nth-child(3) { text-align: right !important; }
body.print-80mm.sale-pos-receipt .sale-items-table tbody tr.product-name-row td,
body.print-80mm.sale-pos-receipt .sale-items-table td.item-name {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
    text-overflow: clip !important;
    white-space: normal !important;
    overflow-wrap: anywhere !important;
    word-break: normal !important;
    text-align: left !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}
/* These rules must remain in the final Agent layer: the shared numeric-column
   rules above otherwise make a colspan message look stuck on the side. */
body.print-80mm.sale-pos-receipt.refund-document table.sale-items-table tr.no-items-row,
body.print-80mm.refund-document table.sale-items-table tr.no-items-row,
body.print-80mm.sale-pos-receipt.refund-document table.sale-items-table tr.no-items-row td.no-items-found,
body.print-80mm.refund-document table.sale-items-table tr.no-items-row td.no-items-found {
    width: 100% !important;
    min-width: 100% !important;
    max-width: none !important;
    text-align: center !important;
    white-space: nowrap !important;
    overflow: visible !important;
    padding: 3px 0 !important;
}
body.print-80mm.sale-pos-receipt.refund-document table.sale-items-table tr.no-items-row {
    display: table-row !important;
}
body.print-80mm.refund-document table.sale-items-table tr.no-items-row {
    display: table-row !important;
}
body.print-80mm.refund-document table.sale-items-table tr.no-items-row td.no-items-found {
    display: table-cell !important;
}
body.print-80mm.sale-pos-receipt.order-request-document .order-number-row {
    display: grid !important;
    grid-template-columns: max-content minmax(0, 1fr) !important;
    width: 100% !important;
    align-items: baseline !important;
    gap: 3mm !important;
    white-space: nowrap !important;
}
body.print-80mm.order-request-document .order-number-row {
    display: grid !important;
    grid-template-columns: max-content minmax(0, 1fr) !important;
    width: 100% !important;
    align-items: baseline !important;
    gap: 3mm !important;
    white-space: nowrap !important;
}
body.print-80mm.sale-pos-receipt.order-request-document .order-number-row > :last-child {
    display: block !important;
    min-width: 0 !important;
    max-width: none !important;
    margin-left: 0 !important;
    text-align: right !important;
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
body.print-80mm.order-request-document .order-number-row > :last-child {
    display: block !important;
    min-width: 0 !important;
    max-width: none !important;
    margin-left: 0 !important;
    text-align: right !important;
    white-space: nowrap !important;
    overflow: visible !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
body.print-80mm.sale-pos-receipt.order-request-document .items-end-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 2mm !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
body.print-80mm.order-request-document .items-end-separator {
    display: block !important;
    visibility: visible !important;
    width: 100% !important;
    height: 0 !important;
    margin: 1mm 0 2mm !important;
    padding: 0 !important;
    border: 0 !important;
    border-top: 1px solid #000 !important;
}
`;
                    doc.head.appendChild(singlePageStyle);
                } else if (doc.body?.classList.contains('print-a5') && !doc.body?.classList.contains('print-a5p')) {
                    // Force A5 landscape orientation in the browser print dialog.
                    const landscapeStyle = doc.createElement('style');
                    landscapeStyle.setAttribute('data-inventree-browser-a5-landscape', '');
                    const topMargin = '5mm';
                    landscapeStyle.textContent = `@media print { @page { size: 210mm 148mm !important; margin: ${topMargin} 0 0 !important; } }`;
                    doc.head.appendChild(landscapeStyle);
                } else if (
                    doc.body?.classList.contains('print-a5p') ||
                    doc.body?.classList.contains('sale-a5p') ||
                    doc.querySelector('style[data-inventree-browser-a5-layout]')?.textContent?.includes('portrait')
                ) {
                    // Force A5 portrait orientation in the browser print dialog.
                    const portraitStyle = doc.createElement('style');
                    portraitStyle.setAttribute('data-inventree-browser-a5-portrait', '');
                    portraitStyle.textContent = `@media print { @page { size: 148mm 210mm !important; margin: 5mm 0 0 !important; } } html, body { width: 148mm !important; max-width: 148mm !important; }`;
                    doc.head.appendChild(portraitStyle);
                }

                frame.contentWindow.addEventListener('afterprint', finishPrint, { once: true });

                frame.contentWindow.focus();
                frame.contentWindow.print();
            } catch (error) {
                settle(reject, error);
            }
        };

        document.body.appendChild(frame);
        frame.contentDocument.open();
        frame.contentDocument.write(String(html));
        frame.contentDocument.close();
    });
}

/**
 * Print a rendered element rather than a generated document — used by the
 * calculator estimate, which is the page itself rather than a built receipt.
 *
 * The page's own stylesheets are serialised into the document instead of being
 * linked: the Agent renders the HTML in its own browser, which is not logged in
 * and should not need to fetch anything back off the app to lay the page out.
 *
 * @param {HTMLElement|null} element
 * @param {string} receiptSize
 */
export async function printElementViaAgent(element, receiptSize = 'A4') {
    if (!element) return { success: false, skipped: 'no element' };

    // Screen-only controls are removed rather than hidden: `@media print` rules
    // are honoured by the browser dialog but not by the Agent's renderer, and a
    // Print button coming out of the printer is the tell.
    const printable = element.cloneNode(true);
    printable.querySelectorAll('.no-print, [data-print-exclude]').forEach((node) => node.remove());

    const styles = Array.from(document.styleSheets)
        .map((sheet) => {
            try {
                return Array.from(sheet.cssRules)
                    .map((rule) => rule.cssText)
                    .join('\n');
            } catch {
                // Cross-origin sheet: its rules cannot be read, so link it and
                // let the renderer fetch it if it can.
                return sheet.href ? `@import url("${sheet.href}");` : '';
            }
        })
        .filter(Boolean)
        .join('\n');

    const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><style>${styles}</style></head>
<body>${printable.outerHTML}</body></html>`;

    return printHtmlReceiptViaAgent(html, receiptSize);
}

export async function getPrinters(forceRefresh = false) {
    if (forceRefresh) printersPromise = null;
    if (!printersPromise) {
        printersPromise = agentRequest('/printers')
            .then((response) => printerNames(response))
            .catch((error) => {
                printersPromise = null;
                throw error;
            });
    }
    return printersPromise;
}
